import type { Card, ChymistCompoundId, CombatUiEvent, CombatUiStatusPulseKey, DelayedBundle, Effect, GameState, RelicCombatMods, RelicFeedbackEntry, RelicFeedbackTriggerKey } from './types';
import { parseVariantFromDescription } from './types';
import { NESTED_HORROR_PARALYZING_VENOM, enemyFighterFromDef, findEnemyDef, getEnemyDef, pickRandomEnemyId } from './enemies';
import { custodianTitleKey, getCustodianTrackedCardType, isCustodianEnemy } from './custodian';
import { formatRelicFeedbackText } from './relicFeedbackText';
import { chooseEnemyCardViaAi, hasEnemyAiRouting } from '../ai/index';
// ---- Enemy card helpers (must be top-level, not inside reducer) ----
function norm(s: unknown): string {
  return String(s ?? '').trim().toLowerCase();
}


function lampKeeperTitleKey(value: unknown): string {
  return norm(value).replace(/[’'`]/g, '');
}

function lampKeeperRoomLightLabel(value: unknown): string {
  const key = String(value ?? '').trim().toLowerCase();
  if (key === 'dark') return 'Dark';
  if (key === 'steady') return 'Steady';
  if (key === 'bright') return 'Bright';
  return 'Unknown';
}

function isLampKeeperEnemy(enemyId: unknown): boolean {
  return norm(enemyId) === 'lamp_keeper';
}

function getLampKeeperRoomLight(state: GameState): 'dark' | 'steady' | 'bright' {
  const raw = String((state as any).lampKeeperRoomLight ?? '').trim().toLowerCase();
  if (raw === 'dark' || raw === 'steady' || raw === 'bright') return raw;
  return 'steady';
}

function getLampKeeperDisposition(state: GameState): 'night_watch' | 'tended_flame' | 'open_flame' | null {
  const raw = String((state as any).lampKeeperDisposition ?? '').trim().toLowerCase();
  if (raw === 'night_watch' || raw === 'tended_flame' || raw === 'open_flame') return raw;
  return null;
}

function deriveLampKeeperRoomLightFromTorchProgress(progressRaw: unknown): 'dark' | 'steady' | 'bright' {
  const progress = Math.max(0, Math.min(1, Number(progressRaw) || 0));
  if (progress <= 0.33) return 'dark';
  if (progress <= 0.66) return 'steady';
  return 'bright';
}

function deriveLampKeeperDispositionFromRoomLight(roomLight: 'dark' | 'steady' | 'bright'): 'night_watch' | 'tended_flame' | 'open_flame' {
  if (roomLight === 'dark') return 'night_watch';
  if (roomLight === 'steady') return 'tended_flame';
  return 'open_flame';
}

function applyLampKeeperRoomLightChange(
  state: GameState,
  nextRoomLight: 'dark' | 'steady' | 'bright',
  sourceTitle: string,
): GameState {
  const prevRoomLight = getLampKeeperRoomLight(state);
  if (prevRoomLight === nextRoomLight) return state;

  const raised =
    (prevRoomLight === 'dark' && (nextRoomLight === 'steady' || nextRoomLight === 'bright')) ||
    (prevRoomLight === 'steady' && nextRoomLight === 'bright');

  const lowered =
    (prevRoomLight === 'bright' && (nextRoomLight === 'steady' || nextRoomLight === 'dark')) ||
    (prevRoomLight === 'steady' && nextRoomLight === 'dark');

  let next = {
    ...state,
    lampKeeperRoomLight: nextRoomLight,
    log: [`${sourceTitle}: Room Light becomes ${lampKeeperRoomLightLabel(nextRoomLight)}.`, ...state.log],
  } as GameState;

  const disposition = getLampKeeperDisposition(next);

  if (raised && !((next as any).lampKeeperRaisedLightThisRound)) {
    next = { ...next, lampKeeperRaisedLightThisRound: true } as GameState;
    if (disposition === 'open_flame') {
      next = applyIncomingEnemyDamage(next, 6, { sourceTitle: 'Open Flame' });
    }
  }

  if (lowered && !((next as any).lampKeeperLoweredLightThisRound)) {
    next = { ...next, lampKeeperLoweredLightThisRound: true } as GameState;
    if (disposition === 'night_watch') {
      next = applyEffect(next, { type: 'block', amount: 12 } as any, 'enemy');
    }
  }

  if (disposition === 'tended_flame') {
    next = applyEnemyPlayerSpLoss(next, 5, 'Tended Flame');
  }

  return next;
}

function shiftLampKeeperRoomLight(state: GameState, step: number, sourceTitle: string): GameState {
  const current = getLampKeeperRoomLight(state);
  const order: Array<'dark' | 'steady' | 'bright'> = ['dark', 'steady', 'bright'];
  const currentIndex = Math.max(0, order.indexOf(current));
  const nextIndex = Math.max(0, Math.min(order.length - 1, currentIndex + Math.sign(step || 0)));
  return applyLampKeeperRoomLightChange(state, order[nextIndex]!, sourceTitle);
}

function setLampKeeperRoomLight(state: GameState, roomLight: 'dark' | 'steady' | 'bright', sourceTitle: string): GameState {
  return applyLampKeeperRoomLightChange(state, roomLight, sourceTitle);
}

function applyLampKeeperEnemyCard(state: GameState, enemyCard: Card): GameState {
  const title = lampKeeperTitleKey(enemyCard.title);

  if (title === 'lower the hood') {
    let next = shiftLampKeeperRoomLight(state, -1, enemyCard.title);
    next = applyEffect(next, { type: 'block', amount: 18 } as any, 'enemy');
    if (getLampKeeperRoomLight(next) === 'dark') {
      next = applyEffect(next, { type: 'block', amount: 10 } as any, 'enemy');
    }
    return next;
  }

  if (title === 'open the shutters') {
    let next = shiftLampKeeperRoomLight(state, 1, enemyCard.title);
    next = applyEnemyPlayerSpLoss(next, 12, enemyCard.title);
    if (getLampKeeperRoomLight(next) === 'bright') {
      next = applyIncomingEnemyDamage(next, 8, { sourceTitle: enemyCard.title });
    }
    return next;
  }

  if (title === 'lantern staff') {
    let next = applyIncomingEnemyDamage(state, 16, { sourceTitle: enemyCard.title });
    const roomLight = getLampKeeperRoomLight(next);
    if (roomLight === 'dark') {
      next = applyEffect(next, { type: 'block', amount: 10 } as any, 'enemy');
    } else if (roomLight === 'bright') {
      next = applyEnemyPlayerSpLoss(next, 10, enemyCard.title);
    }
    return next;
  }

  if (title === 'trim the wick') {
    if (getLampKeeperRoomLight(state) === 'steady') {
      let next = applyEffect(state, { type: 'healHP', amount: 14 } as any, 'enemy');
      next = applyEffect(next, { type: 'block', amount: 12 } as any, 'enemy');
      return next;
    }
    return setLampKeeperRoomLight(state, 'steady', enemyCard.title);
  }

  if (title === 'soot burst') {
    let next = applyEnemyPlayerSpLoss(state, 8, enemyCard.title);
    next = shiftLampKeeperRoomLight(next, -1, enemyCard.title);
    if (getLampKeeperRoomLight(next) === 'dark') {
      next = applyIncomingEnemyDamage(next, 14, { sourceTitle: enemyCard.title });
    }
    return next;
  }

  if (title === 'glass flare') {
    let next = shiftLampKeeperRoomLight(state, 1, enemyCard.title);
    if (getLampKeeperRoomLight(next) === 'bright') {
      next = applyIncomingEnemyDamage(next, 20, { sourceTitle: enemyCard.title });
      next = applyEnemyPlayerSpLoss(next, 12, enemyCard.title);
    }
    return next;
  }

  if (title === 'keepers circuit') {
    const roomLight = getLampKeeperRoomLight(state);
    if (roomLight === 'dark') {
      return applyEffect(state, { type: 'block', amount: 20 } as any, 'enemy');
    }
    if (roomLight === 'steady') {
      return applyEffect(state, { type: 'healHP', amount: 10 } as any, 'enemy');
    }
    return applyEnemyPlayerSpLoss(state, 15, enemyCard.title);
  }

  return applyCard(state, enemyCard, 'enemy');
}


const RESHUFFLE_HEAL_PER_CARD = 0;

export function effectUsesDrawInvoke(effect: any): boolean {
  const type = typeof effect?.type === 'string' ? effect.type : '';
  return (
    type === 'draw' ||
    type === 'drawRandom' ||
    type === 'drawRandomPhysical' ||
    type === 'drawRandomMagic' ||
    type === 'drawFromDeck' ||
    type === 'tutor' ||
    type === 'peekReorderPhysical' ||
    type === 'peekReorderMagic' ||
    type === 'peekReorderDeck' ||
    type === 'peekPickOneDiscardRestPhysical' ||
    type === 'peekPickOneDiscardRestMagic' ||
    type === 'peekPickOneDiscardRestAny'
  );
}

function isDefenseSubtitle(value: unknown): boolean {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return normalized === 'defense';
}

function normalizeExplicitInvokeVariant(value: unknown): 'generic' | 'draw' | 'defense' | null {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === 'generic' || normalized === 'draw' || normalized === 'defense') return normalized;
  return null;
}

export function resolveInvokeAnimationVariantForPlay(args: {
  state: GameState;
  card: Card;
  addendumPaySp?: number;
  addendumPayHp?: number;
  xPaySp?: number;
  xPayHp?: number;
}): 'generic' | 'draw' | 'defense' {
  const card = args.card;
  const state = args.state;

  const directInvoke = (card as any)?.invoke;
  if (directInvoke && typeof directInvoke === 'object') {
    const explicitVariant = normalizeExplicitInvokeVariant(
      (directInvoke as any).variant ?? (directInvoke as any).key ?? (directInvoke as any).id
    );
    if (explicitVariant) return explicitVariant;
  }

  const spCostIsX = !!(card as any).spCostIsX;
  const hpCostIsX = !!(card as any).hpCostIsX;
  const cost0Count = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0);
  const cost0Active = cost0Count > 0;
  const chosenXSp = spCostIsX && !cost0Active
    ? Math.max(0, Math.floor(Number(args.xPaySp ?? 0) || 0))
    : 0;
  const chosenXHp = hpCostIsX && !cost0Active
    ? Math.max(0, Math.floor(Number(args.xPayHp ?? 0) || 0))
    : 0;

  let playEffects: Effect[] = Array.isArray(card.effects) ? [...card.effects] : [];
  let playDelayedGroups: any[] = Array.isArray((card as any).delayed) ? (card as any).delayed : [];
  let playKeywords: string[] = Array.isArray(card.keywords) ? [...card.keywords] : [];

  if (spCostIsX || hpCostIsX) {
    const xValue = spCostIsX ? chosenXSp : chosenXHp;
    const parsedBase = parseVariantFromDescription({
      title: card.title,
      subtitle: card.subtitle,
      kind: card.kind,
      description: (card as any).baseDescription ?? card.description ?? '',
      xValue,
    });
    playEffects = parsedBase.effects;
    playDelayedGroups = parsedBase.delayed ?? [];
    playKeywords = parsedBase.keywords;
  }

  const add = (card as any).addendum as undefined | { costSp?: number | 'X'; costHp?: number | 'X'; altDescription: string };
  if (add) {
    const resource: 'sp' | 'hp' = (add as any).costHp != null ? 'hp' : 'sp';
    const requestedExtra = resource === 'hp'
      ? Math.max(0, Math.floor(Number(args.addendumPayHp ?? 0) || 0))
      : Math.max(0, Math.floor(Number(args.addendumPaySp ?? 0) || 0));
    const costToken = (resource === 'hp' ? (add as any).costHp : (add as any).costSp) as (number | 'X' | undefined);

    if (requestedExtra > 0 && costToken != null) {
      let raw = 0;
      if (costToken === 'X') raw = requestedExtra;
      else {
        const fixed = Math.max(0, Number(costToken) || 0);
        if (fixed > 0 && requestedExtra >= fixed) raw = fixed;
      }

      if (raw > 0) {
        const parsedAlt = parseVariantFromDescription({
          title: card.title,
          subtitle: card.subtitle,
          kind: card.kind,
          description: add.altDescription,
          xValue: costToken === 'X' ? raw : undefined,
        });
        playEffects = [...playEffects, ...parsedAlt.effects];
        playDelayedGroups = [...playDelayedGroups, ...(parsedAlt.delayed ?? [])];
        playKeywords = Array.from(new Set([...(playKeywords ?? []), ...(parsedAlt.keywords ?? [])]));
      }
    }
  }

  const legacyDelayTurns = Math.max(0, Number((card as any).delayTurns || 0) || 0);
  let delayGroups = (Array.isArray(playDelayedGroups) && playDelayedGroups.length > 0)
    ? playDelayedGroups
    : (legacyDelayTurns > 0 ? [{ turns: legacyDelayTurns, effects: playEffects.filter((e: any) => e.type !== 'discard') }] : []);

  const magicDoubleCount = Math.max(0, Number((state as any).nextMagicCardCostDoubleSP ?? 0) || 0);
  const consumeMagicDoubleNow = card.kind === 'magic' && magicDoubleCount > 0;
  const spMult = !cost0Active && consumeMagicDoubleNow ? 2 : 1;
  const isUtilityCard = String((card as any).tag ?? '').trim().toLowerCase() === 'utility' || /\butility\b/i.test(String(card.subtitle ?? ''));
  const utilityCostReductionThisTurn = !cost0Active && isUtilityCard
    ? Math.max(0, Math.floor(Number((state as any).utilityCostReductionThisTurn ?? 0) || 0))
    : 0;
  const printedHpCost = hpCostIsX ? (chosenXHp + (card.cost?.hp ?? 0)) : (card.cost?.hp ?? 0);
  const reducedPrintedHpCost = utilityCostReductionThisTurn > 0
    ? Math.max(0, printedHpCost - utilityCostReductionThisTurn)
    : printedHpCost;
  const baseHpCost = cost0Active ? 0 : reducedPrintedHpCost;
  const printedSpCost = spCostIsX ? (chosenXSp + (card.cost?.sp ?? 0)) : (card.cost?.sp ?? 0);
  const reducedPrintedSpCost = utilityCostReductionThisTurn > 0
    ? Math.max(0, printedSpCost - utilityCostReductionThisTurn)
    : printedSpCost;
  let baseSpCost = cost0Active ? 0 : reducedPrintedSpCost * spMult;
  const baseDelayedGroups = Array.isArray((card as any).delayed) ? (card as any).delayed : [];
  const costDelayTurns = Math.max(0, Number((card as any).costDelayTurns || 0) || 0);

  {
    const mods = (state as any).relicMods as RelicCombatMods | undefined;
    if (mods) {
      const hasChainKeyword = hasKeyword(card, 'Chain');
      const hasExileKeyword = isExileCard(card);
      let chainEligible = false;
      if (mods.exileCardsGetChain && hasExileKeyword) chainEligible = true;
      else chainEligible = hasChainKeyword;

      let effectivePrintedSpCost = Math.max(0, Number(reducedPrintedSpCost ?? 0) || 0);
      if (chainEligible && mods.chainCostReduction && mods.chainCostReduction > 0) {
        const reduce = Number(mods.chainCostReduction) || 0;
        effectivePrintedSpCost = Math.max(0, effectivePrintedSpCost - reduce);
      }

      let newBaseSpCost = cost0Active ? 0 : (effectivePrintedSpCost * spMult);
      const hasDelay = legacyDelayTurns > 0 || (Array.isArray(baseDelayedGroups) && baseDelayedGroups.length > 0) || costDelayTurns > 0;
      if (mods.delayCostReduction && mods.delayCostReduction > 0 && hasDelay) {
        const reduce = Number(mods.delayCostReduction) || 0;
        newBaseSpCost = Math.max(0, newBaseSpCost - reduce);
      }
      baseSpCost = newBaseSpCost;
    }
  }

  const requestedExtraSp = Math.max(0, Math.floor(Number(args.addendumPaySp ?? 0) || 0));
  const requestedExtraHp = Math.max(0, Math.floor(Number(args.addendumPayHp ?? 0) || 0));
  let extraSp = 0;
  let extraHp = 0;

  if ((card as any).addendum) {
    const addendum = (card as any).addendum as { costSp?: number | 'X'; costHp?: number | 'X'; altDescription: string };
    const resource: 'sp' | 'hp' = (addendum as any).costHp != null ? 'hp' : 'sp';
    const requestedExtra = resource === 'hp' ? requestedExtraHp : requestedExtraSp;
    const maxExtra = resource === 'hp'
      ? Math.max(0, state.player.hp - baseHpCost)
      : Math.max(0, state.player.sp - baseSpCost);
    const costToken = (resource === 'hp' ? (addendum as any).costHp : (addendum as any).costSp) as (number | 'X' | undefined);

    if (requestedExtra > 0 && costToken != null) {
      const mods = (state as any).relicMods as RelicCombatMods | undefined;
      const reduce = Math.max(0, Number(mods?.addendumCostReduction ?? 0) || 0);
      let raw = 0;
      if (costToken === 'X') {
        const maxAffordableX = Math.max(0, maxExtra + reduce);
        raw = Math.min(requestedExtra, maxAffordableX);
      } else {
        const fixed = Math.max(0, Number(costToken) || 0);
        if (fixed > 0 && requestedExtra >= fixed) raw = fixed;
      }
      if (raw > 0) {
        const effective = Math.max(0, raw - reduce);
        if (effective <= maxExtra) {
          if (resource === 'hp') extraHp = effective;
          else extraSp = effective;
        }
      }
    }
  }

  const delayCostActive = costDelayTurns > 0 && !cost0Active;
  const immediateHpCost = delayCostActive ? 0 : baseHpCost;
  const immediateSpCost = delayCostActive ? 0 : baseSpCost;

  let stateAfterCosts: GameState = {
    ...state,
    player: {
      ...state.player,
      hp: clampPlayerHp(state, (state.player.hp || 0) - immediateHpCost - extraHp),
      sp: clampPlayerSp(state, (state.player.sp || 0) - immediateSpCost - extraSp),
    },
  };

  const ex = applyPlayExceptions({
    cardTitle: card.title,
    stateAfterCosts,
    playEffects,
    playKeywords,
    delayGroups,
  });

  if (ex) {
    playEffects = ex.playEffects;
    playKeywords = ex.playKeywords;
    delayGroups = ex.delayGroups;
    stateAfterCosts = ex.stateAfterCosts;
  }

  const allEffects = [
    ...playEffects,
    ...delayGroups.flatMap((group: any) => (Array.isArray(group?.effects) ? group.effects : [])),
  ];

  const isDefenseCard =
    String((card as any).tag ?? '').trim().toLowerCase() === 'defense' ||
    isDefenseSubtitle((card as any).subtitle);

  if (isDefenseCard) return 'defense';
  if (allEffects.some((effect: any) => effectUsesDrawInvoke(effect))) return 'draw';
  return 'generic';
}

const COMBAT_RULES = {
  autoReshuffleWhenEmpty: true,
  increaseCostOnReshuffle: false, // <- set false to disable the +1 HP/SP on reshuffle
};


type PlayerPileState = { deck: Card[]; discard: Card[]; exhaust: Card[] };

function deriveKindPiles(cards: Card[]): { physical: Card[]; magic: Card[] } {
  return {
    physical: cards.filter((c) => c.kind === 'physical'),
    magic: cards.filter((c) => c.kind === 'magic'),
  };
}

function getPlayerPileState(state: GameState): PlayerPileState {
  const deck = Array.isArray((state as any).deck)
    ? ([...(state as any).deck] as Card[])
    : ([...(state.physicalDeck || []), ...(state.magicDeck || [])] as Card[]);
  const discard = Array.isArray((state as any).discard)
    ? ([...(state as any).discard] as Card[])
    : ([...(state.physicalDiscard || []), ...(state.magicDiscard || [])] as Card[]);
  const exhaust = Array.isArray((state as any).exhaust)
    ? ([...(state as any).exhaust] as Card[])
    : ([] as Card[]);
  return { deck, discard, exhaust };
}

function withPlayerPileState(state: GameState, piles: PlayerPileState): GameState {
  const deck = [...(piles.deck || [])];
  const discard = [...(piles.discard || [])];
  const exhaust = [...(piles.exhaust || [])];
  const deckKinds = deriveKindPiles(deck);
  const discardKinds = deriveKindPiles(discard);
  return {
    ...state,
    deck,
    discard,
    exhaust,
    physicalDeck: deckKinds.physical,
    magicDeck: deckKinds.magic,
    physicalDiscard: discardKinds.physical,
    magicDiscard: discardKinds.magic,
  } as GameState;
}

function pushCardsToDiscard(state: GameState, cards: Card[]): GameState {
  if (!Array.isArray(cards) || cards.length === 0) return state;
  const piles = getPlayerPileState(state);
  return withPlayerPileState(state, {
    deck: piles.deck,
    discard: [...cards, ...piles.discard],
    exhaust: piles.exhaust,
  });
}

function pushCardsToExhaust(state: GameState, cards: Card[]): GameState {
  if (!Array.isArray(cards) || cards.length === 0) return state;
  const piles = getPlayerPileState(state);
  const next = withPlayerPileState(state, {
    deck: piles.deck,
    discard: piles.discard,
    exhaust: [...cards, ...piles.exhaust],
  });
  return trackCustodianExiledCards(next, cards);
}

function ensureUnifiedDeckReady(state: GameState): GameState {
  if (state.phase === 'gameover') return state;
  if (!COMBAT_RULES.autoReshuffleWhenEmpty) return state;

  const piles = getPlayerPileState(state);
  if (piles.deck.length > 0 || piles.discard.length === 0) return state;

  const didEscalate = COMBAT_RULES.increaseCostOnReshuffle;
  const hadPhysical = piles.discard.some((c) => c.kind === 'physical');
  const hadMagic = piles.discard.some((c) => c.kind === 'magic');
  const nextPhysicalEscalation = state.physicalEscalation + (didEscalate && hadPhysical ? 1 : 0);
  const nextMagicEscalation = state.magicEscalation + (didEscalate && hadMagic ? 1 : 0);

  const deck = bumpDiscardIntoDeck(piles.discard, 'physical');
  let next = withPlayerPileState(
    {
      ...state,
      physicalEscalation: nextPhysicalEscalation,
      magicEscalation: nextMagicEscalation,
    } as GameState,
    { deck, discard: [], exhaust: piles.exhaust }
  );

  const logs: string[] = [];
  if (didEscalate) {
    if (hadPhysical) logs.push(`Deck reshuffled - Physical costs +${nextPhysicalEscalation}.`);
    if (hadMagic) logs.push(`Deck reshuffled - Magic costs +${nextMagicEscalation}.`);
  } else {
    logs.push('Deck reshuffled.');
  }

  if (logs.length > 0) {
    next = { ...next, log: [...logs.reverse(), ...next.log] } as GameState;
  }
  return next;
}

function takeTopCardFromUnifiedDeck(state: GameState): { state: GameState; card: Card | null } {
  const ready = ensureUnifiedDeckReady(state);
  const piles = getPlayerPileState(ready);
  const deck = [...piles.deck];
  const card = deck.shift() || null;
  return {
    state: withPlayerPileState(ready, { deck, discard: piles.discard, exhaust: piles.exhaust }),
    card,
  };
}

function takeKindCardFromUnifiedDeck(state: GameState, kind: 'physical' | 'magic'): { state: GameState; card: Card | null } {
  const ready = ensureUnifiedDeckReady(state);
  const piles = getPlayerPileState(ready);
  const deck = [...piles.deck];
  const idx = deck.findIndex((c) => c.kind === kind);
  if (idx < 0) {
    return { state: ready, card: null };
  }
  const [card] = deck.splice(idx, 1);
  return {
    state: withPlayerPileState(ready, { deck, discard: piles.discard, exhaust: piles.exhaust }),
    card: card || null,
  };
}

function takeRandomCardFromUnifiedDeck(state: GameState, kind: 'physical' | 'magic'): { state: GameState; card: Card | null } {
  const ready = ensureUnifiedDeckReady(state);
  const piles = getPlayerPileState(ready);
  const deck = [...piles.deck];
  const indexes = deck.map((c, i) => ({ c, i })).filter(({ c }) => c.kind === kind).map(({ i }) => i);
  if (indexes.length <= 0) {
    return { state: ready, card: null };
  }
  const idx = indexes[Math.floor(Math.random() * indexes.length)] ?? indexes[0];
  const [card] = deck.splice(idx, 1);
  return {
    state: withPlayerPileState(ready, { deck, discard: piles.discard, exhaust: piles.exhaust }),
    card: card || null,
  };
}

function peekTopCardsFromUnifiedDeck(state: GameState, count: number): { state: GameState; cards: Card[] } {
  const ready = ensureUnifiedDeckReady(state);
  const piles = getPlayerPileState(ready);
  return {
    state: ready,
    cards: piles.deck.slice(0, Math.max(0, Math.floor(count))),
  };
}

function peekCardsByKindFromUnifiedDeck(state: GameState, kind: 'physical' | 'magic', count: number): { state: GameState; cards: Card[] } {
  const ready = ensureUnifiedDeckReady(state);
  const piles = getPlayerPileState(ready);
  const cards = piles.deck.filter((c) => c.kind === kind).slice(0, Math.max(0, Math.floor(count)));
  return { state: ready, cards };
}

function isChargeCard(c: Card): boolean {
  const title = norm((c as any).title);
  const name = norm((c as any).name);
  const id = norm((c as any).id);

  // Charge openers that trigger a 3-turn combo:
  // - Cultist: Sprint → Sprint → Power Strike
  // - Dreadbrand: Sprint → Sprint → Iron Rend
  // - Hexbinder: Meditation → Meditation → Ritual
  // - Yahaka: Crouching Stance → Crouching Stance → Call of the Void
  //
  // NOTE: We ALSO match by known card IDs so enemies can safely override the display title
  // (e.g. boss variants like "Skitter" / "Crushing Bite") without breaking the combo logic.
  const SPRINT_ID = '6998d4cc0bebb02c4299bfd2';
  const MEDITATION_ID = '6998d5870bebb02c4299bfdb';
  const CROUCHING_STANCE_ID = '69bac254c3e5ef3bbff71cea';

  const isSprint =
    id === SPRINT_ID ||
    title.includes('sprint') ||
    name.includes('sprint') ||
    id.includes('sprint');

  const isMeditation =
    id === MEDITATION_ID ||
    title.includes('meditation') ||
    name.includes('meditation') ||
    id.includes('meditation');

  const isCrouchingStance =
    id === CROUCHING_STANCE_ID ||
    title === 'crouching stance' ||
    name === 'crouching stance' ||
    title.includes('crouching stance') ||
    name.includes('crouching stance') ||
    id.includes('crouching_stance') ||
    id.includes('crouching-stance') ||
    id.includes('crouchingstance');

  return isSprint || isMeditation || isCrouchingStance;
}



// Build the enemy deck to use for THIS fight.
// - If the enemy defines `enemyCards`, we take exactly those card IDs from the loaded enemy pool.
// - Otherwise, we use the full loaded enemy pool.
function buildEnemyDeckForFight(enemyDef: any, enemyLibrary: Card[]): { deck: Card[]; missing: string[] } {
  const specs: Array<{ id: string; title?: string }> | undefined = enemyDef?.enemyCards;
  const fallbackAllowed = enemyDef?.allowEnemyLibraryFallbackWhenEmpty !== false;
  if (!Array.isArray(specs)) return { deck: enemyLibrary, missing: [] };
  if (!specs.length) return { deck: fallbackAllowed ? enemyLibrary : [], missing: [] };

  const byId = new Map<string, Card>();
  const byTitle = new Map<string, Card>();
  for (const c of enemyLibrary) {
    const idKey = norm((c as any).id);
    const titleKey = norm((c as any).title ?? (c as any).name);
    if (idKey) byId.set(idKey, c);
    if (titleKey && !byTitle.has(titleKey)) byTitle.set(titleKey, c);
  }

  const deck: Card[] = [];
  const missing: string[] = [];

  for (const spec of specs) {
    const specIdKey = norm(spec.id);
    const specTitleKey = norm(spec.title);
    const base = byId.get(specIdKey) ?? (specTitleKey ? byTitle.get(specTitleKey) : undefined);
    if (!base) {
      missing.push(spec.id || spec.title || 'unknown-enemy-card');
      continue;
    }
    // Clone so we can safely override display title/tag without mutating the library.
    deck.push({
      ...base,
      title: spec.title ?? base.title,
      tag: enemyDef?.id ?? base.tag,
    });
  }

  if (!deck.length) return { deck: fallbackAllowed ? enemyLibrary : [], missing };
  return { deck, missing };
}


function isPowerStrikeCard(c: Card): boolean {
  const title = norm((c as any).title);
  const name = norm((c as any).name);
  const id = norm((c as any).id);

  // Match by known IDs so enemies can override display titles safely.
  const POWER_STRIKE_ID = '6998d4fa0bebb02c4299bfd4';
  const RITUAL_ID = '6998d54d0bebb02c4299bfd9';

  const isPowerStrike =
    id === POWER_STRIKE_ID ||
    title === 'power strike' ||
    name === 'power strike' ||
    id.includes('power-strike') ||
    id.includes('power_strike') ||
    id.includes('powerstrike');

  const isRitual =
    id === RITUAL_ID ||
    title === 'ritual' ||
    name === 'ritual' ||
    title.includes('ritual') ||
    name.includes('ritual') ||
    id.includes('ritual');

  return isPowerStrike || isRitual;
}

function isIronRendCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const title = norm((c as any).title);
  const name = norm((c as any).name);
  const id = norm((c as any).id);
  const IRON_REND_ID = '6998d68b0bebb02c4299bfe8';

  return (
    id === IRON_REND_ID ||
    title === 'iron rend' ||
    name === 'iron rend' ||
    id.includes('iron-rend') ||
    id.includes('iron_rend') ||
    id.includes('ironrend')
  );
}

function isCallOfTheVoidCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const title = norm((c as any).title);
  const name = norm((c as any).name);
  const id = norm((c as any).id);
  const CALL_OF_THE_VOID_ID = '69bac28ec3e5ef3bbff71ced';

  return (
    id === CALL_OF_THE_VOID_ID ||
    title === 'call of the void' ||
    name === 'call of the void' ||
    title.includes('call of the void') ||
    name.includes('call of the void') ||
    id.includes('call_of_the_void') ||
    id.includes('call-of-the-void') ||
    id.includes('callofthevoid')
  );
}

function isYahakaEnemy(enemyId?: string): boolean {
  return norm(enemyId).startsWith('yahaka_voidfeeder_');
}

function isEnemyFinisherCard(c: Card, enemyId?: string): boolean {
  if (enemyId === 'dreadbrand' && isIronRendCard(c)) return true;
  if (isYahakaEnemy(enemyId) && isCallOfTheVoidCard(c)) return true;
  return isPowerStrikeCard(c);
}

function findEnemyFinisherCard(enemyId: string | undefined, enemyDeck: Card[]): Card | null {
  if (enemyId === 'dreadbrand') {
    return findEnemyCard(enemyDeck, isIronRendCard);
  }
  if (isYahakaEnemy(enemyId)) {
    return findEnemyCard(enemyDeck, isCallOfTheVoidCard);
  }
  return findEnemyCard(enemyDeck, isPowerStrikeCard);
}

function findEnemyCard(enemyDeck: Card[], predicate: (c: Card) => boolean): Card | null {
  return enemyDeck.find(predicate) ?? null;
}

function recordEnemyAiAction(state: GameState, enemyCard: Card): GameState {
  const title = String(enemyCard?.title ?? enemyCard?.id ?? '').trim();
  if (!title) return state;
  const current = Array.isArray((state as any).enemyAiCurrentTurnActions) ? ([...((state as any).enemyAiCurrentTurnActions as string[])]) : [];
  const combat = Array.isArray((state as any).enemyAiCombatHistory) ? ([...((state as any).enemyAiCombatHistory as string[])]) : [];
  current.push(title);
  combat.push(title);
  return {
    ...state,
    enemyAiCurrentTurnActions: current,
    enemyAiCombatHistory: combat.slice(-48),
  } as GameState;
}

function rotateEnemyAiTurnHistory(state: GameState): GameState {
  const current = Array.isArray((state as any).enemyAiCurrentTurnActions) ? ([...((state as any).enemyAiCurrentTurnActions as string[])]) : [];
  return {
    ...state,
    enemyAiLastTurnActions: current,
    enemyAiCurrentTurnActions: [],
  } as GameState;
}

function recordPlayerDamageToEnemy(state: GameState, amount: number): GameState {
  const applied = Math.max(0, Math.floor(Number(amount) || 0));
  if (applied <= 0) return state;
  const current = Math.max(0, Math.floor(Number((state as any).playerTurnDamageToEnemy ?? 0) || 0));
  return {
    ...state,
    playerTurnDamageToEnemy: current + applied,
  } as GameState;
}


const MONSIGNOR_GUILT_TITLE = 'Guilt';

function isMonsignorEnemy(enemyId?: string): boolean {
  return norm(enemyId) === 'monsignor';
}

function isGuiltCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const title = norm((c as any).title);
  const specialType = norm((c as any).specialType);
  return title === norm(MONSIGNOR_GUILT_TITLE) || specialType === 'affliction';
}

function makeGuiltCard(state: GameState): Card {
  const template = (state as any).guiltTemplate as Card | null | undefined;
  const sourceId = String((template as any)?.id ?? '69c39caedc9b2da89a95be80');
  const instanceId = `${sourceId}__guilt__${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;

  return {
    ...(template ?? {}),
    id: instanceId,
    sourceId,
    title: 'Guilt',
    kind: 'magic',
    specialType: 'affliction',
    acquisitionLocked: true,
    subtitle: (template as any)?.subtitle ?? 'Affliction',
    tag: (template as any)?.tag ?? 'Affliction',
    description: (template as any)?.description ?? 'Lose 5 HP.',
    baseDescription: (template as any)?.baseDescription ?? (template as any)?.description ?? 'Lose 5 HP.',
    cost: { hp: 0, sp: 15 },
    effects: [{ type: 'loseHP', amount: 5 } as any],
    keywords: Array.isArray((template as any)?.keywords) ? [...((template as any).keywords as any[])] : [],
    delayTurns: 0,
  } as Card;
}

function addGuiltToPlayerDiscard(state: GameState, sourceLabel: string): GameState {
  const guilt = makeGuiltCard(state);
  const next = pushCardsToDiscard(state, [guilt]);
  return {
    ...next,
    log: [`${sourceLabel}: add 1 Guilt to the player's discard pile.`, ...next.log],
  } as GameState;
}

function countGuiltCards(cards: Card[] | undefined | null): number {
  return (Array.isArray(cards) ? cards : []).filter(isGuiltCard).length;
}

function countGuiltInHand(state: GameState): number {
  return countGuiltCards(allHandCards(state));
}

function getAllPlayerCardsForMonsignor(state: GameState): Card[] {
  const piles = getPlayerPileState(state);
  return [
    ...piles.deck,
    ...piles.discard,
    ...piles.exhaust,
    ...allHandCards(state),
  ];
}

function countGuiltInPlayerDeck(state: GameState): number {
  return countGuiltCards(getAllPlayerCardsForMonsignor(state));
}

type MonsignorDecreeRule = {
  id: string;
  text: string;
};

const MONSIGNOR_DECREE_RULES: MonsignorDecreeRule[] = [
  { id: 'max-two-plays', text: 'Do not play more than 2 cards.' },
  { id: 'no-armor-gain', text: 'Do not gain Armor this turn.' },
  { id: 'max-20-sp', text: 'Do not spend more than 20 SP this turn.' },
  { id: 'no-repeat-type', text: 'Do not play the same card type twice.' },
  { id: 'max-three-cards-in-hand', text: 'Do not end your turn with more than 3 cards in hand.' },
];

function pickMonsignorDecree(): MonsignorDecreeRule {
  const idx = Math.max(0, Math.min(MONSIGNOR_DECREE_RULES.length - 1, Math.floor(Math.random() * MONSIGNOR_DECREE_RULES.length)));
  return MONSIGNOR_DECREE_RULES[idx] ?? MONSIGNOR_DECREE_RULES[0];
}

function getPlayedCardTypeForDecree(card: Card): string {
  const specialType = String((card as any)?.specialType ?? '').trim();
  if (specialType) return specialType.toLowerCase();
  const tag = String((card as any)?.tag ?? '').trim();
  if (tag) return tag.toLowerCase();
  return String(card?.kind ?? '').trim().toLowerCase();
}

function breakMonsignorDecree(state: GameState, reason: string): GameState {
  const decree = (state as any).monsignorDecree;
  if (!decree || decree.broken) return state;
  let next = addGuiltToPlayerDiscard(state, 'Decree broken');
  return {
    ...next,
    monsignorDecree: { ...decree, broken: true, breakReason: reason },
    log: [`Decree broken: ${reason}`, ...next.log],
  } as GameState;
}

function maybeBreakMonsignorDecreeForPlay(state: GameState, card: Card): GameState {
  const decree = (state as any).monsignorDecree;
  if (!decree || decree.broken || state.phase !== 'player') return state;

  if (decree.id === 'max-two-plays' && (state.playerPlaysThisTurn || 0) > 2) {
    return breakMonsignorDecree(state, 'played more than 2 cards');
  }

  if (decree.id === 'max-20-sp' && Math.max(0, Number((state as any).monsignorSpSpentThisTurn ?? 0) || 0) > 20) {
    return breakMonsignorDecree(state, 'spent more than 20 SP');
  }

  if (decree.id === 'no-repeat-type') {
    const playedType = getPlayedCardTypeForDecree(card);
    const seen = Array.isArray((state as any).monsignorPlayedCardTypesThisTurn)
      ? ((state as any).monsignorPlayedCardTypesThisTurn as string[])
      : [];
    if (seen.filter((t) => t === playedType).length >= 2) {
      return breakMonsignorDecree(state, `played the same card type twice (${playedType})`);
    }
  }

  return state;
}

function isMonsignorJudgmentCard(c: Card | null | undefined): boolean {
  return norm((c as any)?.title) === 'judgment';
}

function isMonsignorLitanyOfShameCard(c: Card | null | undefined): boolean {
  return norm((c as any)?.title) === 'litany of shame';
}

function isMonsignorCardPlayable(state: GameState, c: Card | null | undefined): boolean {
  if (!c) return false;
  if (!isMonsignorEnemy(state.enemyId)) return true;
  const guiltInDeck = countGuiltInPlayerDeck(state);
  if (isMonsignorJudgmentCard(c)) return guiltInDeck >= 13;
  if (isMonsignorLitanyOfShameCard(c)) return guiltInDeck >= 3;
  return true;
}

function applyMonsignorEnemyCard(state: GameState, enemyCard: Card): GameState {
  const title = norm(enemyCard.title);
  const guiltInHand = countGuiltInHand(state);

  if (title === 'decree of restraint') {
    const decree = pickMonsignorDecree();
    return {
      ...state,
      monsignorDecree: { ...decree, broken: false },
      log: [`Decree of Restraint: ${decree.text}`, ...state.log],
    } as GameState;
  }

  if (title === 'crozier strike') {
    const dmg = 8 + (5 * guiltInHand);
    let next = { ...state, log: [`Crozier Strike scales with ${guiltInHand} Guilt in hand.`, ...state.log] } as GameState;
    return applyEffect(next, { type: 'damage', amount: dmg } as any, 'enemy');
  }

  if (title === 'brand of heresy') {
    return addGuiltToPlayerDiscard(state, 'Brand of Heresy');
  }

  if (title === 'penance tax') {
    const guiltInDeck = countGuiltInPlayerDeck(state);
    let next = { ...state, log: [`Penance Tax counts ${guiltInDeck} Guilt in the player deck.`, ...state.log] } as GameState;
    if (guiltInDeck <= 0) return next;
    return applyEffect(next, { type: 'damage', amount: guiltInDeck } as any, 'enemy');
  }

  if (title === 'false absolution') {
    const armorGain = 10 + (5 * guiltInHand);
    let next = { ...state, log: [`False Absolution scales with ${guiltInHand} Guilt in hand.`, ...state.log] } as GameState;
    return applyEffect(next, { type: 'block', amount: armorGain } as any, 'enemy');
  }

  if (title === 'judgment') {
    return applyEffect(state, { type: 'damage', amount: 99 } as any, 'enemy');
  }

  if (title === 'litany of shame') {
    let next = state;
    let guiltDrawn = 0;
    for (let i = 0; i < 3; i++) {
      const before = allHandCards(next).map((c) => c.id);
      const beforeSize = handCount(next);
      next = drawRandomExtraFromPiles(next, 'any', 1);
      const afterCards = allHandCards(next);
      if (handCount(next) <= beforeSize) continue;
      const drawnCard = afterCards.find((c) => !before.includes(c.id));
      if (isGuiltCard(drawnCard)) guiltDrawn += 1;
    }
    if (guiltDrawn > 0) {
      next = applyEffect(next, { type: 'loseSP', amount: guiltDrawn * 5 } as any, 'player');
    }
    return {
      ...next,
      log: [`Litany of Shame drew ${guiltDrawn} Guilt card(s).`, ...next.log],
    } as GameState;
  }

  return applyCard(state, enemyCard, 'enemy');
}

function isDarkApostleEnemy(enemyId?: string): boolean {
  return norm(enemyId) === 'dark_apostle';
}

function isDarkApostleCursedFateCard(c: Card | null | undefined): boolean {
  return norm((c as any)?.title) === 'cursed fate';
}

function effectsCountAsAttack(effects: Effect[] | null | undefined): boolean {
  if (!Array.isArray(effects)) return false;
  return effects.some((eff: any) => {
    const t = String(eff?.type ?? '').trim();
    return t === 'damage' || t === 'damageIgnoreArmor' || t === 'damageEqualToArmor' || t === 'damageEqualToReflect';
  });
}

function markPlayerAttackForTurn(state: GameState): GameState {
  const alreadyAttacked = !!((state as any).playerAttackedThisTurn);
  const curseActive = !!((state as any).darkApostleCursedFateActive);
  if (alreadyAttacked && !curseActive) return state;

  let next: GameState = {
    ...state,
    playerAttackedThisTurn: true,
  } as GameState;

  if (curseActive) {
    next = {
      ...next,
      darkApostleCursedFateActive: false,
      log: ['Cursed Fate is broken when the player attacks.', ...next.log],
    } as GameState;
  }

  return next;
}

function triggerDarkApostleCursedFate(state: GameState): GameState {
  let next = {
    ...state,
    log: ['Cursed Fate triggers.', ...state.log],
  } as GameState;
  next = applyIncomingEnemyDamage(next, 20, { sourceTitle: 'Cursed Fate' });
  next = applyEffect(next, { type: 'loseSP', amount: 20 } as any, 'enemy');
  return next;
}

function applyDarkApostleEnemyCard(state: GameState, enemyCard: Card): GameState {
  if (isDarkApostleCursedFateCard(enemyCard)) {
    return {
      ...state,
      darkApostleCursedFateActive: true,
      log: ['Cursed Fate is now active until the player attacks.', ...state.log],
    } as GameState;
  }

  return applyCard(state, enemyCard, 'enemy');
}


function isBlackhandEnemy(enemyId: string | undefined): boolean {
  return norm(enemyId) === 'blackhand' || norm(enemyId) === 'the blackhand';
}

function isPrimeVesselEnemy(enemyId: string | undefined): boolean {
  return norm(enemyId) === 'prime_vessel' || norm(enemyId) === 'the prime vessel';
}

function isChymistEnemy(enemyId: string | undefined): boolean {
  return norm(enemyId) === 'chymist';
}

function isMercenaryEnemy(enemyId: string | undefined): boolean {
  return norm(enemyId) === 'mercenary';
}

function isThresholdVerifierEnemy(enemyId: string | undefined): boolean {
  return norm(enemyId) === 'threshold_verifier' || norm(enemyId) === 'threshold verifier';
}

function isThresholdVerifierBlackCard(card: Card | null | undefined): boolean {
  if (!card) return false;
  // This combat base does not expose an explicit black/white flag on Card.
  // Threshold Verifier currently uses Magic cards as the gameplay proxy for black cards.
  return card.kind === 'magic';
}

function getUnauthorizedPassageLocks(state: GameState): Array<{ cardId: string; turns: number }> {
  const raw = Array.isArray((state as any).unauthorizedPassageLocks) ? ((state as any).unauthorizedPassageLocks as Array<any>) : [];
  return raw
    .map((entry) => ({
      cardId: String(entry?.cardId ?? '').trim(),
      turns: Math.max(0, Math.floor(Number(entry?.turns ?? 0) || 0)),
    }))
    .filter((entry) => entry.cardId.length > 0 && entry.turns > 0);
}

function getUnauthorizedPassageTurnsForCard(state: GameState, cardId: string | null | undefined): number {
  const key = String(cardId ?? '').trim();
  if (!key) return 0;
  const match = getUnauthorizedPassageLocks(state).find((entry) => entry.cardId === key);
  return match?.turns ?? 0;
}

function tickUnauthorizedPassageLocksAfterPlayerTurn(state: GameState): { locks: Array<{ cardId: string; turns: number }>; expiredCount: number } {
  const current = getUnauthorizedPassageLocks(state);
  if (current.length === 0) return { locks: [], expiredCount: 0 };
  const next: Array<{ cardId: string; turns: number }> = [];
  let expiredCount = 0;
  for (const entry of current) {
    const turns = Math.max(0, entry.turns - 1);
    if (turns > 0) next.push({ cardId: entry.cardId, turns });
    else expiredCount += 1;
  }
  return { locks: next, expiredCount };
}


function getCustodianTrackedCards(state: GameState): Card[] {
  return Array.isArray((state as any).custodianTrackedExiledCards)
    ? [...((state as any).custodianTrackedExiledCards as Card[])]
    : [];
}

function shouldTrackCustodianExile(state: GameState, card: Card): boolean {
  if (!isCustodianEnemy(state.enemyId)) return false;
  if (!card || card.kind === 'enemy') return false;
  return true;
}

function trackCustodianExiledCards(
  state: GameState,
  cards: Card[],
  options?: { causedByForfeiture?: boolean },
): GameState {
  const trackedNow = (Array.isArray(cards) ? cards : []).filter((card) =>
    shouldTrackCustodianExile(state, card),
  );
  if (trackedNow.length <= 0) return state;

  let next = {
    ...state,
    custodianTrackedExiledCards: [...getCustodianTrackedCards(state), ...trackedNow],
  } as GameState;

  let attackInc = 0;
  let defenseInc = 0;
  let utilityInc = 0;
  for (const card of trackedNow) {
    const trackedType = getCustodianTrackedCardType(card);
    if (trackedType === 'attack') attackInc += 1;
    else if (trackedType === 'defense') defenseInc += 1;
    else if (trackedType === 'utility') utilityInc += 1;
  }

  if (attackInc > 0) {
    next = {
      ...next,
      custodianExiledAttackCount:
        Math.max(0, Number((next as any).custodianExiledAttackCount ?? 0) || 0) + attackInc,
    } as GameState;
  }
  if (defenseInc > 0) {
    next = {
      ...next,
      custodianExiledDefenseCount:
        Math.max(0, Number((next as any).custodianExiledDefenseCount ?? 0) || 0) + defenseInc,
    } as GameState;
  }
  if (utilityInc > 0) {
    next = {
      ...next,
      custodianExiledUtilityCount:
        Math.max(0, Number((next as any).custodianExiledUtilityCount ?? 0) || 0) + utilityInc,
    } as GameState;
  }

  if (options?.causedByForfeiture) {
    const names = trackedNow.map((card) => card.title).join(', ');
    next = {
      ...next,
      log: [`Forfeiture exiles ${names}.`, ...next.log],
    } as GameState;
  }

  return next;
}

function resolveCustodianFullProcessing(state: GameState): GameState {
  let next = applyIncomingEnemyDamage(state, 40, { sourceTitle: 'Full Processing' });
  next = applyEffect(next, { type: 'block', amount: 30 } as any, 'enemy');
  next = applyEffect(next, { type: 'healHP', amount: 20 } as any, 'enemy');

  const trackedCards = getCustodianTrackedCards(next);
  if (trackedCards.length > 0) {
    const trackedSet = new Set<Card>(trackedCards);
    const recovered: Card[] = [];
    const exhaust = (next.exhaust ?? []).filter((card) => {
      if (trackedSet.has(card)) {
        recovered.push(card);
        return false;
      }
      return true;
    });
    const recoveredSet = new Set<Card>(recovered);
    const missing = trackedCards.filter((card) => !recoveredSet.has(card));
    const toReturn = [...recovered, ...missing];
    next = {
      ...next,
      deck: shuffle([...(next.deck ?? []), ...toReturn]),
      exhaust,
      log: [`Full Processing returns ${toReturn.length} exiled card(s) to the deck and reshuffles.`, ...next.log],
    } as GameState;
  } else {
    next = {
      ...next,
      log: ['Full Processing finds no tracked exiled cards to return.', ...next.log],
    } as GameState;
  }

  return {
    ...next,
    custodianTrackedExiledCards: [],
    custodianExiledAttackCount: 0,
    custodianExiledDefenseCount: 0,
    custodianExiledUtilityCount: 0,
  } as GameState;
}

function continueAfterEnemyPlayerPrompt(state: GameState): GameState {
  const pending = (state as any).pendingEnemyResolution as { consumesAction?: boolean } | null | undefined;
  let next = {
    ...state,
    pendingEnemyResolution: null,
    pendingChoice: null,
    pendingDiscard: null,
  } as GameState;

  next = settleGameOver(next);
  if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) return next;

  const enemyActionsBeforePlay = Math.max(0, Math.floor(Number((next as any).enemyActionsRemainingThisTurn ?? 1) || 1));
  const consumesEnemyAction = pending?.consumesAction !== false;
  const actionsRemaining = consumesEnemyAction
    ? Math.max(0, enemyActionsBeforePlay - 1)
    : enemyActionsBeforePlay;

  if (actionsRemaining > 0) {
    const enemyContinues = {
      ...next,
      enemyActionsRemainingThisTurn: actionsRemaining,
      phase: 'enemy',
      enemyAwaitingAck: false,
      enemyStartPending: false,
      log: ['Enemy acts again.', ...next.log],
    } as GameState;
    return reducer(enemyContinues, { type: 'RESOLVE_ENEMY_TURN' });
  }

  next = { ...next, enemyActionsRemainingThisTurn: 0 } as GameState;
  next = tickEnemyDamageDebuffAfterEnemyTurn(next);
  return startPlayerTurn(next);
}

function applyCustodianEnemyCard(state: GameState, enemyCard: Card): GameState {
  const title = custodianTitleKey(enemyCard.title);

  if (title === 'forfeiture') {
    return {
      ...state,
      custodianForfeitureTurns: 3,
      log: ['Forfeiture: for 3 turns, discarded player cards are exiled instead.', ...state.log],
    } as GameState;
  }

  if (title === 'inventory tax') {
    const playerHandCards = allHandCards(state);
    if (playerHandCards.length <= 0) {
      const next = applyEffect(state, { type: 'loseSP', amount: 25 } as any, 'player');
      return {
        ...next,
        log: ['Inventory Tax: no card can be discarded, so the player burns 25 SP.', ...next.log],
      } as GameState;
    }

    return {
      ...state,
      pendingChoice: {
        sourceTitle: enemyCard.title,
        sourceId: enemyCard.id,
        sourceKind: enemyCard.kind,
        sourceOwner: 'enemy',
        options: [
          { label: 'Burn 25 SP', effects: [] as any, custodianAction: 'burn_sp' } as any,
          { label: 'Discard a card', effects: [] as any, custodianAction: 'discard_card' } as any,
        ],
        after: { discard: 0, chain: false, finisher: false },
      },
      log: ['Inventory Tax: choose one.', ...state.log],
    } as GameState;
  }

  if (title === 'reissued violence') {
    const attacks = Math.max(0, Number((state as any).custodianExiledAttackCount ?? 0) || 0);
    const amount = attacks * 9;
    if (amount <= 0) {
      return { ...state, log: ['Reissued Violence finds no exiled Attacks.', ...state.log] } as GameState;
    }
    return applyIncomingEnemyDamage(state, amount, { sourceTitle: 'Reissued Violence' });
  }

  if (title === 'salvaged ward') {
    const defenses = Math.max(0, Number((state as any).custodianExiledDefenseCount ?? 0) || 0);
    const amount = defenses * 6;
    if (amount <= 0) {
      return { ...state, log: ['Salvaged Ward finds no exiled Defenses.', ...state.log] } as GameState;
    }
    return applyEffect(state, { type: 'block', amount } as any, 'enemy');
  }

  if (title === 'reprocessed essence') {
    const utilities = Math.max(0, Number((state as any).custodianExiledUtilityCount ?? 0) || 0);
    const amount = utilities * 3;
    if (amount <= 0) {
      return { ...state, log: ['Reprocessed Essence finds no exiled Utilities.', ...state.log] } as GameState;
    }
    return applyEffect(state, { type: 'healHP', amount } as any, 'enemy');
  }

  if (title === 'full processing') {
    return resolveCustodianFullProcessing(state);
  }

  return applyCard(state, enemyCard, 'enemy');
}

function applyThresholdVerifierEnemyCard(state: GameState, enemyCard: Card): GameState {
  const title = lampKeeperTitleKey(enemyCard.title);

  if (title === 'punitive measure') {
    const cur = Math.max(0, Number((state as any).punitiveMeasureTurns ?? 0) || 0);
    return {
      ...state,
      punitiveMeasureTurns: cur + 1,
      log: [`Punitive Measure: for ${cur + 1} turn(s), lose 5 HP whenever you play a black card.`, ...state.log],
    } as GameState;
  }

  if (title === 'unauthorized passage') {
    const handCards = allHandCards(state);
    if (!handCards.length) {
      return { ...state, log: ['Unauthorized Passage: no card in hand to lock.', ...state.log] } as GameState;
    }

    const current = getUnauthorizedPassageLocks(state);
    const lockedIds = new Set(current.map((entry) => entry.cardId));
    const unlockedCards = handCards.filter((card) => !lockedIds.has(card.id));
    const pool = unlockedCards.length > 0 ? unlockedCards : handCards;
    const target = pool[Math.floor(Math.random() * pool.length)] ?? pool[0];
    if (!target) {
      return { ...state, log: ['Unauthorized Passage: no valid target.', ...state.log] } as GameState;
    }

    const nextLocks = [...current];
    const idx = nextLocks.findIndex((entry) => entry.cardId === target.id);
    if (idx >= 0) nextLocks[idx] = { ...nextLocks[idx], turns: nextLocks[idx].turns + 2 };
    else nextLocks.push({ cardId: target.id, turns: 2 });

    return {
      ...state,
      unauthorizedPassageLocks: nextLocks,
      log: [`Unauthorized Passage: ${target.title} cannot be played for 2 turns.`, ...state.log],
    } as GameState;
  }

  if (title === 'counterweight') {
    const plays = Math.max(0, Math.floor(Number((state as any).lastPlayerTurnPlays ?? 0) || 0));
    const gain = plays * 8;
    if (gain <= 0) {
      return { ...state, log: ['Counterweight: the player played no cards last turn, so no Armor is gained.', ...state.log] } as GameState;
    }
    const next = applyEffect(state, { type: 'block', amount: gain } as any, 'enemy');
    return { ...next, log: [`Counterweight: +${gain} Armor (${plays} card${plays === 1 ? '' : 's'} played last turn).`, ...next.log] } as GameState;
  }

  return applyCard(state, enemyCard, 'enemy');
}

function isMercenaryTakeAimCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cd6d06da15bce3f890b819' || title === 'take aim';
}

function isMercenaryDeadeyeShotCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cd6d62da15bce3f890b81b' || title === 'deadeye shot';
}

function isMercenaryFeintCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cd6d99da15bce3f890b81d' || title === 'feint';
}

function isMercenaryGuttingStrikeCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cd6e01da15bce3f890b81f' || title === 'gutting strike';
}

function isMercenarySmokeBombCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cd6e52da15bce3f890b821' || title === 'smoke bomb';
}

function pickMercenaryPriorityCard(pool: Card[], state: GameState): Card | null {
  if (!Array.isArray(pool) || pool.length === 0) return null;
  const hasPrecision = !!((state as any).mercenaryPrecision);
  const hasAmbush = !!((state as any).mercenaryAmbush);

  if (hasPrecision) {
    const deadeye = findEnemyCard(pool, isMercenaryDeadeyeShotCard);
    if (deadeye) return deadeye;
  }
  if (hasAmbush) {
    const gutting = findEnemyCard(pool, isMercenaryGuttingStrikeCard);
    if (gutting) return gutting;
  }
  return null;
}

function pickMercenaryPriorityCardDeterministic(pool: Card[], state: GameState): Card | null {
  if (!Array.isArray(pool) || pool.length === 0) return null;
  const hasPrecision = !!((state as any).mercenaryPrecision);
  const hasAmbush = !!((state as any).mercenaryAmbush);

  if (hasPrecision) {
    const deadeye = findEnemyCard(pool, isMercenaryDeadeyeShotCard);
    if (deadeye) return deadeye;
  }
  if (hasAmbush) {
    const gutting = findEnemyCard(pool, isMercenaryGuttingStrikeCard);
    if (gutting) return gutting;
  }
  return null;
}

function applyMercenaryEnemyCard(state: GameState, enemyCard: Card): GameState {
  if (isMercenaryTakeAimCard(enemyCard)) {
    return {
      ...state,
      mercenaryPrecision: true,
      log: ['Take Aim: Precision gained.', ...state.log],
    } as GameState;
  }

  if (isMercenaryFeintCard(enemyCard)) {
    return {
      ...state,
      mercenaryAmbush: true,
      log: ['Feint: Ambush gained.', ...state.log],
    } as GameState;
  }

  if (isMercenarySmokeBombCard(enemyCard)) {
    let next = applyEffect(state, { type: 'block', amount: 25 } as any, 'enemy');
    next = {
      ...next,
      mercenaryAmbush: true,
      log: ['Smoke Bomb: Ambush gained.', ...next.log],
    } as GameState;
    return next;
  }

  if (isMercenaryDeadeyeShotCard(enemyCard)) {
    const empowered = !!((state as any).mercenaryPrecision);
    return {
      ...applyIncomingEnemyDamage(state, empowered ? 36 : 18, {
        sourceTitle: `${state.enemy.name} — Deadeye Shot${empowered ? ' (Precision)' : ''}`,
        suppressReflect: true,
      }),
      mercenaryPrecision: false,
    } as GameState;
  }

  if (isMercenaryGuttingStrikeCard(enemyCard)) {
    const empowered = !!((state as any).mercenaryAmbush);
    return {
      ...applyIncomingEnemyDamage(state, empowered ? 34 : 16, {
        sourceTitle: `${state.enemy.name} — Gutting Strike${empowered ? ' (Ambush)' : ''}`,
        ignoreArmor: true,
      }),
      mercenaryAmbush: false,
    } as GameState;
  }

  return applyCard(state, enemyCard, 'enemy');
}

function isChymistCompoundCard(card: Card | null | undefined): boolean {
  const title = norm(card?.title);
  return title === 'caustic compound'
    || title === 'mindrot compound'
    || title === 'iron compound'
    || title === 'catalyst compound'
    || title === 'sedative compound';
}

function shouldForceChymistOpeningCompound(state: GameState): boolean {
  return isChymistEnemy(state.enemyId) && !((state as any).chymistOpeningCompoundPlayed);
}

function shouldForceChymistCompoundNow(state: GameState): boolean {
  return shouldForceChymistOpeningCompound(state) || !!((state as any).chymistForceNextCompound);
}

function getActiveChymistCompound(state: GameState): ChymistCompoundId | null {
  const raw = String((state as any).activeCompound ?? '').trim().toLowerCase();
  if (raw === 'caustic' || raw === 'mindrot' || raw === 'iron' || raw === 'catalyst' || raw === 'sedative') {
    return raw as ChymistCompoundId;
  }
  return null;
}

function setActiveChymistCompound(state: GameState, compound: ChymistCompoundId, sourceTitle: string): GameState {
  return {
    ...state,
    activeCompound: compound,
    log: [`${sourceTitle}: ${compound.charAt(0).toUpperCase() + compound.slice(1)} Compound is now active.`, ...state.log],
  } as GameState;
}

function applyChymistCompoundTurnStart(state: GameState): GameState {
  const compound = getActiveChymistCompound(state);
  if (!compound) return state;

  if (compound === 'caustic') {
    return applyIncomingEnemyDamage(state, 4, { sourceTitle: 'Caustic Compound' });
  }

  if (compound === 'mindrot') {
    const beforeSp = Math.max(0, Number(state.player.sp ?? 0) || 0);
    const afterSp = clampPlayerSp(state, beforeSp - 6);
    const burned = Math.max(0, beforeSp - afterSp);
    return maybeTriggerZeroKey(
      state,
      {
        ...state,
        player: { ...state.player, sp: afterSp },
        log: [`Mindrot Compound burns ${burned} SP.`, ...state.log],
      } as GameState,
    );
  }

  return state;
}

function applyEnemyPlayerSpLoss(state: GameState, amount: number, sourceTitle: string): GameState {
  const beforeSp = Math.max(0, Number(state.player.sp ?? 0) || 0);
  const afterSp = clampPlayerSp(state, beforeSp - Math.max(0, Math.floor(Number(amount) || 0)));
  const lost = Math.max(0, beforeSp - afterSp);
  return maybeTriggerZeroKey(
    state,
    {
      ...state,
      player: { ...state.player, sp: afterSp },
      log: [
        ...(lost > 0 ? [`${sourceTitle} burns ${lost} SP.`] : [`${sourceTitle} burns 0 SP.`]),
        ...state.log,
      ],
    } as GameState,
  );
}

function applyChymistEnemyCard(state: GameState, enemyCard: Card): GameState {
  const title = norm(enemyCard.title);

  const withCompoundFlags = (shouldForceChymistOpeningCompound(state) || (state as any).chymistForceNextCompound)
    ? ({
        ...state,
        chymistOpeningCompoundPlayed: true,
        chymistForceNextCompound: false,
      } as GameState)
    : state;

  if (title === 'caustic compound') {
    return setActiveChymistCompound(withCompoundFlags, 'caustic', enemyCard.title);
  }
  if (title === 'mindrot compound') {
    return setActiveChymistCompound(withCompoundFlags, 'mindrot', enemyCard.title);
  }
  if (title === 'iron compound') {
    return setActiveChymistCompound(withCompoundFlags, 'iron', enemyCard.title);
  }
  if (title === 'catalyst compound') {
    return setActiveChymistCompound(withCompoundFlags, 'catalyst', enemyCard.title);
  }
  if (title === 'sedative compound') {
    return setActiveChymistCompound(withCompoundFlags, 'sedative', enemyCard.title);
  }

  if (title === 'caustic flask') {
    let next = withCompoundFlags;
    let spBurn = 0;
    for (const eff of (enemyCard.effects || [])) {
      if ((eff as any)?.type === 'loseSP') {
        spBurn += Math.max(0, Math.floor(Number((eff as any).amount) || 0));
        continue;
      }
      next = applyEffect(next, eff, 'enemy');
    }
    if (spBurn > 0) {
      next = applyEnemyPlayerSpLoss(next, spBurn, enemyCard.title);
    }
    return next;
  }

  let next = applyCard(withCompoundFlags, enemyCard, 'enemy');

  if (title === 'volatile reaction') {
    const hadCompound = !!getActiveChymistCompound(next);
    next = {
      ...next,
      activeCompound: null,
      chymistForceNextCompound: true,
      log: hadCompound ? ['Volatile Reaction consumes the active compound.', ...next.log] : next.log,
    } as GameState;
  }

  return next;
}

function getPrimeVesselCombatFlags(state: GameState) {
  const mods = ((state as any)?.relicMods ?? {}) as any;
  const active = isPrimeVesselEnemy(state.enemyId);
  return {
    pulseEngineActive: active && !!mods.primeVesselPulseEngineActive,
    synapticRetortActive: active && !!mods.primeVesselSynapticRetortActive,
    astralReliquaryActive: active && !!mods.primeVesselAstralReliquaryActive,
    primeCrucibleActive: active && !!mods.primeVesselPrimeCrucibleActive,
  };
}

function applyPrimeVesselEnemyCard(state: GameState, enemyCard: Card): GameState {
  const title = norm(enemyCard.title);
  const flags = getPrimeVesselCombatFlags(state);

  let next = applyCard(state, enemyCard, 'enemy');

  if (title === 'corruption surge') {
    if (flags.astralReliquaryActive) {
      next = applyEffect(next, { type: 'loseSP', amount: 20 } as any, 'enemy');
    }
    if (flags.primeCrucibleActive) {
      const currentActions = Math.max(
        0,
        Math.floor(Number((next as any).enemyActionsRemainingThisTurn ?? 1) || 1)
      );
      next = {
        ...next,
        enemyActionsRemainingThisTurn: currentActions + 1,
        log: ['Prime Crucible: Corruption Surge chains.', ...next.log],
      } as GameState;
    }
    return next;
  }

  if (title === 'containment breach') {
    if (flags.primeCrucibleActive) {
      next = applyEffect(next, { type: 'damage', amount: 40 } as any, 'enemy');
    }
    if (flags.pulseEngineActive) {
      next = applyEffect(next, { type: 'block', amount: 30 } as any, 'enemy');
    }
    return next;
  }

  if (title === 'protection protocol') {
    if (flags.synapticRetortActive) {
      next = applyEffect(next, { type: 'healHP', amount: 30 } as any, 'enemy');
    }
    if (flags.pulseEngineActive) {
      next = applyEffect(next, { type: 'reflect', percent: 50 } as any, 'enemy');
    }
    return next;
  }

  if (title === 'ruptured ascension') {
    if (flags.synapticRetortActive) {
      next = applyEffect(next, { type: 'healHP', amount: 40 } as any, 'enemy');
    }
    if (flags.astralReliquaryActive) {
      next = applyEffect(next, { type: 'loseSP', amount: 20 } as any, 'enemy');
    }
    return next;
  }

  return next;
}

function getPrimeVesselEnemyDelayGroups(card: Card): Array<{ turns: number; effects: Effect[] }> {
  const delayed = Array.isArray((card as any).delayed) ? ((card as any).delayed as Array<{ turns: number; effects: Effect[] }>) : [];
  if (delayed.length > 0) return delayed;

  const legacyDelayTurns = Math.max(0, Number((card as any).delayTurns || 0) || 0);
  if (legacyDelayTurns <= 0) return [];

  const effects = Array.isArray(card.effects)
    ? card.effects.filter((eff: any) => eff?.type !== 'discard')
    : [];

  return effects.length > 0 ? [{ turns: legacyDelayTurns, effects }] : [];
}

function applyEnemyCardDelayGroups(state: GameState, card: Card): GameState {
  const groups = getPrimeVesselEnemyDelayGroups(card);
  if (groups.length === 0) return state;

  const { astralReliquaryActive } = getPrimeVesselCombatFlags(state);
  let next = state;
  const sourceCard = { ...card, id: String(card.id), title: String(card.title), kind: card.kind } as Card;

  if (astralReliquaryActive) {
    next = {
      ...next,
      log: ['Astral Reliquary: enemy Delay effects resolve immediately.', ...next.log],
    } as GameState;
  }

  for (const group of groups) {
    const turns = Math.max(0, Number(group?.turns) || 0);
    const safe = Array.isArray(group?.effects)
      ? group.effects.filter((eff: any) => eff?.type !== 'discard')
      : [];
    if (safe.length === 0) continue;

    if (astralReliquaryActive || turns <= 0) {
      next = triggerDelayedEffectsNow(next, sourceCard, safe, 'enemy');
    } else {
      next = scheduleDelayed(next, sourceCard, safe, turns, 'enemy');
    }

    next = settleGameOver(next);
    if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) {
      return next;
    }
  }

  return next;
}

function isBlackhandSigilCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69caf81b816db67213cc7251' || title === 'blackhand sigil';
}

function isBlackhandMomentumCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cac01eb67bdde39f741f51' || title === 'momentum';
}

function isBlackhandEviscerateCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cabe41b67bdde39f741f45' || title === 'eviscerate';
}

function isBlackhandExecuteCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cabccbb67bdde39f741f43' || title === 'execute';
}

function isBlackhandPressurePointCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cabc50b67bdde39f741f41' || title === 'pressure point';
}

function isBlackhandShadowstepCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const id = norm((c as any).id);
  const title = norm((c as any).title);
  return id === '69cabb6bb67bdde39f741f3f' || title === 'shadowstep';
}

function isPlayerMarkedByBlackhand(state: GameState): boolean {
  return !!((state as any).playerMarkedByBlackhand);
}

function didBlackhandMarkPredateCurrentTurn(state: GameState): boolean {
  if (!isPlayerMarkedByBlackhand(state)) return false;
  const markedTurn = Number((state as any).playerMarkedTurn ?? -1);
  const currentTurn = Number((state as any).turn ?? 0);
  return Number.isFinite(markedTurn) && markedTurn < currentTurn;
}

function isBlackhandShadowstepActiveForCurrentTurn(state: GameState): boolean {
  return Number((state as any).blackhandShadowstepTurn ?? -1) === Number((state as any).turn ?? -999);
}

function isBlackhandPressurePointActiveForCurrentTurn(state: GameState): boolean {
  return Number((state as any).blackhandPressurePointTurn ?? -1) === Number((state as any).turn ?? -999);
}

function buildOffensiveEffectBlacklistForShadowstep(effect: any): any | null {
  if (!effect || typeof effect !== 'object') return effect;
  const t = String((effect as any).type ?? '').trim();
  if (
    t === 'damage' ||
    t === 'damageIgnoreArmor' ||
    t === 'damageEqualToArmor' ||
    t === 'damageEqualToReflect' ||
    t === 'setOpponentArmor' ||
    t === 'stun'
  ) {
    return null;
  }
  if (t === 'ifOpponentHasStun') {
    const thenBranch = Array.isArray((effect as any).then)
      ? (effect as any).then.map(buildOffensiveEffectBlacklistForShadowstep).filter(Boolean)
      : [];
    const elseBranch = Array.isArray((effect as any).else)
      ? (effect as any).else.map(buildOffensiveEffectBlacklistForShadowstep).filter(Boolean)
      : [];
    if (thenBranch.length === 0 && elseBranch.length === 0) return null;
    return { ...(effect as any), then: thenBranch, else: elseBranch };
  }
  if (t === 'chooseOne' && Array.isArray((effect as any).options)) {
    const options = (effect as any).options
      .map((opt: any) => {
        const effs = Array.isArray(opt?.effects)
          ? opt.effects.map(buildOffensiveEffectBlacklistForShadowstep).filter(Boolean)
          : [];
        const delayed = Array.isArray(opt?.delayed)
          ? opt.delayed
              .map((group: any) => {
                const groupEffects = Array.isArray(group?.effects)
                  ? group.effects.map(buildOffensiveEffectBlacklistForShadowstep).filter(Boolean)
                  : [];
                return groupEffects.length > 0 ? { ...group, effects: groupEffects } : null;
              })
              .filter(Boolean)
          : undefined;
        if (effs.length === 0 && (!delayed || delayed.length === 0)) return null;
        return { ...opt, effects: effs, delayed };
      })
      .filter(Boolean);
    if (options.length === 0) return null;
    return { ...(effect as any), options };
  }
  return effect;
}

function stripAttackEffectsForBlackhandShadowstep(card: Card, delayGroups: any[]): { cardImmediate: Card; delayGroups: any[] } {
  const filteredImmediate = Array.isArray(card.effects)
    ? card.effects.map(buildOffensiveEffectBlacklistForShadowstep).filter(Boolean)
    : [];
  const filteredDelayed = Array.isArray(delayGroups)
    ? delayGroups
        .map((group: any) => {
          const groupEffects = Array.isArray(group?.effects)
            ? group.effects.map(buildOffensiveEffectBlacklistForShadowstep).filter(Boolean)
            : [];
          return groupEffects.length > 0 ? { ...group, effects: groupEffects } : null;
        })
        .filter(Boolean)
    : [];
  return {
    cardImmediate: { ...card, effects: filteredImmediate as any },
    delayGroups: filteredDelayed,
  };
}

function enemyActionsAtTurnStart(state: GameState): { enemyActionsRemainingThisTurn: number; enemyBonusActionsNextTurn: number } {
  const bonus = Math.max(0, Math.floor(Number((state as any).enemyBonusActionsNextTurn ?? 0) || 0));
  return {
    enemyActionsRemainingThisTurn: 1 + bonus,
    enemyBonusActionsNextTurn: 0,
  };
}

function applyBlackhandEnemyCard(state: GameState, enemyCard: Card): GameState {
  if (isBlackhandSigilCard(enemyCard)) {
    return {
      ...state,
      playerMarkedByBlackhand: true,
      playerMarkedTurn: state.turn,
      log: ['Blackhand Sigil: you are Marked.', ...state.log],
    } as GameState;
  }

  if (isBlackhandShadowstepCard(enemyCard)) {
    return {
      ...state,
      blackhandShadowstepTurn: (Number(state.turn ?? 0) || 0) + 1,
      log: ['Shadowstep: the first Attack card you play next turn will be evaded.', ...state.log],
    } as GameState;
  }

  if (isBlackhandPressurePointCard(enemyCard)) {
    return {
      ...state,
      blackhandPressurePointTurn: (Number(state.turn ?? 0) || 0) + 1,
      log: [
        isPlayerMarkedByBlackhand(state)
          ? 'Pressure Point: on your next turn, chain overload also costs HP and those overload costs are doubled while Marked.'
          : 'Pressure Point: on your next turn, chain overload also costs HP.',
        ...state.log,
      ],
    } as GameState;
  }

  if (isBlackhandMomentumCard(enemyCard)) {
    const curBonus = Math.max(0, Math.floor(Number((state as any).enemyBonusActionsNextTurn ?? 0) || 0));
    return {
      ...state,
      enemyBonusActionsNextTurn: curBonus + 1,
      log: ['Momentum: The Blackhand does not consume this action and will take 2 actions on his next turn.', ...state.log],
    } as GameState;
  }

  if (isBlackhandEviscerateCard(enemyCard)) {
    let next = applyIncomingEnemyDamage(state, 40, {
      sourceTitle: `${state.enemy.name} — Eviscerate`,
      suppressReflect: true,
    });
    if (isPlayerMarkedByBlackhand(next)) {
      const beforeSp = Math.max(0, Number(next.player.sp ?? 0) || 0);
      const afterSp = clampPlayerSp(next, beforeSp - 20);
      const lost = Math.max(0, beforeSp - afterSp);
      next = maybeTriggerZeroKey(next, {
        ...next,
        player: { ...next.player, sp: afterSp },
        log: [
          ...(lost > 0 ? [`Eviscerate: Marked target loses ${lost} SP.`] : ['Eviscerate: Marked target has no SP left to lose.']),
          ...next.log,
        ],
      } as GameState);
    }
    return next;
  }

  if (isBlackhandExecuteCard(enemyCard)) {
    const doubled = didBlackhandMarkPredateCurrentTurn(state);
    return applyIncomingEnemyDamage(state, doubled ? 100 : 50, {
      sourceTitle: `${state.enemy.name} — Execute${doubled ? ' x2' : ''}`,
      ignoreArmor: true,
    });
  }

  return applyCard(state, enemyCard, 'enemy');
}

function isParalyzingVenomCard(c: Card | null | undefined): boolean {
  if (!c) return false;
  const title = norm((c as any).title);
  const name = norm((c as any).name);
  const id = norm((c as any).id);
  return id === norm(NESTED_HORROR_PARALYZING_VENOM.cardId) || title === norm(NESTED_HORROR_PARALYZING_VENOM.title) || name === norm(NESTED_HORROR_PARALYZING_VENOM.title);
}

function createInitialEnemyOngoingVenomState() {
  return {
    active: false,
    played: false,
    ticksResolved: 0,
    firstTickDamage: Math.max(0, Math.floor(Number(NESTED_HORROR_PARALYZING_VENOM.firstTickDamage) || 0)),
    damageStepPerTurn: Math.max(0, Math.floor(Number(NESTED_HORROR_PARALYZING_VENOM.damageStepPerTurn) || 0)),
  };
}

function shouldForceParalyzingVenomOnEnemyTurn(state: GameState): boolean {
  const venom = (state as any).enemyOngoingVenom;
  return state.enemyId === 'nested_horror' && state.turn === 1 && !(venom?.played);
}

function filterRandomEnemyDeck(state: GameState, enemyDeck: Card[]): Card[] {
  if (state.enemyId !== 'nested_horror') return enemyDeck;
  return enemyDeck.filter((c) => !isParalyzingVenomCard(c));
}

function markPlayerTookEnemyDamage(state: GameState, hpDamage: number, totalDamage: number): GameState {
  let next = state;
  if (totalDamage > 0) {
    next = { ...next, playerTookEnemyDamageThisCombat: true } as GameState;
  }
  if (hpDamage > 0) {
    next = { ...next, playerTookDamage: true } as GameState;
  }
  return next;
}

function maybeTriggerZeroKey(prevState: GameState, nextState: GameState): GameState {
  const mods = (nextState as any).relicMods as RelicCombatMods | undefined;
  const gain = Math.max(0, Number(mods?.spGainWhenZero ?? 0) || 0);
  const prevSp = Math.max(0, Number(prevState.player?.sp ?? 0) || 0);
  const nextSp = Math.max(0, Number(nextState.player?.sp ?? 0) || 0);
  if (gain <= 0 || prevSp <= 0 || nextSp !== 0) return nextState;
  const boostedSp = clampPlayerSp(nextState, nextSp + gain);
  let out = {
    ...nextState,
    player: { ...nextState.player, sp: boostedSp },
    log: [`Relic: SP is 0, gain +${gain} SP.`, ...nextState.log],
  } as GameState;
  out = pushRelicTriggerAmount(out, 'spGainWhenZero', gain);
  return out;
}

function applyDamageBonusToEffects(
  effs: any[],
  amount: number,
  options?: { includeIgnoreArmor?: boolean; onlyFirst?: boolean }
): { effects: any[]; applied: boolean } {
  if (!Array.isArray(effs) || effs.length === 0 || amount <= 0) {
    return { effects: Array.isArray(effs) ? effs : [], applied: false };
  }
  const includeIgnoreArmor = !!options?.includeIgnoreArmor;
  const onlyFirst = !!options?.onlyFirst;
  let applied = false;
  const effects = effs.map((e: any) => {
    const matches = e?.type === 'damage' || (includeIgnoreArmor && e?.type === 'damageIgnoreArmor');
    if (!matches) return e;
    if (onlyFirst && applied) return e;
    applied = true;
    return { ...e, amount: (Number(e.amount) || 0) + amount };
  });
  return { effects, applied };
}

function applyDamageBonusToCardAndDelayGroups(
  cardImmediate: Card,
  delayGroups: any[],
  amount: number,
  options?: { includeIgnoreArmor?: boolean; onlyFirst?: boolean }
): { cardImmediate: Card; delayGroups: any[]; applied: boolean } {
  let applied = false;
  const immediateBoost = applyDamageBonusToEffects(cardImmediate.effects || [], amount, options);
  applied = immediateBoost.applied;
  let nextCardImmediate = immediateBoost.applied
    ? ({ ...cardImmediate, effects: immediateBoost.effects } as Card)
    : cardImmediate;
  let nextDelayGroups = delayGroups;
  if (Array.isArray(delayGroups) && delayGroups.length > 0) {
    nextDelayGroups = delayGroups.map((group: any) => {
      if (!Array.isArray(group?.effects)) return group;
      const boost = applyDamageBonusToEffects(group.effects, amount, {
        includeIgnoreArmor: options?.includeIgnoreArmor,
        onlyFirst: !!options?.onlyFirst && !applied,
      });
      if (boost.applied) applied = true;
      return boost.applied ? { ...group, effects: boost.effects } : group;
    });
  }
  return { cardImmediate: nextCardImmediate, delayGroups: nextDelayGroups, applied };
}

function applyIncomingEnemyDamage(
  state: GameState,
  incomingRaw: number,
  options?: { ignoreArmor?: boolean; sourceTitle?: string; flavorLabel?: string; suppressReflect?: boolean }
): GameState {
  const sourceTitle = String(options?.sourceTitle ?? state.enemy.name ?? 'Enemy');
  const flavorLabel = options?.flavorLabel ? ` ${options.flavorLabel}` : '';
  const ignoreArmor = !!options?.ignoreArmor;
  const suppressReflect = !!options?.suppressReflect;
  let incoming = Math.max(0, Math.floor(Number(incomingRaw) || 0));
  if (incoming <= 0) return state;

  const dmgRed = Math.max(0, Number((state as any).damageReductionIfLargeDeck ?? 0) || 0);
  const incomingBeforeDamageReduction = incoming;
  if (dmgRed > 0) {
    incoming = Math.max(0, incoming - dmgRed);
  }
  const negate = Math.max(0, Number(state.player.negateNextDamage ?? 0) || 0);

  if (negate > 0 && incoming > 0) {
    const nextPlayer = { ...state.player, negateNextDamage: Math.max(0, negate - 1) };
    let outState: GameState = {
      ...state,
      player: nextPlayer,
      log: [`${sourceTitle} deals ${incoming}${flavorLabel} (reduced to 0).`, ...state.log],
    };
    const damageReducedByRelic = Math.max(0, incomingBeforeDamageReduction - incoming);
    if (damageReducedByRelic > 0) {
      outState = pushRelicTriggerAmount(outState, 'damageReductionIfLargeDeck', damageReducedByRelic);
    }
    return outState;
  }

  const pct = clampPlayerReflectPct(state, state.player.reflectPct || 0);
  const extraReflectTriggers = Math.max(0, Math.floor(Number((state as any).nextReflectTriggerTwice ?? 0) || 0));
  const reflectTriggers = 1 + extraReflectTriggers;

  if (!suppressReflect && pct > 0 && incoming > 0) {
    const reflectedBase = Math.max(0, Math.round((incoming * pct) / 100));
    const damageToPlayerBase = incoming;

    let playerHp = state.player.hp;
    let playerArmor = state.player.armor;
    let dmgToPlayerHp = 0;
    let totalDamageToPlayer = 0;
    if (ignoreArmor) {
      playerHp = Math.max(0, state.player.hp - damageToPlayerBase);
      dmgToPlayerHp = Math.max(0, state.player.hp - playerHp);
      totalDamageToPlayer = dmgToPlayerHp;
    } else {
      const dealt = dealDamage(state.player.hp, state.player.armor, damageToPlayerBase);
      playerHp = dealt.newHp;
      playerArmor = dealt.newArmor;
      dmgToPlayerHp = dealt.dmgToHp;
      totalDamageToPlayer = Math.max(0, (state.player.hp + state.player.armor) - (playerHp + playerArmor));
    }

    let enemyHp = state.enemy.hp;
    let enemyArmor = state.enemy.armor;
    let reflectedToEnemyHpTotal = 0;
    let exactKillByReflect = false;
    for (let i = 0; i < reflectTriggers; i++) {
      const beforeReflectHp = enemyHp;
      const beforeReflectArmor = enemyArmor;
      const r = dealDamage(enemyHp, enemyArmor, reflectedBase);
      enemyHp = r.newHp;
      enemyArmor = r.newArmor;
      reflectedToEnemyHpTotal += r.dmgToHp;
      if (!exactKillByReflect && beforeReflectHp > 0 && enemyHp === 0 && reflectedBase === (beforeReflectHp + beforeReflectArmor)) {
        exactKillByReflect = true;
      }
    }
    const totalReflectedBase = reflectedBase * reflectTriggers;

    const nextPlayer = {
      ...state.player,
      hp: playerHp,
      armor: playerArmor,
      reflectPct: 0,
    };
    const nextEnemy = { ...state.enemy, hp: enemyHp, armor: enemyArmor };

    let updatedState: GameState = {
      ...state,
      player: nextPlayer,
      enemy: nextEnemy,
      nextReflectTriggerTwice: 0,
      log: [
        `${sourceTitle} deals ${incoming}${flavorLabel} (Player takes ${damageToPlayerBase} -> ${dmgToPlayerHp} to HP). Reflected ${reflectedBase}${reflectTriggers > 1 ? ` x${reflectTriggers} = ${totalReflectedBase}` : ''} (${reflectedToEnemyHpTotal} to enemy HP) - Reflect ${pct}% consumed.`,
        ...state.log,
      ],
    };

    const damageReducedByRelic = Math.max(0, incomingBeforeDamageReduction - incoming);
    if (damageReducedByRelic > 0) {
      updatedState = pushRelicTriggerAmount(updatedState, 'damageReductionIfLargeDeck', damageReducedByRelic);
    }
    updatedState = markPlayerTookEnemyDamage(updatedState, dmgToPlayerHp, totalDamageToPlayer);
    if (exactKillByReflect) {
      updatedState = { ...updatedState, enemyKilledExactlyAtZero: true } as GameState;
    }

    {
      const mods = (state as any).relicMods as RelicCombatMods | undefined;
      const gainHp = Math.max(0, Number(mods?.reflectBonusHp ?? 0) || 0) * reflectTriggers;
      if (gainHp > 0) {
        const healResult = applyPlayerHealingWithInvertedHeart(updatedState, gainHp);
        const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
        updatedState = {
          ...healResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            `Relic: reflect heals +${gainHp} HP.`,
            ...updatedState.log,
          ],
        };
        updatedState = pushRelicTriggerAmount(updatedState, 'reflectBonusHp', gainHp);
      }
      const gainSp = Math.max(0, Number(mods?.reflectBonusSp ?? 0) || 0) * reflectTriggers;
      if (gainSp > 0) {
        const newSp = clampPlayerSp(updatedState, (updatedState.player.sp || 0) + gainSp);
        updatedState = {
          ...updatedState,
          player: { ...updatedState.player, sp: newSp },
          log: [`Relic: reflect grants +${gainSp} SP.`, ...updatedState.log],
        };
        updatedState = pushRelicTriggerAmount(updatedState, 'reflectBonusSp', gainSp);
      }
      const draw = Math.max(0, Number(mods?.reflectBonusDraw ?? 0) || 0) * reflectTriggers;
      if (draw > 0) {
        const before = handCount(updatedState);
        updatedState = drawRandomExtraFromPiles(updatedState, 'any', draw);
        const drewCards = Math.max(0, handCount(updatedState) - before);
        if (drewCards > 0) {
          updatedState = { ...updatedState, log: [`Relic: reflect draws ${drewCards} card(s).`, ...updatedState.log] };
          updatedState = pushRelicTriggerAmount(updatedState, 'reflectBonusDraw', drewCards, { distribute: 'sequential' });
        }
      }
    }

    {
      const stacks = Math.max(0, Math.floor(Number((state as any).nextReflectGainSp ?? 0) || 0));
      if (stacks > 0) {
        const gainAmt = Math.max(0, Math.floor(reflectedBase * stacks * reflectTriggers));
        const beforeSp = updatedState.player.sp || 0;
        const afterSp = clampPlayerSp(updatedState, beforeSp + gainAmt);
        const actual = Math.max(0, afterSp - beforeSp);
        updatedState = {
          ...updatedState,
          nextReflectGainSp: 0,
          player: { ...updatedState.player, sp: afterSp },
          log: [`Reflect converts to +${actual} SP.`, ...updatedState.log],
        };
      }
    }
    return updatedState;
  }

  let nextPlayer = state.player;
  let dmgToPlayerHp = 0;
  let totalDamageToPlayer = 0;
  if (ignoreArmor) {
    const newHp = Math.max(0, state.player.hp - incoming);
    dmgToPlayerHp = Math.max(0, state.player.hp - newHp);
    totalDamageToPlayer = dmgToPlayerHp;
    nextPlayer = { ...state.player, hp: newHp };
  } else {
    const dealt = dealDamage(state.player.hp, state.player.armor, incoming);
    nextPlayer = { ...state.player, hp: dealt.newHp, armor: dealt.newArmor };
    dmgToPlayerHp = dealt.dmgToHp;
    totalDamageToPlayer = Math.max(0, (state.player.hp + state.player.armor) - (dealt.newHp + dealt.newArmor));
  }

  let outState: GameState = {
    ...state,
    player: nextPlayer,
    log: [`${sourceTitle} deals ${incoming}${flavorLabel} (${dmgToPlayerHp} to HP).`, ...state.log],
  };
  const damageReducedByRelic = Math.max(0, incomingBeforeDamageReduction - incoming);
  if (damageReducedByRelic > 0) {
    outState = pushRelicTriggerAmount(outState, 'damageReductionIfLargeDeck', damageReducedByRelic);
  }
  outState = markPlayerTookEnemyDamage(outState, dmgToPlayerHp, totalDamageToPlayer);
  return outState;
}

function applyIncomingPlayerDamage(
  state: GameState,
  incomingRaw: number,
  options?: { ignoreArmor?: boolean; sourceTitle?: string; flavorLabel?: string }
): GameState {
  const sourceTitle = String(options?.sourceTitle ?? state.player.name ?? 'Player');
  const flavorLabel = options?.flavorLabel ? ` ${options.flavorLabel}` : '';
  const ignoreArmor = !!options?.ignoreArmor;
  const incomingBase = Math.max(0, Math.floor(Number(incomingRaw) || 0));
  if (incomingBase <= 0) return state;

  const { pulseEngineActive } = getPrimeVesselCombatFlags(state);
  const afterPulseEngine = pulseEngineActive
    ? Math.max(0, Math.round(incomingBase * 0.7))
    : incomingBase;
  const reducedByPulseEngine = Math.max(0, incomingBase - afterPulseEngine);
  const ironCompoundActive = getActiveChymistCompound(state) === 'iron';
  const incoming = ironCompoundActive
    ? Math.max(0, Math.round(afterPulseEngine * 0.75))
    : afterPulseEngine;
  const reducedByIronCompound = Math.max(0, afterPulseEngine - incoming);

  const healPer = Math.max(0, Math.floor(Number((state as any).healPerDamageDealtThisTurn ?? 0) || 0));
  const appliedDamage = ignoreArmor
    ? Math.max(0, Math.min(incoming, Math.max(0, state.enemy.hp || 0)))
    : Math.max(0, Math.min(incoming, Math.max(0, (state.enemy.hp || 0) + (state.enemy.armor || 0))));
  const healWant = healPer > 0 ? appliedDamage * healPer : 0;

  let enemyHp = state.enemy.hp;
  let enemyArmor = state.enemy.armor;
  let dmgToEnemyHp = 0;
  let exactKill = false;

  if (ignoreArmor) {
    const wasAlive = enemyHp > 0;
    const nextEnemyHp = Math.max(0, enemyHp - incoming);
    dmgToEnemyHp = Math.max(0, enemyHp - nextEnemyHp);
    exactKill = wasAlive && nextEnemyHp === 0 && incoming === enemyHp;
    enemyHp = nextEnemyHp;
  } else {
    const dealt = dealDamage(enemyHp, enemyArmor, incoming);
    dmgToEnemyHp = dealt.dmgToHp;
    exactKill = enemyHp > 0 && dealt.newHp === 0 && incoming === (enemyHp + enemyArmor);
    enemyHp = dealt.newHp;
    enemyArmor = dealt.newArmor;
  }

  const enemyReflectPct = Math.max(0, Math.min(500, Math.floor(Number(state.enemy.reflectPct) || 0)));
  const reflectedBase = enemyReflectPct > 0 ? Math.max(0, Math.round((incoming * enemyReflectPct) / 100)) : 0;

  let nextState: GameState = {
    ...state,
    enemy: {
      ...state.enemy,
      hp: enemyHp,
      armor: enemyArmor,
      reflectPct: enemyReflectPct > 0 ? 0 : state.enemy.reflectPct,
    },
    log: [
      `${sourceTitle} deals ${incoming}${flavorLabel}${reducedByPulseEngine > 0 ? ` (Pulse Engine reduces ${incomingBase} to ${afterPulseEngine}).` : ''}${reducedByIronCompound > 0 ? ` (Iron Compound reduces ${afterPulseEngine} to ${incoming}).` : ''}${ignoreArmor ? ` (ignores Armor: ${dmgToEnemyHp} to HP).` : ` (${dmgToEnemyHp} to HP).`}${enemyReflectPct > 0 ? ` Reflected ${reflectedBase} back to Player - Reflect ${enemyReflectPct}% consumed.` : ''}`,
      ...state.log,
    ],
  };

  if (exactKill) {
    nextState = { ...nextState, enemyKilledExactlyAtZero: true } as GameState;
  }

  nextState = recordPlayerDamageToEnemy(nextState, appliedDamage);

  if (healWant > 0) {
    const healResult = applyPlayerHealingWithInvertedHeart(nextState, healWant);
    if (healResult.gainedHp > 0 || healResult.gainedArmor > 0 || healResult.gainedSp > 0) {
      const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
      nextState = {
        ...healResult.state,
        log: [
          ...(overflowLog ? [overflowLog] : []),
          `Grim Renewal: heal +${healResult.gainedHp} HP.`,
          ...nextState.log,
        ],
      };
    }
  }

  if (enemyReflectPct > 0 && reflectedBase > 0) {
    nextState = applyIncomingEnemyDamage(nextState, reflectedBase, {
      sourceTitle: `${state.enemy.name} Reflect`,
    });
  }

  return nextState;
}

function applyDirectPlayerHpLoss(state: GameState, amount: number, sourceTitle: string): GameState {
  return applyIncomingEnemyDamage(state, amount, { ignoreArmor: true, sourceTitle, flavorLabel: 'venom damage' });
}

function resolveEnemyOngoingVenomTick(state: GameState, sourceTitle: string): GameState {
  const venom = (state as any).enemyOngoingVenom;
  if (!venom?.active) return state;
  const tickNumber = Math.max(1, (Math.floor(Number(venom.ticksResolved) || 0) + 1));
  const firstTickDamage = Math.max(0, Math.floor(Number(venom.firstTickDamage) || 0));
  const damageStepPerTurn = Math.max(0, Math.floor(Number(venom.damageStepPerTurn) || 0));
  const amount = Math.max(0, firstTickDamage + ((tickNumber - 1) * damageStepPerTurn));
  let next = applyDirectPlayerHpLoss(state, amount, sourceTitle);
  next = {
    ...next,
    enemyOngoingVenom: {
      ...venom,
      active: next.player.hp > 0 && next.enemy.hp > 0,
      ticksResolved: tickNumber,
    },
  };
  return next;
}

function activateParalyzingVenom(state: GameState, enemyCard: Card): GameState {
  const current = (state as any).enemyOngoingVenom ?? createInitialEnemyOngoingVenomState();
  let next: GameState = {
    ...state,
    enemyOngoingVenom: {
      ...current,
      active: true,
      played: true,
      ticksResolved: 0,
      firstTickDamage: Math.max(0, Math.floor(Number(current.firstTickDamage ?? NESTED_HORROR_PARALYZING_VENOM.firstTickDamage) || 0)),
      damageStepPerTurn: Math.max(0, Math.floor(Number(current.damageStepPerTurn ?? NESTED_HORROR_PARALYZING_VENOM.damageStepPerTurn) || 0)),
    },
    log: [`${state.enemy.name} injects ${enemyCard.title}.`, ...state.log],
  };
  return resolveEnemyOngoingVenomTick(next, enemyCard.title);
}

const clampTo = (v: unknown, cap: unknown): number => {
  const n = Math.floor(Number(v) || 0);
  const c = Math.max(0, Math.floor(Number(cap) || 0));
  // If cap is missing/invalid, fall back to 100
  const safeCap = c > 0 ? c : 100;
  return Math.max(0, Math.min(safeCap, n));
};

const capEnemyHp = (state: any) => {
  const enemyDef = findEnemyDef(state?.enemyId ?? state?.enemy?.name ?? '');
  const base = Math.floor(Number(enemyDef?.hp ?? state?.enemy?.hp ?? 100) || 100);
  return Math.max(1, base);
};

const clampEnemyHp = (state: any, v: unknown): number => clampTo(v, capEnemyHp(state));

// Enemy can stay hard-capped at 100 for SP unless a future enemy-specific SP cap is introduced.
const clamp100 = (v: unknown): number => clampTo(v, 100);

// Player caps come from relicMods (sent by CombatOverlay), plus any temporary
// in-combat Max HP bonuses (e.g. Endure).
const capHp = (state: any) => {
  const base = Math.floor(Number(state?.relicMods?.capHp ?? 100) || 100);
  const bonus = Math.max(0, Math.floor(Number(state?.tempMaxHpBonus ?? 0) || 0));
  return Math.max(0, base + bonus);
};
const capSp = (state: any) => (state?.relicMods?.capSp ?? 100);

// Player-only caps (combat balance rules)
// - Armor caps at the same value as MaxHP (capHp)
// - Reflect caps at the same value as MaxHP (capHp)
const clampPlayerArmor = (state: any, v: unknown): number => clampTo(v, capHp(state));
const clampPlayerReflectPct = (state: any, v: unknown): number => {
  const n = Math.floor(Number(v) || 0);
  return Math.max(0, Math.min(capHp(state), n));
};

const clampPlayerHp = (state: any, v: unknown): number => clampTo(v, capHp(state));
const clampPlayerSp = (state: any, v: unknown): number => clampTo(v, capSp(state));

function playerHasInvertedHeart(state: GameState): boolean {
  return !!((state as any).relicMods?.invertedHeart);
}

function applyPlayerArmorGainWithInvertedHeart(
  state: GameState,
  amount: number
): {
  state: GameState;
  gainedArmor: number;
  overflowArmor: number;
  gainedSp: number;
} {
  const gain = Math.max(0, Math.floor(Number(amount) || 0));
  if (gain <= 0) {
    return { state, gainedArmor: 0, overflowArmor: 0, gainedSp: 0 };
  }

  const beforeArmor = Math.max(0, Math.floor(Number(state.player.armor || 0) || 0));
  const targetArmor = beforeArmor + gain;
  const nextArmor = clampPlayerArmor(state, targetArmor);
  const overflowArmor = Math.max(0, targetArmor - nextArmor);

  let nextSp = Math.max(0, Math.floor(Number(state.player.sp || 0) || 0));
  let gainedSp = 0;

  if (overflowArmor > 0 && playerHasInvertedHeart(state)) {
    const cappedSp = clampPlayerSp(state, nextSp + overflowArmor);
    gainedSp = Math.max(0, cappedSp - nextSp);
    nextSp = cappedSp;
  }

  return {
    state: {
      ...state,
      player: { ...state.player, armor: nextArmor, sp: nextSp },
    },
    gainedArmor: Math.max(0, nextArmor - beforeArmor),
    overflowArmor,
    gainedSp,
  };
}

function applyPlayerHealingWithInvertedHeart(
  state: GameState,
  amount: number
): {
  state: GameState;
  gainedHp: number;
  overflowHp: number;
  gainedArmor: number;
  gainedSp: number;
} {
  const heal = Math.max(0, Math.floor(Number(amount) || 0));
  if (heal <= 0) {
    return { state, gainedHp: 0, overflowHp: 0, gainedArmor: 0, gainedSp: 0 };
  }

  const beforeHp = Math.max(0, Math.floor(Number(state.player.hp || 0) || 0));
  const targetHp = beforeHp + heal;
  const nextHp = clampPlayerHp(state, targetHp);
  const overflowHp = Math.max(0, targetHp - nextHp);

  let nextState: GameState = {
    ...state,
    player: { ...state.player, hp: nextHp },
  };

  let gainedArmor = 0;
  let gainedSp = 0;

  if (overflowHp > 0 && playerHasInvertedHeart(state)) {
    const armorResult = applyPlayerArmorGainWithInvertedHeart(nextState, overflowHp);
    nextState = armorResult.state;
    gainedArmor = armorResult.gainedArmor;
    gainedSp = armorResult.gainedSp;
  }

  return {
    state: nextState,
    gainedHp: Math.max(0, nextHp - beforeHp),
    overflowHp,
    gainedArmor,
    gainedSp,
  };
}

function formatInvertedHeartHealOverflowLog(
  result: {
    overflowHp: number;
    gainedArmor: number;
    gainedSp: number;
  }
): string | null {
  if (result.overflowHp <= 0 || (result.gainedArmor <= 0 && result.gainedSp <= 0)) return null;
  const pieces: string[] = [];
  if (result.gainedArmor > 0) pieces.push(`+${result.gainedArmor} Armor`);
  if (result.gainedSp > 0) pieces.push(`+${result.gainedSp} SP`);
  return `Relic: Inverted Heart turns excess healing into ${pieces.join(' and ')}.`;
}

function formatInvertedHeartArmorOverflowLog(
  result: {
    overflowArmor: number;
    gainedSp: number;
  }
): string | null {
  if (result.overflowArmor <= 0 || result.gainedSp <= 0) return null;
  return `Relic: Inverted Heart turns excess Armor into +${result.gainedSp} SP.`;
}

const PLAYER_WEAKEN_MULT = 0.5;

function applyPlayerWeaken(state: GameState, by: 'player' | 'enemy', amount: unknown): number {
  const n = Math.max(0, Math.floor(Number(amount) || 0));
  if (by !== 'player') return n;
  const turns = Math.max(0, Number((state as any).playerWeakenedTurns ?? 0) || 0);
  if (turns <= 0) return n;
  return Math.max(0, Math.floor(n * PLAYER_WEAKEN_MULT));
}





function handCount(state: GameState): number {
  const m = state.hand?.main;
  return (m?.physical ? 1 : 0) + (m?.magic ? 1 : 0) + (state.hand?.extra?.length || 0);
}

function normalizePlayerSourceKind(kind: unknown): 'physical' | 'magic' | undefined {
  if (kind === 'physical' || kind === 'magic') return kind;
  return undefined;
}

function shouldQueueAutoFocus(state: GameState, sourceKind: unknown): boolean {
  return normalizePlayerSourceKind(sourceKind) === 'magic' && handCount(state) === 0;
}

function queueAutoFocus(state: GameState): GameState {
  return {
    ...state,
    canPlay: false,
    autoFocusPending: true,
    log: ['Hand empty: Focus triggers automatically.', ...state.log],
  } as GameState;
}

function finalizePlayerAction(state: GameState, options: { chain: boolean; finisher: boolean; sourceKind?: unknown }): GameState {
  const { chain, finisher, sourceKind } = options;

  if (shouldQueueAutoFocus(state, sourceKind)) {
    return settleGameOver(queueAutoFocus(state));
  }

  if (chain && !finisher) {
    return settleGameOver({
      ...state,
      canPlay: true,
      autoFocusPending: false,
      log: ['Chain: you may play another card.', ...state.log],
    } as GameState);
  }

  let next: GameState = {
    ...state,
    canPlay: false,
    autoFocusPending: false,
    log: ['Turn ends.', ...state.log],
  } as GameState;
  next = reducer(next, { type: 'END_PLAYER_TURN' } as any);
  return settleGameOver(next);
}

/**
 * Handle a number of cards being drawn into the player's hand. This function
 * increments the per-turn draw counter (drawnThisTurn) and applies any SP
 * gain from the Dealing Die relic for cards drawn beyond the first. The
 * function should be called once for each card that was actually drawn
 * (i.e. when the hand size increases). If no cards were drawn (for example
 * because the hand was full and a relic converted the draw into HP), then
 * this function should not be called.
 *
 * @param state The current game state before accounting for these draws.
 * @param count The number of cards drawn. Typically 1 when called.
 * @returns A new GameState reflecting updated drawnThisTurn and SP gains.
 */
function handleDrawn(state: GameState, count: number): GameState {
  if ((state as any).suppressDrawTrackingThisTurn) return state;
  let next: GameState = state;
  const mods = (state as any).relicMods as RelicCombatMods | undefined;
  const spPerCard = Math.max(0, Number(mods?.drawBeyondFirstGainSp ?? 0) || 0);
  let prevDrawn = Math.max(0, Number((state as any).drawnThisTurn ?? 0) || 0);
  for (let i = 0; i < count; i++) {
    const newDrawn = prevDrawn + 1;
    prevDrawn = newDrawn;
    // Always update the drawnThisTurn counter
    next = { ...next, drawnThisTurn: newDrawn } as GameState;
    // Award SP only if the card drawn is beyond the first this turn
    if (spPerCard > 0 && newDrawn > 1) {
      const newSp = clampPlayerSp(next, (next.player.sp || 0) + spPerCard);
      next = {
        ...next,
        player: { ...next.player, sp: newSp },
        log: [`Relic: draw beyond first, gain +${spPerCard} SP.`, ...next.log],
      } as GameState;
      next = pushRelicTriggerAmount(next, 'drawBeyondFirstGainSp', spPerCard);
    }
  }
  return next;
}

function allHandCards(state: GameState): Card[] {
  const out: Card[] = [];
  const m = state.hand?.main;
  if (m?.physical) out.push(m.physical);
  if (m?.magic) out.push(m.magic);
  for (const c of state.hand?.extra || []) out.push(c);
  return out;
}


// --- Addendum tracking (used by Red Ledger Seal / Sealed Receipt) ---
// "Seen" means a card with Addendum entered the player's hand at least once this combat.
// "Played" means that card was played at least once this combat.
// "Paid all Addendum costs" means: for every played Addendum card, the Addendum mode was used.
function noteAddendumSeen(state: GameState, card: Card): GameState {
  if (!(card as any)?.addendum) return state;
  const prev: string[] = Array.isArray((state as any).addendumSeenThisCombat)
    ? (state as any).addendumSeenThisCombat
    : [];
  if (prev.includes(card.id)) return state;
  return { ...state, addendumSeenThisCombat: [...prev, card.id] } as GameState;
}

function noteAddendumPlayed(state: GameState, card: Card, usedAddendum: boolean): GameState {
  if (!(card as any)?.addendum) return state;

  const seenPrev: string[] = Array.isArray((state as any).addendumSeenThisCombat)
    ? (state as any).addendumSeenThisCombat
    : [];
  const playedPrev: string[] = Array.isArray((state as any).addendumPlayedThisCombat)
    ? (state as any).addendumPlayedThisCombat
    : [];

  const seen = seenPrev.includes(card.id) ? seenPrev : [...seenPrev, card.id];
  const played = playedPrev.includes(card.id) ? playedPrev : [...playedPrev, card.id];

  // Once false, stays false.
  const prevAllPaid = (state as any).paidAllAddendumCostsThisCombat;
  const allPaid = prevAllPaid === false ? false : usedAddendum;

  return {
    ...state,
    addendumSeenThisCombat: seen,
    addendumPlayedThisCombat: played,
    addendumPlayedCountThisCombat: Math.max(0, Number((state as any).addendumPlayedCountThisCombat ?? 0) || 0) + 1,
    paidAllAddendumCostsThisCombat: allPaid,
  } as GameState;
}


type HandLoc = { loc: 'main_physical' | 'main_magic' | 'extra'; index?: number };

function findInHand(state: GameState, cardId: string): { card: Card; where: HandLoc } | null {
  const m = state.hand.main;
  if (m.physical && m.physical.id === cardId) return { card: m.physical, where: { loc: 'main_physical' } };
  if (m.magic && m.magic.id === cardId) return { card: m.magic, where: { loc: 'main_magic' } };
  const idx = state.hand.extra.findIndex((c) => c.id === cardId);
  if (idx >= 0) return { card: state.hand.extra[idx], where: { loc: 'extra', index: idx } };
  return null;
}

function removeFromHand(state: GameState, where: HandLoc): GameState {
  if (where.loc === 'main_physical') {
    return { ...state, hand: { ...state.hand, main: { ...state.hand.main, physical: null } } };
  }
  if (where.loc === 'main_magic') {
    return { ...state, hand: { ...state.hand, main: { ...state.hand.main, magic: null } } };
  }
  const idx = where.index ?? -1;
  if (idx < 0) return state;
  const extra = [...state.hand.extra];
  extra.splice(idx, 1);
  return { ...state, hand: { ...state.hand, extra } };
}

function addExtraToHand(state: GameState, card: Card): GameState {
  // obey hand cap = 5
  // If the hand is already full, some relics (e.g. Bottomless Pouch) grant HP instead of drawing.
  if (handCount(state) >= 5) {
    const mods = (state as any).relicMods as RelicCombatMods | undefined;
    const gainHp = Math.max(0, Number(mods?.fullHandGainHpOnDraw ?? 0) || 0);
    if (gainHp > 0) {
      const healResult = applyPlayerHealingWithInvertedHeart(state, gainHp);
      const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
      let next = {
        ...healResult.state,
        log: [
          ...(overflowLog ? [overflowLog] : []),
          `Relic: hand is full, gain +${gainHp} HP instead of drawing.`,
          ...state.log,
        ],
      } as GameState;
      next = pushRelicTriggerAmount(next, 'fullHandGainHpOnDraw', gainHp);
      return next;
    }
    return state;
  }
  const next = { ...state, hand: { ...state.hand, extra: [...state.hand.extra, card] } } as GameState;
  return noteAddendumSeen(next, card);
}

function setMainSlot(state: GameState, kind: 'physical' | 'magic', card: Card | null): GameState {
  const next = { ...state, hand: { ...state.hand, main: { ...state.hand.main, [kind]: card } as any } } as GameState;
  return card ? noteAddendumSeen(next, card) : next;
}

export type GameAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: { deck: Card[]; enemyDeck: Card[]; guiltTemplate?: Card | null } }
  | { type: 'LOAD_ERROR'; error: string }
  | { type: 'SET_PLAYER_DECK'; deck: Card[] }
  | { type: 'START_COMBAT'; enemyId?: string }
  | { type: 'PLAY_CARD'; cardId: string; addendumPaySp?: number; addendumPayHp?: number; xPaySp?: number; xPayHp?: number; skipDiscardPrereq?: boolean }
  | { type: 'PREPARE_DISCARD_PREREQ_PLAY'; cardId: string; addendumPaySp?: number; addendumPayHp?: number; xPaySp?: number; xPayHp?: number }
  | { type: 'DISCARD_FROM_HAND'; cardId: string }
  | { type: 'CONFIRM_PEEK_ORDER'; orderedIds: string[] }
  | { type: 'CONFIRM_CHOICE'; optionIndex: number }
  | { type: 'CONFIRM_X'; value: number }
  | { type: 'CONFIRM_TUTOR_PICK'; cardId: string }
  | { type: 'CONFIRM_TOP_PICK'; cardId: string }
  | { type: 'END_PLAYER_TURN' }
  | { type: 'SKIP_PLAYER_TURN' }
  | { type: 'RECAST' }
  | { type: 'RESHUFFLE_SELECTED'; cardIds: string[] }
  | { type: 'RESOLVE_ENEMY_TURN' }
  | { type: 'ACK_ENEMY_CARD' }
  | { type: 'SET_REWARD_CHOICES'; choices: Card[] }
  | { type: 'SET_RELIC_MODS'; mods: RelicCombatMods }
  | { type: 'BACK_TO_SETUP' }
  | { type: 'RESTART' };
  

export function initialState(): GameState {
  const enemyId = 'cultist';
  const enemyDef = getEnemyDef(enemyId);
  return {
    phase: 'loading',
    turn: 0,
    playerPlaysThisTurn: 0,
    playerBlackCardPlaysThisTurn: 0,
    nextAttackBonus: 0,
    relicNextAttackBonus: 0,
    nextAttackIgnoreArmor: 0,
    nextCardCost0: 0,
    utilityCostReductionThisTurn: 0,
    nextMagicCardCostDoubleSP: 0,
    playerWeakenedTurns: 0,
    punitiveMeasureTurns: 0,
    unauthorizedPassageLocks: [],
    enemyDamageMultTurns: 0,
    enemyDamageMult: 1,
    playerDamageMultThisTurn: 1,
    playerDamageMultNextTurn: 1,
    playerIncomingDamageMultNextTurn: 1,
    playerMarkedByBlackhand: false,
    playerMarkedTurn: null,
    blackhandShadowstepTurn: null,
    blackhandPressurePointTurn: null,
    enemyActionsRemainingThisTurn: 1,
    enemyBonusActionsNextTurn: 0,
    nextReflectGainSp: 0,
    nextReflectTriggerTwice: 0,
    playerTurnDamageToEnemy: 0,
    lastPlayerTurnDamageToEnemy: 0,
    lastPlayerTurnBlackCardPlays: 0,
    relicMods: {
      startArmor: 0,
      startHp: 0,
      startSp: 0,
      startNextCardCost0: 0,
      startReflectPct: 0,
      startNegateNextDamage: 0,
      turnStartAttackBonus: 0,
      turnStartArmor: 0,
      firstTurnExtraDraw: 0,
    },
    tempMaxHpBonus: 0,
    healPerDamageDealtThisTurn: 0,
    playerAttackedThisTurn: false,
    darkApostleCursedFateActive: false,
    activeCompound: null,
    chymistOpeningCompoundPlayed: false,
    chymistForceNextCompound: false,
    mercenaryPrecision: false,
    mercenaryAmbush: false,
    lampKeeperRoomLight: undefined,
    lampKeeperDisposition: null,
    lampKeeperRaisedLightThisRound: false,
    lampKeeperLoweredLightThisRound: false,
    custodianForfeitureTurns: 0,
    custodianExiledAttackCount: 0,
    custodianExiledDefenseCount: 0,
    custodianExiledUtilityCount: 0,
    custodianTrackedExiledCards: [],
    player: { name: 'Player', hp: 100, sp: 100, armor: 0, stunned: 0, reflectPct: 0, negateNextDamage: 0 },
    enemyId,
    enemy: enemyFighterFromDef(enemyDef),
    pool: [],
    deck: [],
    discard: [],
    exhaust: [],
    physicalDeck: [],
    magicDeck: [],
    physicalDiscard: [],
    magicDiscard: [],
    physicalEscalation: 0,
    magicEscalation: 0,
    magicChainStreak: 0,
    enemyLibrary: [],
    enemyDeck: [],
    guiltTemplate: null,
    enemyForcedQueue: [],
    enemyOngoingVenom: createInitialEnemyOngoingVenomState(),
    hand: { main: { physical: null, magic: null }, extra: [] },
    enemyIntent: undefined,
    enemyIntentPreview: undefined,
    enemyAwaitingAck: false,
    canPlay: false,
    autoFocusPending: false,
    pendingDiscard: null,
    pendingPeek: null,
    pendingTopPick: null,
    pendingChoice: null,
    pendingEnemyResolution: null,
    pendingTutor: null,
    pendingX: null,
    rewardChoices: [],
    delayed: [],
    pendingPrereqPlay: null,
    log: [],
    uiEvents: [],
    error: undefined,

    // --- Relic mechanics flags ---
    // Whether the player has taken HP damage this combat (used by Untarnished Candle).
    playerTookDamage: false,
    // Whether the player has taken any enemy damage this combat, including Armor loss.
    playerTookEnemyDamageThisCombat: false,
    // Whether the enemy was defeated by exact lethal damage (no overkill). Used by relics like Zero Fang (Ivory Tooth).
    enemyKilledExactlyAtZero: false,
    // Snapshot of HP/SP at the instant combat ends (before end-of-combat relic restores).
    endCombatHp: undefined,
    endCombatSp: undefined,
    addendumPlayedCountThisCombat: 0,
    // Legacy: whether the player has used Addendum at least once this combat.
    // (Kept for backwards compatibility.)
    paidAddendumCostsThisCombat: false,
    // Red Ledger Seal (Sealed Receipt): track which Addendum cards entered hand and were played.
    addendumSeenThisCombat: [],
    addendumPlayedThisCombat: [],
    // Red Ledger Seal (Sealed Receipt): becomes false if ANY played Addendum card was played without Addendum.
    paidAllAddendumCostsThisCombat: true,
    // Whether the player has already played a card with Exile this combat (used by First Exile Seal).
    firstExileCardPlayed: false,
    // Number of cards drawn in the current player turn (used by Dealing Die).
    drawnThisTurn: 0,
    // Used during combat setup so opening-hand draws do not count as in-turn draws.
    suppressDrawTrackingThisTurn: false,
    // Damage reduction applied to incoming damage if the deck size condition is met (used by Ballast Shield).
    damageReductionIfLargeDeck: 0,
    // Delay reduction applied to delayed effects if the deck size condition is met (used by Fine Sand Hourglass).
    reduceDelayForLargeDeck: 0,
    reflectOverflowBurstStacks: 0,
    discardPlayThisTurn: 0,
    monsignorDecree: null,
    monsignorSpSpentThisTurn: 0,
    monsignorPlayedCardTypesThisTurn: [],
    playerMagicChainOverloadSpThisTurn: 0,
    playerFocusedThisTurn: false,
  } as GameState;
}


let combatUiEventSeq = 0;

function nextCombatUiEventId(prefix: string): string {
  combatUiEventSeq += 1;
  return `${prefix}-${combatUiEventSeq}`;
}

function pushUiEvent(state: GameState, event: any): GameState {
  const fullEvent: CombatUiEvent = { ...event, id: nextCombatUiEventId(event.kind) } as CombatUiEvent;
  return {
    ...state,
    uiEvents: [...(state.uiEvents || []), fullEvent],
  };
}

function pushStatusPulse(state: GameState, key: CombatUiStatusPulseKey): GameState {
  return pushUiEvent(state, { kind: 'statusPulse', key });
}

function pushRelicTrigger(
  state: GameState,
  trigger: RelicFeedbackTriggerKey,
  text?: string
): GameState {
  const detailed = state.relicMods?.feedbackRelicEntriesByTrigger?.[trigger];
  if (Array.isArray(detailed) && detailed.length > 0) {
    let next = state;
    for (const entry of detailed) {
      const entryText = entry.text ?? text ?? (typeof entry.amount === 'number' ? formatRelicFeedbackText(trigger, entry.amount) : undefined);
      next = pushUiEvent(next, { kind: 'relicTriggered', relicId: entry.relicId, text: entryText });
    }
    return next;
  }

  const raw = state.relicMods?.feedbackRelicIdByTrigger?.[trigger];
  if (!raw) return state;

  const relicIds = Array.isArray(raw) ? raw : [raw];
  let next = state;
  for (const relicId of relicIds) {
    next = pushUiEvent(next, { kind: 'relicTriggered', relicId, text });
  }
  return next;
}

function pushRelicTriggerAmount(
  state: GameState,
  trigger: RelicFeedbackTriggerKey,
  amount?: number,
  options?: { multiplier?: number; distribute?: 'exact' | 'sequential' }
): GameState {
  const detailed = state.relicMods?.feedbackRelicEntriesByTrigger?.[trigger];
  if (Array.isArray(detailed) && detailed.length > 0) {
    const multiplier = Number(options?.multiplier ?? 1) || 1;
    const distribute = options?.distribute ?? 'exact';
    const hasKnownTotal = typeof amount === 'number' && Number.isFinite(amount);
    let remaining = hasKnownTotal ? Math.max(0, Number(amount) || 0) : 0;
    let next = state;

    for (const entry of detailed as RelicFeedbackEntry[]) {
      let entryAmount: number | undefined = undefined;
      if (typeof entry.amount === 'number') {
        const rawEntryAmount = Math.max(0, entry.amount * multiplier);
        if (distribute === 'sequential' || hasKnownTotal) {
          const alloc = Math.min(remaining, rawEntryAmount);
          if (hasKnownTotal) {
            remaining = Math.max(0, remaining - alloc);
          }
          entryAmount = alloc;
        } else {
          entryAmount = rawEntryAmount;
        }
      } else if (typeof amount === 'number') {
        entryAmount = amount;
      }

      const entryText = entry.text ?? formatRelicFeedbackText(trigger, entryAmount);
      if (!entryText) continue;
      if (typeof entryAmount === 'number' && entryAmount <= 0) continue;
      next = pushUiEvent(next, { kind: 'relicTriggered', relicId: entry.relicId, text: entryText });
    }
    return next;
  }

  return pushRelicTrigger(state, trigger, formatRelicFeedbackText(trigger, amount));
}

export function reducer(state: GameState, action: GameAction): GameState {
  state = { ...state, uiEvents: [] };
  // Auto-resolve discard requirements when the hand is empty.
  // This prevents soft-locks when an effect asks you to discard but you have no cards.
  if (
    state.phase === 'player' &&
    state.pendingDiscard &&
    state.pendingDiscard.remaining > 0 &&
    handCount(state) === 0
  ) {
    const chain = !!state.pendingDiscard.chain;
    const finisher = !!state.pendingDiscard.finisher;

    let next: GameState = {
      ...state,
      pendingDiscard: null,
      canPlay: false,
      autoFocusPending: false,
      log: ['No cards to discard. Discard requirement canceled.', ...state.log],
    };

    return finalizePlayerAction(next, { chain, finisher, sourceKind: state.pendingDiscard?.sourceKind });
  }

  switch (action.type) {
    case 'LOAD_START':
      return { ...initialState(), phase: 'loading' };

    case 'LOAD_SUCCESS':
      return {
        ...state,
        phase: 'setup',
        // Keep pool unshuffled until combat starts so selection changes are predictable.
        pool: [...action.payload.deck],
        enemyLibrary: [...action.payload.enemyDeck],
        enemyDeck: [...action.payload.enemyDeck],
        guiltTemplate: action.payload.guiltTemplate ?? state.guiltTemplate ?? null,
        enemyForcedQueue: [],
        enemyAiCurrentTurnActions: [],
        enemyAiLastTurnActions: [],
        enemyAiCombatHistory: [],
        playerTurnDamageToEnemy: 0,
        lastPlayerTurnDamageToEnemy: 0,
      lastPlayerTurnBlackCardPlays: 0,
        deck: [],
        discard: [],
        exhaust: [],
        physicalDeck: [],
        magicDeck: [],
        physicalDiscard: [],
        magicDiscard: [],
        physicalEscalation: 0,
        magicEscalation: 0,
        magicChainStreak: 0,
        hand: { main: { physical: null, magic: null }, extra: [] },
        canPlay: false,
        autoFocusPending: false,
        playerAttackedThisTurn: false,
        darkApostleCursedFateActive: false,
        playerMarkedByBlackhand: false,
        playerMarkedTurn: null,
        blackhandShadowstepTurn: null,
        blackhandPressurePointTurn: null,
        enemyActionsRemainingThisTurn: 1,
        enemyBonusActionsNextTurn: 0,
        enemyAwaitingAck: false,
        enemyIntentPreview: undefined,
        pendingDiscard: null,
        pendingPeek: null,
        pendingTopPick: null,
        pendingChoice: null,
        pendingEnemyResolution: null,
        pendingTutor: null,
        pendingX: null,
        rewardChoices: [],
        delayed: [],
        pendingPrereqPlay: null,
        log: ['Loaded cards. Choose a deck to start.', ...state.log],
        error: undefined,
      };

    case 'LOAD_ERROR':
      return { ...state, phase: 'loading', error: action.error };

    case 'SET_PLAYER_DECK':
      // Only affects the player deck; enemy deck stays as loaded.
      return { ...state, pool: [...action.deck] };

    case 'SET_RELIC_MODS': {
      const prev = (state as any).relicMods || {};
      return {
        ...state,
        relicMods: {
          startArmor: 0,
          startHp: 0,
          startSp: 0,
          startNextCardCost0: 0,
          startReflectPct: 0,
          startNegateNextDamage: 0,
          turnStartAttackBonus: 0,
          turnStartArmor: 0,
          firstTurnExtraDraw: 0,
          ...prev,
          ...(action.mods || {}),
        },
      };
    }

    case 'START_COMBAT': {
      const shuffledDeck = shuffle(state.pool);

      // Pick an enemy at combat start.
      // For now this is random between the available enemies.
      const forcedRaw = typeof (action as any).enemyId === 'string' ? String((action as any).enemyId) : '';
      const forcedDef = forcedRaw ? findEnemyDef(forcedRaw) : null;
      const enemyDef = forcedDef ?? getEnemyDef(pickRandomEnemyId());
      const enemyId = enemyDef.id;

// Decide which enemy cards to use for this fight.
// Keep the full loaded enemy pool in `enemyLibrary`, and store the per-fight deck in `enemyDeck`.
const enemyLibrary = (state as any).enemyLibrary && (state as any).enemyLibrary.length
  ? (state as any).enemyLibrary
  : state.enemyDeck;

const built = buildEnemyDeckForFight(enemyDef as any, enemyLibrary);
const enemyDeckForThisFight = built.deck;


      const mods = (state as any).relicMods as RelicCombatMods | undefined;

      // Determine deck size and activate any relic effects that depend on it (e.g. Ballast Shield, Fine Sand Hourglass).
      const deckSize = Array.isArray(state.pool) ? state.pool.length : 0;
      const damageReductionActive = mods?.damageReductionIfLargeDeck && deckSize >= 30 ? Number(mods.damageReductionIfLargeDeck) || 0 : 0;
      const delayReductionActive = mods?.reduceDelayForLargeDeck && deckSize >= 30 ? Number(mods.reduceDelayForLargeDeck) || 0 : 0;
      const startArmor = Math.max(0, Math.floor(Number(mods?.startArmor ?? 0) || 0));
      const startHp = Math.floor(Number(mods?.startHp ?? 0) || 0);
      const startSp = Math.floor(Number(mods?.startSp ?? 0) || 0);
      const feedbackStartHp = Math.floor(Number(mods?.feedbackStartHp ?? startHp) || 0);
      const feedbackStartSp = Math.floor(Number(mods?.feedbackStartSp ?? startSp) || 0);
      const startCost0 = Math.max(0, Math.floor(Number(mods?.startNextCardCost0 ?? 0) || 0));
      const startReflectPct = clampPlayerReflectPct(state, mods?.startReflectPct ?? 0);
      const startNegateNextDamage = Math.max(0, Math.floor(Number(mods?.startNegateNextDamage ?? 0) || 0));
      const firstTurnExtraDraw = Math.max(0, Math.floor(Number(mods?.firstTurnExtraDraw ?? 0) || 0));
      // Bonus enemy armor from relic mods (used for tarot mechanics)
      const enemyStartArmor = Math.max(0, Math.floor(Number((mods as any)?.enemyStartArmor ?? 0) || 0));
      const enemyStartHp = Math.floor(Number((mods as any)?.enemyStartHp ?? 0) || 0);
      const playerStarts = Math.random() < 0.5;
      const lampKeeperInitialRoomLight = isLampKeeperEnemy(enemyId)
        ? deriveLampKeeperRoomLightFromTorchProgress((mods as any)?.lampKeeperInitialTorchProgress ?? 1)
        : undefined;
      const lampKeeperDisposition = lampKeeperInitialRoomLight
        ? deriveLampKeeperDispositionFromRoomLight(lampKeeperInitialRoomLight)
        : null;

      const startPlayerBase = {
        ...(state as GameState),
        player: {
          ...state.player,
          name: 'Player',
          hp: state.player?.hp ?? 100,
          sp: state.player?.sp ?? 100,
          armor: 0,
          stunned: 0,
          reflectPct: 0,
          negateNextDamage: 0,
        },
      } as GameState;
      const startHealResult = applyPlayerHealingWithInvertedHeart(startPlayerBase, startHp);
      const startArmorResult = applyPlayerArmorGainWithInvertedHeart(startHealResult.state, startArmor);
      const startPlayer = {
        name: 'Player',
        hp: startArmorResult.state.player.hp,
        sp: clampPlayerSp(startArmorResult.state, startArmorResult.state.player.sp + startSp),
        armor: startArmorResult.state.player.armor,
        stunned: 0,
        reflectPct: clampPlayerReflectPct(startArmorResult.state, startReflectPct),
        negateNextDamage: startNegateNextDamage,
      };
      const startHealOverflowLog = formatInvertedHeartHealOverflowLog(startHealResult);
      const startArmorOverflowLog = formatInvertedHeartArmorOverflowLog(startArmorResult);

      let next = {
        ...state,
        phase: 'enemy',
        turn: playerStarts ? 0 : 1,
        playerPlaysThisTurn: 0,
    playerBlackCardPlaysThisTurn: 0,
        nextAttackBonus: 0,
        relicNextAttackBonus: 0,
        nextCardCost0: startCost0 > 0 ? 1 : 0,
        utilityCostReductionThisTurn: 0,
        nextMagicCardCostDoubleSP: 0,
        playerWeakenedTurns: 0,
        punitiveMeasureTurns: 0,
        unauthorizedPassageLocks: [],
        enemyDamageMultTurns: 0,
        enemyDamageMult: 1,
        playerDamageMultThisTurn: 1,
        playerDamageMultNextTurn: 1,
        playerIncomingDamageMultNextTurn: 1,
        playerMarkedByBlackhand: false,
        playerMarkedTurn: null,
        blackhandShadowstepTurn: null,
        blackhandPressurePointTurn: null,
        enemyActionsRemainingThisTurn: 1,
        enemyBonusActionsNextTurn: 0,
        nextReflectGainSp: 0,
        nextReflectTriggerTwice: 0,
        playerAttackedThisTurn: false,
        darkApostleCursedFateActive: false,
        activeCompound: null,
        chymistOpeningCompoundPlayed: false,
        chymistForceNextCompound: false,
        enemyAiCurrentTurnActions: [],
        enemyAiLastTurnActions: [],
        enemyAiCombatHistory: [],
        mercenaryPrecision: false,
        mercenaryAmbush: false,
        lampKeeperRoomLight: lampKeeperInitialRoomLight,
        lampKeeperDisposition,
        lampKeeperRaisedLightThisRound: false,
        lampKeeperLoweredLightThisRound: false,
        custodianForfeitureTurns: 0,
        custodianExiledAttackCount: 0,
        custodianExiledDefenseCount: 0,
        custodianExiledUtilityCount: 0,
        custodianTrackedExiledCards: [],
        // Clear any previous in-combat Max HP bonuses.
        tempMaxHpBonus: 0,
        // Reset flags for relic mechanics at the start of combat.
        playerTookDamage: false,
        playerTookEnemyDamageThisCombat: false,
        enemyKilledExactlyAtZero: false,
        endCombatHp: undefined,
        endCombatSp: undefined,
        addendumPlayedCountThisCombat: 0,
        paidAddendumCostsThisCombat: false,
        addendumSeenThisCombat: [],
        addendumPlayedThisCombat: [],
        paidAllAddendumCostsThisCombat: true,
        firstExileCardPlayed: false,
        drawnThisTurn: 0,
        suppressDrawTrackingThisTurn: true,
        damageReductionIfLargeDeck: damageReductionActive,
        reduceDelayForLargeDeck: delayReductionActive,
        reflectOverflowBurstStacks: 0,
        discardPlayThisTurn: 0,
        monsignorDecree: null,
        monsignorSpSpentThisTurn: 0,
        monsignorPlayedCardTypesThisTurn: [],
        // Start from current persisted stats (exploration) + start-of-combat mods.
        player: startPlayer,
        enemyId,
        enemyMaxHpBase: Math.max(1, Math.floor(Number(enemyDef?.hp ?? 1) || 1)) + enemyStartHp,
        enemy: (() => {
          const base = enemyFighterFromDef(enemyDef);
          return {
            ...base,
            hp: Math.max(1, base.hp + enemyStartHp),
            maxHp: Math.max(1, base.hp + enemyStartHp),
            armor: Math.max(0, base.armor + enemyStartArmor),
          };
        })(),
        playerTurnDamageToEnemy: 0,
        lastPlayerTurnDamageToEnemy: 0,
      lastPlayerTurnBlackCardPlays: 0,
        deck: shuffledDeck,
        discard: [],
        exhaust: [],
        physicalDeck: [],
        magicDeck: [],
        physicalDiscard: [],
        magicDiscard: [],
        physicalEscalation: 0,
        magicEscalation: 0,
        magicChainStreak: 0,
        enemyDeck: shuffle(enemyDeckForThisFight),
        enemyForcedQueue: [],
        enemyOngoingVenom: createInitialEnemyOngoingVenomState(),
        hand: { main: { physical: null, magic: null }, extra: [] },
        enemyIntent: undefined,
        enemyIntentPreview: undefined,
        enemyAwaitingAck: false,
        enemyStartPending: !playerStarts,
        pendingDiscard: null,
        pendingPeek: null,
        pendingTopPick: null,
        pendingChoice: null,
        pendingEnemyResolution: null,
        pendingTutor: null,
        pendingX: null,
        rewardChoices: [],
        delayed: [],
        pendingPrereqPlay: null,
        canPlay: false,
        log: [
          ...(forcedRaw && !forcedDef ? [`⚠ Unknown enemy '${forcedRaw}'. Falling back to ${enemyDef.name}.`] : []),
          `Combat started vs ${enemyDef.name}. Draw 3 random cards.`,
          ...(lampKeeperInitialRoomLight
            ? [
                `Room Light starts at ${lampKeeperRoomLightLabel(lampKeeperInitialRoomLight)}.`,
                `Lamp Keeper disposition: ${lampKeeperDisposition === 'night_watch' ? 'Night Watch' : lampKeeperDisposition === 'tended_flame' ? 'Tended Flame' : 'Open Flame'}.`,
              ]
            : []),
          playerStarts ? 'Player starts.' : 'Enemy starts.',
          ...state.log,
        ],
        error: undefined,
      } as GameState;
      if (isPrimeVesselEnemy(enemyId)) {
        (next as any).primeVesselInitialHp = next.enemy.hp;
        (next as any).primeVesselSynapticRetortUsed = false;
      }
      next = withPlayerPileState(next, { deck: next.deck, discard: next.discard, exhaust: next.exhaust });
      (next as any).lastNonEchoPlayThisTurn = null;



// If you configured an enemy-specific deck and some IDs were not found in the loaded enemy pool,
// surface that in the combat log so it's obvious what to fix.
if ((built as any).missing && (built as any).missing.length) {
  next = {
    ...next,
    log: [
      `⚠ Missing enemy cards for ${enemyDef.name}: ${(built as any).missing.join(', ')}`,
      ...next.log,
    ],
  };
}

      // Apply any first-turn extra draw from relics.
      // Initial draw: 3 random cards from the unified piles (max hand size 5).
      {
        const before = handCount(next);
        next = drawRandomExtraFromPiles(next, 'any', 3);
        const drew = Math.max(0, handCount(next) - before);
        if (drew > 0) {
          next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
        }
      }
      if (firstTurnExtraDraw > 0) {
        const before = handCount(next);
        next = drawRandomExtraFromPiles(next, 'any', firstTurnExtraDraw);
        const drew = Math.max(0, handCount(next) - before);
        if (drew > 0) {
          next = { ...next, log: [`Relic: drew ${drew} extra card(s).`, ...next.log] };
          next = pushRelicTriggerAmount(next, 'firstTurnExtraDraw', drew, { distribute: 'sequential' });
        }
      }

      next = { ...next, suppressDrawTrackingThisTurn: false, drawnThisTurn: 0 };

      if (startArmor > 0) {
        next = { ...next, log: [`Relic: start with +${startArmor} Armor.`, ...next.log] };
        next = pushRelicTriggerAmount(next, 'startArmor', startArmor);
      }
      if (startSp !== 0) {
        next = { ...next, log: [`Combat: start with ${startSp >= 0 ? '+' : ''}${startSp} Sanity (SP).`, ...next.log] };
        if (feedbackStartSp !== 0) {
  next = pushRelicTriggerAmount(next, 'startSp', feedbackStartSp);
}
      }
      if (startHp !== 0) {
        next = { ...next, log: [`Combat: start with ${startHp >= 0 ? '+' : ''}${startHp} Health (HP).`, ...next.log] };
        if (feedbackStartHp !== 0) {
  next = pushRelicTriggerAmount(next, 'startHp', feedbackStartHp);
}
      }
      if (startArmorOverflowLog) {
        next = { ...next, log: [startArmorOverflowLog, ...next.log] };
      }
      if (startHealOverflowLog) {
        next = { ...next, log: [startHealOverflowLog, ...next.log] };
      }
      if (startReflectPct > 0) {
        next = { ...next, log: [`Relic: start with ${startReflectPct}% Reflect.`, ...next.log] };
        next = pushRelicTriggerAmount(next, 'startReflectPct', startReflectPct);
      }
      if (startNegateNextDamage > 0) {
        next = { ...next, log: [`Relic: negate next ${startNegateNextDamage} incoming hit(s).`, ...next.log] };
        next = pushRelicTriggerAmount(next, 'startNegateNextDamage', startNegateNextDamage);
      }
      if (startCost0 > 0) {
        next = { ...next, log: ['Relic: your next card costs 0.', ...next.log] };
        next = pushRelicTrigger(next, 'startNextCardCost0', formatRelicFeedbackText('startNextCardCost0'));
      }

      if (playerStarts) {
        return startPlayerTurn(next);
      }

      if (mods?.revealEnemyIntentPreview) {
        next = { ...next, enemyIntentPreview: predictEnemyIntentCard(next) };
      }

      return next;
    }


    case 'PREPARE_DISCARD_PREREQ_PLAY': {
      if (state.phase !== 'player' && !(state.phase === 'enemy' && (state.pendingDiscard as any)?.resumeEnemyTurn)) return state;
      if (state.player.hp <= 0 || state.enemy.hp <= 0) return state;
      if ((state.player?.stunned ?? 0) > 0) return state;
      if (!state.canPlay) return state;
      if (state.pendingDiscard && state.pendingDiscard.remaining > 0) return state;

      const skipDiscardPrereq = !!(action as any).skipDiscardPrereq;

      const found = findInHand(state, action.cardId);
      if (!found) return state;
      const { card } = found;
      const discardNeeded = (Array.isArray(card.effects) ? card.effects : [])
        .filter((e: any) => e?.type === 'discard')
        .reduce((a: number, e: any) => a + (Number(e?.amount) || 0), 0);
      if (discardNeeded <= 0) {
        return reducer(state, {
          type: 'PLAY_CARD',
          cardId: action.cardId,
          addendumPaySp: (action as any).addendumPaySp,
          addendumPayHp: (action as any).addendumPayHp,
          xPaySp: (action as any).xPaySp,
          xPayHp: (action as any).xPayHp,
        } as any);
      }

      const availableOtherCards = Math.max(0, handCount(state) - 1);
      if (availableOtherCards < discardNeeded) {
        return {
          ...state,
          log: [`Not enough cards in hand to discard ${discardNeeded} for ${card.title}.`, ...state.log],
        };
      }

      return {
        ...state,
        canPlay: false,
        pendingPrereqPlay: {
          cardId: action.cardId,
          addendumPaySp: (action as any).addendumPaySp,
          addendumPayHp: (action as any).addendumPayHp,
          xPaySp: (action as any).xPaySp,
          xPayHp: (action as any).xPayHp,
        },
        pendingDiscard: { remaining: discardNeeded, chain: false, finisher: false },
        log: [`Choose ${discardNeeded} card(s) to discard.`, ...state.log],
      };
    }

    case 'PLAY_CARD': {
      if (state.phase !== 'player') return state;
      if (state.player.hp <= 0 || state.enemy.hp <= 0) return state;
      if ((state.player?.stunned ?? 0) > 0) return state;

      const skipDiscardPrereq = !!(action as any).skipDiscardPrereq;
      const replayingPrereqPlay = skipDiscardPrereq && state.pendingPrereqPlay?.cardId === action.cardId;
      if (!state.canPlay && !replayingPrereqPlay) return state;

      const found = findInHand(state, action.cardId);
      if (!found) return state;
      const { card, where } = found;

      const unauthorizedTurns = getUnauthorizedPassageTurnsForCard(state, card.id);
      if (unauthorizedTurns > 0) {
        return { ...state, log: [`Unauthorized Passage blocks ${card.title} for ${unauthorizedTurns} more turn(s).`, ...state.log] };
      }

      const spCostIsX = !!(card as any).spCostIsX;
      const hpCostIsX = !!(card as any).hpCostIsX;

      // Temporary modifier: "The next card you play has cost 0." (Behold the Path)
      const cost0Count = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0);
      const cost0Active = cost0Count > 0;

            // Enemy debuff: "The player's next magic card costs double SP." (consumed on the next successful Magic play)
      const magicDoubleCount = Math.max(0, Number((state as any).nextMagicCardCostDoubleSP ?? 0) || 0);
      const consumeMagicDoubleNow = card.kind === 'magic' && magicDoubleCount > 0;
      const spMult = !cost0Active && consumeMagicDoubleNow ? 2 : 1;

      // Magic chain penalty:
      // first Magic in a streak pays +0, second +1, third +2, etc.
      const magicChainStreak = Math.max(0, Number((state as any).magicChainStreak ?? 0) || 0);
      const magicChainSurcharge =
        !cost0Active && card.kind === 'magic'
          ? magicChainStreak
          : 0;

      const chosenXSp = spCostIsX && !cost0Active
        ? Math.max(0, Math.floor(Number((action as any).xPaySp ?? 0) || 0))
        : 0;

      const chosenXHp = hpCostIsX && !cost0Active
        ? Math.max(0, Math.floor(Number((action as any).xPayHp ?? 0) || 0))
        : 0;

      // X-cost cards must choose a positive X unless a "next card costs 0" effect is active.
      if (spCostIsX && !cost0Active && chosenXSp <= 0) {
        return { ...state, log: [`Choose X to play ${card.title}.`, ...state.log] };
      }

      if (hpCostIsX && !cost0Active && chosenXHp <= 0) {
        return { ...state, log: [`Choose X to play ${card.title}.`, ...state.log] };
      }

      const isUtilityCard = String((card as any).tag ?? '').trim().toLowerCase() === 'utility' || /\butility\b/i.test(String(card.subtitle ?? ''));
      const utilityCostReductionThisTurn = !cost0Active && isUtilityCard
        ? Math.max(0, Math.floor(Number((state as any).utilityCostReductionThisTurn ?? 0) || 0))
        : 0;

      // For X-HP cards, card.cost.hp holds only escalation (+N on reshuffles).
      const printedHpCost = hpCostIsX
        ? (chosenXHp + (card.cost?.hp ?? 0))
        : (card.cost?.hp ?? 0);
      const reducedPrintedHpCost = utilityCostReductionThisTurn > 0
        ? Math.max(0, printedHpCost - utilityCostReductionThisTurn)
        : printedHpCost;
      const utilityHpReductionApplied = Math.max(0, printedHpCost - reducedPrintedHpCost);
      const baseHpCost = cost0Active ? 0 : reducedPrintedHpCost;
      // For X-cost cards, card.cost.sp holds only escalation (+N on reshuffles).
      const printedSpCost = spCostIsX
        ? (chosenXSp + (card.cost?.sp ?? 0))
        : (card.cost?.sp ?? 0);
      const reducedPrintedSpCost = utilityCostReductionThisTurn > 0
        ? Math.max(0, printedSpCost - utilityCostReductionThisTurn)
        : printedSpCost;
      const utilitySpReductionApplied = Math.max(0, printedSpCost - reducedPrintedSpCost);
      let baseSpCost = cost0Active ? 0 : reducedPrintedSpCost * spMult;
      let chainCostReductionApplied = 0;
      let delayCostReductionApplied = 0;
      let addendumCostReductionApplied = 0;

      // Determine delay-related properties early so that cost reductions can use them.
      const baseDelayedGroups = Array.isArray((card as any).delayed) ? (card as any).delayed : [];
      const legacyDelayTurns = Math.max(0, Number((card as any).delayTurns || 0) || 0);
      const costDelayTurns = Math.max(0, Number((card as any).costDelayTurns || 0) || 0);

      // Apply relic-based SP cost reductions before any checks.
      {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        if (mods) {
          // Determine if the card should receive the Chain cost reduction. A card is
          // chain-eligible if it explicitly has the Chain keyword or, when the
          // Exile→Chain relic is active, if it has the Exile keyword. This ensures
          // Light Link reduces cost only for appropriate cards.
          const hasChainKeyword = hasKeyword(card, 'Chain');
          const hasExileKeyword = isExileCard(card);
          let chainEligible = false;
          if (mods.exileCardsGetChain && hasExileKeyword) {
            chainEligible = true;
          } else {
            chainEligible = hasChainKeyword;
          }
          // Compute the printed SP cost of the card. For X-cost cards this is (X + escalation).
          let effectivePrintedSpCost = Math.max(0, Number(reducedPrintedSpCost ?? 0) || 0);
          // Apply Light Link: subtract exactly `chainCostReduction` from the printed cost.
          if (chainEligible && mods.chainCostReduction && mods.chainCostReduction > 0) {
            const reduce = Number(mods.chainCostReduction) || 0;
            const reducedCost = Math.max(0, effectivePrintedSpCost - reduce);
            chainCostReductionApplied = Math.max(0, effectivePrintedSpCost - reducedCost);
            effectivePrintedSpCost = reducedCost;
          }
          // Compute base SP cost after applying Magic double/debuff multiplier. If nextCardCost0 is active, SP cost is 0.
          let newBaseSpCost = cost0Active ? 0 : (effectivePrintedSpCost * spMult);
          // Apply Lightened Buckle: reduce SP cost for Delay cards after scaling. Delay applies if delayTurns > 0 or delayed segments exist.
          const hasDelay = legacyDelayTurns > 0 || (Array.isArray(baseDelayedGroups) && baseDelayedGroups.length > 0) || costDelayTurns > 0;
          if (mods.delayCostReduction && mods.delayCostReduction > 0 && hasDelay) {
            const reduce = Number(mods.delayCostReduction) || 0;
            const reducedCost = Math.max(0, newBaseSpCost - reduce);
            delayCostReductionApplied = Math.max(0, newBaseSpCost - reducedCost);
            newBaseSpCost = reducedCost;
          }
                    // Finally assign the computed SP cost.
          baseSpCost = newBaseSpCost;
        }
      }

      // Add the chain surcharge after the normal SP-cost calculations.
      if (magicChainSurcharge > 0) {
        baseSpCost += magicChainSurcharge;
      }

      const blackhandPressurePointActive = isBlackhandPressurePointActiveForCurrentTurn(state);
      const blackhandPressurePointMarkedMult =
        blackhandPressurePointActive && magicChainSurcharge > 0
          ? (isPlayerMarkedByBlackhand(state) ? 2 : 1)
          : 0;
      const pressurePointExtraHpCost = magicChainSurcharge > 0 ? magicChainSurcharge * blackhandPressurePointMarkedMult : 0;
      const pressurePointExtraSpCost = magicChainSurcharge > 0 ? magicChainSurcharge * Math.max(0, blackhandPressurePointMarkedMult - 1) : 0;

      const isAttackTagged = String((card as any).tag ?? '').trim().toLowerCase() === 'attack';
      const pendingNextAttackBonus = Math.max(0, Number((state as any).nextAttackBonus) || 0);
      const pendingRelicNextAttackBonus = Math.max(0, Number((state as any).relicNextAttackBonus ?? 0) || 0);
      const pendingNextAttackIgnoreArmor = Math.max(0, Number((state as any).nextAttackIgnoreArmor ?? 0) || 0);
      const consumeNextAttackIgnoreArmorNow = pendingNextAttackIgnoreArmor > 0 && isAttackTagged;

      // "Next Attack" bonuses must survive non-Attack plays and only be consumed by an actual Attack.
      const consumeNextAttackBonusNow = pendingNextAttackBonus > 0 && isAttackTagged;
      const attackBonusThisPlay = consumeNextAttackBonusNow ? pendingNextAttackBonus : 0;
      const relicAttackBonusThisPlay = consumeNextAttackBonusNow ? pendingRelicNextAttackBonus : 0;
      const retainAfterPlay = !!(card as any).retainAfterPlay;
      // baseDelayedGroups and legacyDelayTurns are defined above for cost calculations

      // Addendum: some cards have an optional "pay extra SP" mode that ADDS to the card's normal effects.
      // - Fixed: "Addendum (20 SP) - ..."
      // - Variable: "Addendum (X SP) - ..." where X is chosen at play time.
      const requestedExtraSp = Math.max(0, Math.floor(Number((action as any).addendumPaySp ?? 0) || 0));
      const requestedExtraHp = Math.max(0, Math.floor(Number((action as any).addendumPayHp ?? 0) || 0));
      const maxExtraSp = Math.max(0, state.player.sp - baseSpCost);
      const maxExtraHp = Math.max(0, state.player.hp - baseHpCost);
      let extraSp = 0;
      let extraHp = 0;
      // For X-cost cards, parse the base description at play time with the chosen X.
      // Match existing Addendum behavior: X effects use the chosen X value (pre-reduction)
      // even if cost modifiers reduce/increase the SP actually paid.
      let playEffects: Effect[] = card.effects;
      let playDelayedGroups: any[] = baseDelayedGroups;
      let playKeywords: string[] = card.keywords;
      if (spCostIsX || hpCostIsX) {
        const xValue = spCostIsX ? chosenXSp : chosenXHp;
        const parsedBase = parseVariantFromDescription({
          title: card.title,
          subtitle: card.subtitle,
          kind: card.kind,
          description: (card.baseDescription ?? card.description ?? ''),
          xValue,
        });
        playEffects = parsedBase.effects;
        playDelayedGroups = parsedBase.delayed ?? [];
        playKeywords = parsedBase.keywords;
      }
      let usedAddendum = false;

      if ((card as any).addendum) {
        const add = (card as any).addendum as { costSp?: number | 'X'; costHp?: number | 'X'; altDescription: string };
        const resource: 'sp' | 'hp' = (add as any).costHp != null ? 'hp' : 'sp';
        const requestedExtra = resource === 'hp' ? requestedExtraHp : requestedExtraSp;
        const maxExtra = resource === 'hp' ? maxExtraHp : maxExtraSp;
        const costToken = (resource === 'hp' ? (add as any).costHp : (add as any).costSp) as (number | 'X' | undefined);

        if (requestedExtra <= 0 || costToken == null) {
          // No Addendum payment requested (or invalid card config)
        } else {


        // Apply relic-based Addendum cost reduction (Muted Inkwell).
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        const reduce = Math.max(0, Number(mods?.addendumCostReduction ?? 0) || 0);

        // `raw` is the chosen Addendum amount (fixed cost or X value) BEFORE reduction.
        // `extraSp` is what the player actually pays AFTER reduction.
        let raw = 0;

        if (costToken === 'X') {
          // If costs are reduced, you can afford an X that's up to (maxExtra + reduce).
          const maxAffordableX = Math.max(0, maxExtra + reduce);
          raw = Math.min(requestedExtra, maxAffordableX);
        } else {
          const fixed = Math.max(0, Number(costToken) || 0);
          if (fixed > 0 && requestedExtra >= fixed) raw = fixed;
        }

        if (raw > 0) {
          const effective = Math.max(0, raw - reduce);
          addendumCostReductionApplied = Math.max(0, raw - effective);
          // Must be able to pay the reduced cost.
          if (effective <= maxExtra) {
            if (resource === 'hp') extraHp = effective;
            else extraSp = effective;

            const parsedAlt = parseVariantFromDescription({
              title: card.title,
              subtitle: card.subtitle,
              kind: card.kind,
              description: add.altDescription,
              // X effects use the chosen X (pre-reduction), not the reduced payment.
              xValue: costToken === 'X' ? raw : undefined,
            });
            playEffects = [...playEffects, ...parsedAlt.effects];
            playDelayedGroups = [...playDelayedGroups, ...(parsedAlt.delayed ?? [])];
            playKeywords = Array.from(new Set([...(playKeywords ?? []), ...(parsedAlt.keywords ?? [])]));
            usedAddendum = true;
          }
        }
        }

      }

      let delayGroups = (Array.isArray(playDelayedGroups) && playDelayedGroups.length > 0)
        ? playDelayedGroups
        : (legacyDelayTurns > 0 ? [{ turns: legacyDelayTurns, effects: playEffects.filter((e: any) => e.type !== 'discard') }] : []);

      // If this card delays its COST, we pay the base HP/SP cost later, but effects are immediate.
      // (Example: Borrowed Dawn)
      const delayCostActive = costDelayTurns > 0 && !cost0Active;
      const immediateHpCost = delayCostActive ? 0 : baseHpCost;
      const immediateSpCost = delayCostActive ? 0 : baseSpCost;

      // Relic: Exile Chain – cards with Exile gain Chain keyword automatically.
      {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        if (mods?.exileCardsGetChain) {
          // Use isExileCard so "implicit Exile" (from description text) also counts.
          // Pass playKeywords in case Addendum altered keywords.
          const hasExile = isExileCard({ ...card, keywords: playKeywords });
          const hasChainKw = hasKeywordList(playKeywords, 'Chain');
          if (hasExile && !hasChainKw) {
            playKeywords = [...playKeywords, 'Chain'];
          }
        }
      }

      // Global rule: all Magic cards behave as if they have the "Chain" keyword.
      // This is applied at play-time only (we don't mutate card data), so the card
      // doesn't need to explicitly list "Chain" in its keyword list.
      if (card.kind === 'magic') {
        const hasChainKw = hasKeywordList(playKeywords, 'Chain');
        if (!hasChainKw) playKeywords = [...playKeywords, 'Chain'];
      }


      // Cost check
      if (state.player.hp < (immediateHpCost + extraHp + pressurePointExtraHpCost) || state.player.sp < (immediateSpCost + extraSp + pressurePointExtraSpCost)) {
        return { ...state, log: [`Not enough resources to play ${card.title}.`, ...state.log] };
      }

      let next: GameState = {
        ...state,
        nextAttackBonus: consumeNextAttackBonusNow ? 0 : state.nextAttackBonus,
        relicNextAttackBonus: consumeNextAttackBonusNow ? 0 : pendingRelicNextAttackBonus,
        nextAttackIgnoreArmor: consumeNextAttackIgnoreArmorNow ? Math.max(0, pendingNextAttackIgnoreArmor - 1) : pendingNextAttackIgnoreArmor,

        nextCardCost0: cost0Active ? Math.max(0, cost0Count - 1) : cost0Count,
        nextMagicCardCostDoubleSP: consumeMagicDoubleNow ? Math.max(0, magicDoubleCount - 1) : magicDoubleCount,
        magicChainStreak: card.kind === 'magic' ? magicChainStreak + 1 : 0,
        hand: retainAfterPlay ? state.hand : removeFromHand(state, where).hand,
        playerPlaysThisTurn: (state.playerPlaysThisTurn || 0) + 1,
        playerBlackCardPlaysThisTurn: (state.playerBlackCardPlaysThisTurn || 0) + (isThresholdVerifierBlackCard(card) ? 1 : 0),
        pendingPrereqPlay: state.pendingPrereqPlay?.cardId === action.cardId ? null : state.pendingPrereqPlay,
        player: {
          ...state.player,
          hp: clampPlayerHp(state, (state.player.hp || 0) - immediateHpCost - extraHp - pressurePointExtraHpCost),
sp: clampPlayerSp(state, (state.player.sp || 0) - immediateSpCost - extraSp - pressurePointExtraSpCost),

        },
                log: [
          `Played ${card.title}${spCostIsX ? ` (X=${chosenXSp})` : hpCostIsX ? ` (X=${chosenXHp})` : ''}${usedAddendum ? ` (Addendum +${extraSp} SP)` : ''}.`,
          ...(cost0Active ? [`Cost reduced to 0 (nextCardCost0).`] : []),
          ...(consumeMagicDoubleNow && !cost0Active ? [`Debuff: Magic card cost x2 SP.`] : []),
          ...(consumeMagicDoubleNow && cost0Active ? [`Debuff: Magic x2 SP consumed.`] : []),
          ...(magicChainSurcharge > 0 ? [`Magic chain surcharge: +${magicChainSurcharge} SP.`] : []),
          ...(pressurePointExtraHpCost > 0 || pressurePointExtraSpCost > 0 ? [`Pressure Point: chain overload adds ${pressurePointExtraHpCost > 0 ? `+${pressurePointExtraHpCost} HP` : ''}${pressurePointExtraHpCost > 0 && pressurePointExtraSpCost > 0 ? ' and ' : ''}${pressurePointExtraSpCost > 0 ? `+${pressurePointExtraSpCost} SP` : ''}.`] : []),
          ...((utilityHpReductionApplied > 0 || utilitySpReductionApplied > 0)
            ? [`Utility discount: ${[
                utilityHpReductionApplied > 0 ? `-${utilityHpReductionApplied} HP` : '',
                utilitySpReductionApplied > 0 ? `-${utilitySpReductionApplied} SP` : '',
              ].filter(Boolean).join(' and ')}.`]
            : []),
          ...(delayCostActive && (baseHpCost > 0 || baseSpCost > 0)
            ? [`Cost delayed (${costDelayTurns}): pay ${baseHpCost > 0 ? `${baseHpCost} HP` : ''}${baseHpCost > 0 && baseSpCost > 0 ? ' + ' : ''}${baseSpCost > 0 ? `${baseSpCost} SP` : ''} at turn start.`]
            : []),
          ...state.log,
        ],
      };

      next = {
        ...next,
        monsignorSpSpentThisTurn: Math.max(0, Number((next as any).monsignorSpSpentThisTurn ?? 0) || 0) + immediateSpCost + extraSp,
        monsignorPlayedCardTypesThisTurn: [
          ...((Array.isArray((state as any).monsignorPlayedCardTypesThisTurn) ? (state as any).monsignorPlayedCardTypesThisTurn : []) as string[]),
          getPlayedCardTypeForDecree(card),
        ],
      } as GameState;

      next = maybeBreakMonsignorDecreeForPlay(next, card);

      const punitiveTurns = Math.max(0, Number((next as any).punitiveMeasureTurns ?? 0) || 0);
      if (punitiveTurns > 0 && isThresholdVerifierBlackCard(card)) {
        const prevHp = Number(next.player.hp || 0);
        const afterHp = clampPlayerHp(next, prevHp - 5);
        const lost = Math.max(0, prevHp - afterHp);
        next = {
          ...next,
          player: { ...next.player, hp: afterHp },
          log: [
            ...(lost > 0 ? [`Punitive Measure: black card penalty, lose ${lost} HP.`] : ['Punitive Measure: black card penalty triggers, but HP is already at minimum.']),
            ...next.log,
          ],
        } as GameState;
      }

      if (chainCostReductionApplied > 0) {
        next = pushRelicTriggerAmount(next, 'chainCostReduction', chainCostReductionApplied);
      }
      if (delayCostReductionApplied > 0) {
        next = pushRelicTriggerAmount(next, 'delayCostReduction', delayCostReductionApplied);
      }
      if (addendumCostReductionApplied > 0) {
        next = pushRelicTriggerAmount(next, 'addendumCostReduction', addendumCostReductionApplied);
      }

      // If base costs are delayed, schedule the payment bundle now.
      if (delayCostActive && (baseHpCost > 0 || baseSpCost > 0)) {
        next = scheduleDelayedCost(next, card, baseHpCost, baseSpCost, costDelayTurns);
      }

      // If SP drops to exactly zero after paying costs, apply Zero Key SP gain.
      next = maybeTriggerZeroKey(state, next);

      // Addendum tracking (Red Ledger Seal / Sealed Receipt).
      next = noteAddendumPlayed(next, card, usedAddendum);

      // Legacy flag: whether ANY Addendum was used this combat (kept for backwards compatibility).
      if (usedAddendum) {
        next = { ...next, paidAddendumCostsThisCombat: true } as GameState;
      }

// Card-specific exceptions (conditional effects/keywords, etc.)
{
  const ex = applyPlayExceptions({ cardTitle: card.title, stateAfterCosts: next, playEffects, playKeywords, delayGroups });
  if (ex) {
    playEffects = ex.playEffects;
    playKeywords = ex.playKeywords;
    delayGroups = ex.delayGroups;
    next = ex.stateAfterCosts;
  }
}


      

// Track the last NON-ECHO card played this turn so "Spell Echo" can replay it.
{
  const isSpellEcho = card.title.trim().toLowerCase() === 'spell echo';
  if (!isSpellEcho) {
    const safeDelayGroups = Array.isArray(delayGroups)
      ? (delayGroups as any[]).map((g: any) => ({
          turns: Math.max(0, Number(g?.turns) || 0),
          effects: Array.isArray(g?.effects) ? (g.effects as Effect[]) : [],
        }))
      : [];
    (next as any).lastNonEchoPlayThisTurn = {
      id: card.id,
      title: card.title,
      kind: card.kind,
      playEffects,
      playKeywords,
      delayGroups: safeDelayGroups,
    };
  }
}
// (First Exile Seal logic moved to discardPlayed; do not modify playKeywords here.)

      // Move to discard (unless Exile). Some cards explicitly say they are not
      // discarded after being played; those stay in hand and can only be removed
      // by an explicit Discard effect.
      if (!retainAfterPlay) {
        // Use the play-time keywords (Addendum can add keywords like Exile), BUT do
        // not persist the implicit Magic→Chain rule back onto the card instance.
        // Otherwise, Magic cards would start displaying a CHAIN tag after being
        // played once and reshuffled.
        const persistKeywords =
          card.kind === 'magic' && !hasKeyword(card, 'Chain')
            ? (playKeywords || []).filter((k) => String(k).trim().toLowerCase() !== 'chain')
            : playKeywords;
        next = discardPlayed(next, { ...card, keywords: persistKeywords }, 'played');
      } else {
        next = { ...next, log: [`${card.title} remains in hand.`, ...next.log] };
      }

      // Apply gameplay effects (except Discard, which requires player choice)
      const discardNeeded = playEffects
        .filter((e: any) => e.type === 'discard')
        .reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
      // Extra draws are resolved in the PLAY_CARD flow (after the standard replacement draw),
      // so we remove them from the immediate effect pass.
      const drawBuckets = playEffects
        .filter((e: any) => e.type === 'draw')
        .reduce(
          (acc: { sameDeck: number; physical: number; magic: number; any: number }, e: any) => {
            const amt = Number(e.amount) || 0;
            const sourceKind = String(e?.sourceKind ?? '').trim().toLowerCase();
            if (sourceKind === 'physical') acc.physical += amt;
            else if (sourceKind === 'magic') acc.magic += amt;
            else if (sourceKind === 'any') acc.any += amt;
            else acc.sameDeck += amt;
            return acc;
          },
          { sameDeck: 0, physical: 0, magic: 0, any: 0 }
        );
      let drawSameDeckNeeded = drawBuckets.sameDeck;
      let drawSameDeckPhysicalNeeded = drawBuckets.physical;
      let drawSameDeckMagicNeeded = drawBuckets.magic;
      let drawSameDeckAnyNeeded = drawBuckets.any;
      let drawRandomNeeded = playEffects
        .filter((e: any) => e.type === 'drawRandom')
        .reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
      let drawRandomPhysicalNeeded = playEffects
        .filter((e: any) => e.type === 'drawRandomPhysical')
        .reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
      let drawRandomMagicNeeded = playEffects
        .filter((e: any) => e.type === 'drawRandomMagic')
        .reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
      let anyDrawNeeded = drawSameDeckNeeded + drawSameDeckPhysicalNeeded + drawSameDeckMagicNeeded + drawSameDeckAnyNeeded + drawRandomNeeded + drawRandomPhysicalNeeded + drawRandomMagicNeeded;

      // If a card both Discards and Draws, respect the written order for prompt timing.
      // Example: "Discard 2 cards. Draw 3 cards." should prompt discard BEFORE drawing.
      const descForOrder = (() => {
      const base = String((card as any).baseDescription ?? card.description ?? '');
      const add = (usedAddendum && (card as any).addendum)
        ? String((((card as any).addendum as any)?.altDescription) ?? '')
        : '';
      return (base + ' ' + add).trim();
    })();
      const iDiscard = descForOrder.toLowerCase().indexOf('discard');
      const iDraw = descForOrder.toLowerCase().indexOf('draw');
      const drawAfterDiscard =
        discardNeeded > 0 && anyDrawNeeded > 0 && iDiscard !== -1 && iDraw !== -1 && iDiscard < iDraw;


      const chooseOne = playEffects.find((e: any) => e.type === 'chooseOne') as any;
      const chooseOneOptions = chooseOne && Array.isArray(chooseOne.options) ? chooseOne.options : null;

      const xTradeNeeded = playEffects.find((e: any) => e.type === 'xTrade') as any;

const tutorEffect = playEffects.find((e: any) => e.type === 'tutor') as any;
const tutorNeeded = tutorEffect?.kind as ('physical'|'magic'|'any'|undefined);
const tutorZone = (tutorEffect?.zone === 'discard' ? 'discard' : 'deck') as ('deck'|'discard');

const peekPhysicalCount = playEffects
  .filter((e: any) => e.type === 'peekReorderPhysical')
  .reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);

const peekMagicCount = playEffects
  .filter((e: any) => e.type === 'peekReorderMagic')
  .reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);

const peekDeckCount = playEffects
  .filter((e: any) => e.type === 'peekReorderDeck')
  .reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);

const topPickPhysicalCount = playEffects
  .filter((e: any) => e.type === 'peekPickOneDiscardRestPhysical')
  .reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);

const topPickMagicCount = playEffects
  .filter((e: any) => e.type === 'peekPickOneDiscardRestMagic')
  .reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);

const topPickAnyCount = playEffects
  .filter((e: any) => e.type === 'peekPickOneDiscardRestAny')
  .reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);


// If a card both reorders the top of a deck and draws, respect written order: peek/reorder BEFORE draw.
const iPeek = (() => {
  const low = descForOrder.toLowerCase();
  let i = low.indexOf('look at the top');
  if (i === -1) i = low.indexOf('look at');
  if (i === -1) i = low.indexOf('reorder');
  return i;
})();
const drawAfterPeek =
  !drawAfterDiscard &&
  (peekPhysicalCount > 0 || peekMagicCount > 0 || peekDeckCount > 0) &&
  anyDrawNeeded > 0 &&
  iPeek !== -1 &&
  iDraw !== -1 &&
  iPeek < iDraw;

      // Apply immediate gameplay effects (except Discard and Draw, which are handled in the PLAY_CARD flow).
      let cardImmediate = (discardNeeded > 0 || anyDrawNeeded > 0)
        ? {
            ...card,
            effects: playEffects.filter(
              (e: any) =>
                e.type !== 'discard' &&
                e.type !== 'draw' &&
                e.type !== 'drawRandom' &&
                e.type !== 'drawRandomPhysical' &&
                e.type !== 'drawRandomMagic' &&
                e.type !== 'peekReorderPhysical' &&
                e.type !== 'peekReorderMagic' &&
                e.type !== 'peekReorderDeck' &&
                e.type !== 'peekPickOneDiscardRestPhysical' &&
                e.type !== 'peekPickOneDiscardRestMagic' &&
                e.type !== 'peekPickOneDiscardRestAny' &&
                e.type !== 'chooseOne' &&
                e.type !== 'tutor' &&
                e.type !== 'xTrade'
            ),
          }
        : { ...card, effects: playEffects.filter((e: any) => e.type !== 'chooseOne' && e.type !== 'xTrade' && e.type !== 'peekPickOneDiscardRestPhysical' && e.type !== 'peekPickOneDiscardRestMagic' && e.type !== 'peekPickOneDiscardRestAny') };
      // Apply any pending next-attack bonus to this Attack play.
      if (consumeNextAttackBonusNow) {
        if (attackBonusThisPlay > 0) {
          const boosted = applyDamageBonusToCardAndDelayGroups(cardImmediate, delayGroups, attackBonusThisPlay, {
            includeIgnoreArmor: true,
            onlyFirst: true,
          });
          cardImmediate = boosted.cardImmediate;
          delayGroups = boosted.delayGroups;
          if (boosted.applied) {
            next = { ...next, log: [`Next Attack bonus applied (+${attackBonusThisPlay}).`, ...next.log] };
            if (relicAttackBonusThisPlay > 0) {
              next = pushRelicTriggerAmount(next, 'turnStartAttackBonus', relicAttackBonusThisPlay);
            }
          } else {
            next = { ...next, log: ['Next Attack bonus expired.', ...next.log] };
          }
        } else {
          next = { ...next, log: ['Next Attack bonus expired.', ...next.log] };
        }
      }


      // If the player has a pending 'next Attack ignores Armor' buff, apply it to this Attack play.
      if (consumeNextAttackIgnoreArmorNow) {
        const convertDamageToIgnoreArmor = (e: any) => (e?.type === 'damage' ? ({ type: 'damageIgnoreArmor', amount: Number(e.amount) || 0 } as any) : e);
        cardImmediate = { ...cardImmediate, effects: (cardImmediate.effects || []).map(convertDamageToIgnoreArmor) as any };
        if (Array.isArray(delayGroups)) {
          for (const g of delayGroups as any) {
            if (Array.isArray(g?.effects)) {
              g.effects = g.effects.map(convertDamageToIgnoreArmor);
            }
          }
        }
        next = { ...next, log: ['Next Attack ignores Armor.', ...next.log] };
      }
      // Relic: Apply additional attack bonuses based on player SP or Armor (Threshold Coin, Bare Blade).
      {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        let flatAttackBonus = 0;
        let lowSpBonus = 0;
        let armorZeroBonus = 0;
        if (mods) {
          flatAttackBonus = Math.max(0, Number(mods.attackBonusFlat) || 0);
          // Bonus when player SP is at or below the threshold
          if (mods.attackBonusWhenLowSp && mods.lowSpThreshold != null) {
            if (next.player.sp <= Number(mods.lowSpThreshold)) {
              lowSpBonus = Math.max(0, Number(mods.attackBonusWhenLowSp) || 0);
            }
          }
          // Bonus when player armor is zero
          if (mods.attackBonusWhenArmorZero) {
            if ((next.player.armor || 0) <= 0) {
              armorZeroBonus = Math.max(0, Number(mods.attackBonusWhenArmorZero) || 0);
            }
          }
        }
        if (flatAttackBonus > 0 && isAttackTagged) {
          const boosted = applyDamageBonusToCardAndDelayGroups(cardImmediate, delayGroups, flatAttackBonus, { includeIgnoreArmor: true });
          cardImmediate = boosted.cardImmediate;
          delayGroups = boosted.delayGroups;
          if (boosted.applied) {
            next = { ...next, log: [`Relic: +${flatAttackBonus} damage to this Attack.`, ...next.log] };
            next = pushRelicTriggerAmount(next, 'attackBonusFlat', flatAttackBonus);
          }
        }
        const conditionalBonus = lowSpBonus + armorZeroBonus;
        if (conditionalBonus > 0) {
          const boosted = applyDamageBonusToCardAndDelayGroups(cardImmediate, delayGroups, conditionalBonus, { includeIgnoreArmor: true });
          cardImmediate = boosted.cardImmediate;
          delayGroups = boosted.delayGroups;
          if (boosted.applied) {
            next = { ...next, log: [`Relic: +${conditionalBonus} damage due to SP/Armor condition.`, ...next.log] };
            if (lowSpBonus > 0) {
              next = pushRelicTriggerAmount(next, 'attackBonusWhenLowSp', lowSpBonus);
            }
            if (armorZeroBonus > 0) {
              next = pushRelicTriggerAmount(next, 'attackBonusWhenArmorZero', armorZeroBonus);
            }
          }
        }
      }
      const blackhandShadowstepTriggers = isAttackTagged && isBlackhandShadowstepActiveForCurrentTurn(state);
      if (blackhandShadowstepTriggers) {
        const stripped = stripAttackEffectsForBlackhandShadowstep(cardImmediate, delayGroups);
        cardImmediate = stripped.cardImmediate;
        delayGroups = stripped.delayGroups;
        next = {
          ...next,
          blackhandShadowstepTurn: null,
          log: ['Shadowstep evades this Attack card.', ...next.log],
        } as GameState;
      }
      if (isAttackTagged || effectsCountAsAttack(cardImmediate.effects)) {
        next = markPlayerAttackForTurn(next);
      }
      if (cardImmediate.effects.length > 0) {
        next = applyCard(next, cardImmediate, 'player');
      }

      // Schedule any delayed effect bundles (Delay (X) sequences).
      // Each bundle triggers at the start of a future player turn.
      // Discard is treated as a cost and is never delayed.
      if (Array.isArray(delayGroups) && delayGroups.length > 0) {
        for (const g of delayGroups) {
          const baseTurns = Math.max(0, Number((g as any).turns) || 0);
          let t = baseTurns;

          // Apply relic-based delay reduction (Fine Sand Hourglass) if deck is large.
          const red = Math.max(0, Number((state as any).reduceDelayForLargeDeck ?? 0) || 0);
          const delayReducedByRelic = red > 0 ? Math.min(red, baseTurns) : 0;
          if (red > 0) {
            t = Math.max(0, t - red);
          }
          if (delayReducedByRelic > 0) {
            next = pushRelicTriggerAmount(next, 'reduceDelayForLargeDeck', delayReducedByRelic);
          }

          const effs = Array.isArray((g as any).effects) ? (g as any).effects : [];
          const safe = effs.filter((e: any) => e.type !== 'discard');
          if (safe.length === 0) continue;

          // Some effects (e.g. Spell Echo) schedule delayed bundles that should keep the
          // original card's title/kind (important for delayed draws).
          const sourceTitle = (g as any).sourceTitle ?? card.title;
          const sourceKind = (g as any).sourceKind ?? card.kind;
          const sourceId = (g as any).sourceId ?? card.id;
          const sourceCard: Card =
            sourceTitle === card.title && sourceKind === card.kind && sourceId === card.id
              ? card
              : ({ ...card, id: String(sourceId), title: String(sourceTitle), kind: sourceKind } as any);

          if (t > 0) {
            next = scheduleDelayed(next, sourceCard, safe, t);
          } else if (baseTurns > 0 && red > 0) {
            // If a relic reduces Delay to 0, the delayed bundle triggers immediately.
            next = triggerDelayedEffectsNow(next, sourceCard, safe);
          }
        }
      }


// Resolve "Draw X" after replacement draw (capped by max hand size inside drawFromSameDeck).
      // Resolve immediate Draw effects after the standard replacement draw (capped by max hand size).
      // If a Draw effect is delayed, it will be handled when its delayed bundle triggers.
      if (!drawAfterDiscard && !drawAfterPeek && anyDrawNeeded > 0 && (card.kind === 'physical' || card.kind === 'magic')) {
        const before = handCount(next);

        // Standard draw: draw randomly from any pile.
        if (drawSameDeckNeeded > 0) {
          next = drawExtraFromDeck(next, card.kind, drawSameDeckNeeded);
        }
        // Random draw variants
        if (drawRandomPhysicalNeeded > 0) {
          next = drawRandomExtraFromPiles(next, 'physical', drawRandomPhysicalNeeded);
        }
        if (drawRandomMagicNeeded > 0) {
          next = drawRandomExtraFromPiles(next, 'magic', drawRandomMagicNeeded);
        }
        if (drawRandomNeeded > 0) {
          next = drawRandomExtraFromPiles(next, 'any', drawRandomNeeded);
        }

        const drew = Math.max(0, handCount(next) - before);
        if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
      }

      // Relic: gain Armor when playing a card with Delay (Tardy Bracer)
      {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        const armorBonus = Math.max(0, Number(mods?.delayCardArmor ?? 0) || 0);
        const hasDelayNow = (legacyDelayTurns > 0) || (Array.isArray(delayGroups) && delayGroups.length > 0) || (costDelayTurns > 0);
        if (armorBonus > 0 && hasDelayNow) {
          const armorResult = applyPlayerArmorGainWithInvertedHeart(next, armorBonus);
          const overflowLog = formatInvertedHeartArmorOverflowLog(armorResult);
          next = {
            ...armorResult.state,
            log: [
              ...(overflowLog ? [overflowLog] : []),
              `Relic: Delay card played, gain +${armorBonus} Armor.`,
              ...next.log,
            ],
          };
          next = pushRelicTriggerAmount(next, 'delayCardArmor', armorBonus);
        }
      }


      // If the played card requires choosing a numeric X value (e.g. Blood Ritual / Forbidden Knowledge),
      // pause here AFTER replacement draw and any immediate Draw effects, then resume.
      if (xTradeNeeded) {
        const finisher = hasKeywordList(playKeywords, 'Finisher');
        const chain = hasKeywordList(playKeywords, 'Chain');
        const from = (xTradeNeeded as any).from as ('hp'|'sp');
        const to = (xTradeNeeded as any).to as ('hp'|'sp');
        const max = Math.max(0, Math.floor((from === 'hp' ? next.player.hp : next.player.sp) || 0));
        next = {
          ...next,
          canPlay: false,
          pendingX: { sourceTitle: card.title, from, to, max, sourceKind: card.kind === 'magic' ? 'magic' : 'physical', after: { discard: discardNeeded, chain, finisher } },
          log: [`Choose X for ${card.title}.`, ...next.log],
        };
        return settleGameOver(next);
      }


// If the played card requires peeking/reordering the top of a deck (e.g. Third Eye),
// pause here AFTER replacement draw and any immediate Draw effects, then resume.
// If the played card requires searching a deck and picking a card (e.g. Tutors),
      // pause here AFTER replacement draw and any immediate Draw effects, then resume.
      if (tutorNeeded) {
        const finisher = hasKeywordList(playKeywords, 'Finisher');
        const chain = hasKeywordList(playKeywords, 'Chain');
        if (tutorZone === 'deck') {
          next = ensureUnifiedDeckReady(next);
        }
        next = {
          ...next,
          canPlay: false,
          pendingTutor: { kind: tutorNeeded, zone: tutorZone, sourceKind: card.kind === 'magic' ? 'magic' : 'physical', after: { discard: discardNeeded, chain, finisher } },
          log: [
            tutorZone === 'discard'
              ? (tutorNeeded === 'any' ? `Pick a card from your discard pile.` : `Pick a card from your ${tutorNeeded} discard pile.`)
              : (tutorNeeded === 'any' ? `Pick a card from your deck.` : `Pick a card from your ${tutorNeeded} deck.`),
            ...next.log,
          ],
        };
        return settleGameOver(next);
      }


if (topPickPhysicalCount > 0 || topPickMagicCount > 0 || topPickAnyCount > 0) {
  const kind = topPickAnyCount > 0 ? 'any' : (topPickPhysicalCount > 0 ? 'physical' : 'magic');
  const rawCount = topPickAnyCount > 0 ? topPickAnyCount : (topPickPhysicalCount > 0 ? topPickPhysicalCount : topPickMagicCount);
  const count = Math.max(0, Math.min(20, Math.floor(rawCount)));

  let top: Card[] = [];
  if (kind === 'any') {
    const revealed = peekTopCardsFromUnifiedDeck(next, count);
    next = revealed.state;
    top = revealed.cards;
  } else {
    const revealed = peekCardsByKindFromUnifiedDeck(next, kind, count);
    next = revealed.state;
    top = revealed.cards;
  }

  const finisher = hasKeywordList(playKeywords, 'Finisher');
  const chain = hasKeywordList(playKeywords, 'Chain');
  next = {
    ...next,
    canPlay: false,
    pendingTopPick: { kind, cards: top, sourceKind: card.kind === 'magic' ? 'magic' : 'physical', after: { discard: discardNeeded, chain, finisher } },
    log: [
      kind === 'any'
        ? `Pick 1 of the top ${top.length} deck card(s).`
        : `Pick 1 of the top ${top.length} ${kind} card(s).`,
      ...next.log,
    ],
  };
  return settleGameOver(next);
}

if (peekPhysicalCount > 0 || peekMagicCount > 0 || peekDeckCount > 0) {
  const kind = peekDeckCount > 0 ? 'any' : (peekPhysicalCount > 0 ? 'physical' : 'magic');
  const rawCount = peekDeckCount > 0 ? peekDeckCount : (peekPhysicalCount > 0 ? peekPhysicalCount : peekMagicCount);
  const count = Math.max(0, Math.min(20, Math.floor(rawCount)));
  const revealed = kind === 'any'
    ? peekTopCardsFromUnifiedDeck(next, count)
    : peekCardsByKindFromUnifiedDeck(next, kind, count);
  next = revealed.state;
  const top = revealed.cards;
  const finisher = hasKeywordList(playKeywords, 'Finisher');
  const chain = hasKeywordList(playKeywords, 'Chain');
  next = {
    ...next,
    canPlay: false,
    pendingPeek: { kind, cards: top, sourceKind: card.kind === 'magic' ? 'magic' : 'physical', after: { discard: discardNeeded, chain, finisher, afterDraw: drawAfterPeek && anyDrawNeeded > 0 ? { sameDeck: drawSameDeckNeeded, sameDeckPhysical: drawSameDeckPhysicalNeeded, sameDeckMagic: drawSameDeckMagicNeeded, sameDeckAny: drawSameDeckAnyNeeded, random: drawRandomNeeded, randomPhysical: drawRandomPhysicalNeeded, randomMagic: drawRandomMagicNeeded } : undefined } },
    log: [kind === 'any' ? `Reorder the top ${top.length} deck card(s).` : `Reorder the top ${top.length} ${kind} card(s).`, ...next.log],
  };
  return settleGameOver(next);
}


      // If the played card requires a choice (Choose one), pause here AFTER replacement draw and any immediate Draw effects.
      if (chooseOneOptions && chooseOneOptions.length > 0) {
        const finisher = hasKeywordList(playKeywords, 'Finisher');
        const chain = hasKeywordList(playKeywords, 'Chain');
        next = {
          ...next,
          canPlay: false,
          pendingChoice: { sourceTitle: card.title, sourceId: card.id, sourceKind: card.kind, options: chooseOneOptions, after: { discard: discardNeeded, chain, finisher } },
          log: [`Choose one effect for ${card.title}.`, ...next.log],
        };
        return settleGameOver(next);
      }

      // If a card both Draws and Discards, and the written order is Draw -> Discard,
      // those draws must resolve now before opening the discard prompt.
      // This is important for cards like Spell Echo, whose copied draw effects would
      // otherwise be skipped once the discard prompt interrupts the normal draw pass.
      if (discardNeeded > 0 && !drawAfterDiscard && !drawAfterPeek && anyDrawNeeded > 0 && (card.kind === 'physical' || card.kind === 'magic')) {
        const before = handCount(next);

        if (drawSameDeckNeeded > 0) next = drawExtraFromDeck(next, card.kind, drawSameDeckNeeded);
        if (drawSameDeckPhysicalNeeded > 0) next = drawExtraFromDeck(next, 'physical', drawSameDeckPhysicalNeeded);
        if (drawSameDeckMagicNeeded > 0) next = drawExtraFromDeck(next, 'magic', drawSameDeckMagicNeeded);
        if (drawSameDeckAnyNeeded > 0) next = drawRandomExtraFromPiles(next, 'any', drawSameDeckAnyNeeded);
        if (drawRandomPhysicalNeeded > 0) next = drawRandomExtraFromPiles(next, 'physical', drawRandomPhysicalNeeded);
        if (drawRandomMagicNeeded > 0) next = drawRandomExtraFromPiles(next, 'magic', drawRandomMagicNeeded);
        if (drawRandomNeeded > 0) next = drawRandomExtraFromPiles(next, 'any', drawRandomNeeded);

        const drew = Math.max(0, handCount(next) - before);
        if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };

        drawSameDeckNeeded = 0;
        drawSameDeckPhysicalNeeded = 0;
        drawSameDeckMagicNeeded = 0;
        drawSameDeckAnyNeeded = 0;
        drawRandomNeeded = 0;
        drawRandomPhysicalNeeded = 0;
        drawRandomMagicNeeded = 0;
        anyDrawNeeded = 0;
      }

      // If the played card requires Discard, resolve it now (after replacement draw).
      // If the hand is empty, the discard requirement is canceled.
      if (discardNeeded > 0 && !skipDiscardPrereq) {
        const finisher = hasKeywordList(playKeywords, 'Finisher');
        const chain = hasKeywordList(playKeywords, 'Chain');

        const payable = Math.min(discardNeeded, handCount(next));
        if (payable > 0) {
          // pendingDiscard.sourceKind is only used to decide which deck to draw from after discarding.
          // Enemy cards should never trigger a discard prompt, but TS sees CardKind includes 'enemy',
          // so clamp to the player deck kinds.
          const sourceKind: 'physical' | 'magic' = card.kind === 'magic' ? 'magic' : 'physical';
          next = {
            ...next,
            canPlay: false,
            pendingDiscard: { remaining: payable, chain, finisher, sourceKind, afterDraw: drawAfterDiscard && anyDrawNeeded > 0 ? { sameDeck: drawSameDeckNeeded, sameDeckPhysical: drawSameDeckPhysicalNeeded, sameDeckMagic: drawSameDeckMagicNeeded, sameDeckAny: drawSameDeckAnyNeeded, random: drawRandomNeeded, randomPhysical: drawRandomPhysicalNeeded, randomMagic: drawRandomMagicNeeded } : undefined },
            log: [`Choose ${payable} card(s) to discard.`, ...next.log],
          };
          return settleGameOver(next);
        }

        // Nothing to discard: continue as if the cost was paid.
        next = { ...next, log: ['No cards to discard. Discard requirement canceled.', ...next.log] };


        // If the written order was Discard → Draw, draws were deferred until after the discard prompt.
        // If discard is canceled due to an empty hand, resolve those deferred draws now.
        if (drawAfterDiscard && anyDrawNeeded > 0 && (card.kind === 'physical' || card.kind === 'magic')) {
          const before = handCount(next);

          if (drawSameDeckNeeded > 0) next = drawExtraFromDeck(next, card.kind, drawSameDeckNeeded);
          if (drawRandomPhysicalNeeded > 0) next = drawRandomExtraFromPiles(next, 'physical', drawRandomPhysicalNeeded);
          if (drawRandomMagicNeeded > 0) next = drawRandomExtraFromPiles(next, 'magic', drawRandomMagicNeeded);
          if (drawRandomNeeded > 0) next = drawRandomExtraFromPiles(next, 'any', drawRandomNeeded);

          const drew = Math.max(0, handCount(next) - before);
          if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
        }
        return finalizePlayerAction(next, { chain, finisher, sourceKind: card.kind });
      }

      if (discardNeeded > 0 && skipDiscardPrereq && drawAfterDiscard && anyDrawNeeded > 0 && (card.kind === 'physical' || card.kind === 'magic')) {
        const before = handCount(next);
        if (drawSameDeckNeeded > 0) next = drawExtraFromDeck(next, card.kind, drawSameDeckNeeded);
        if (drawSameDeckPhysicalNeeded > 0) next = drawExtraFromDeck(next, 'physical', drawSameDeckPhysicalNeeded);
        if (drawSameDeckMagicNeeded > 0) next = drawExtraFromDeck(next, 'magic', drawSameDeckMagicNeeded);
        if (drawSameDeckAnyNeeded > 0) next = drawRandomExtraFromPiles(next, 'any', drawSameDeckAnyNeeded);
        if (drawRandomPhysicalNeeded > 0) next = drawRandomExtraFromPiles(next, 'physical', drawRandomPhysicalNeeded);
        if (drawRandomMagicNeeded > 0) next = drawRandomExtraFromPiles(next, 'magic', drawRandomMagicNeeded);
        if (drawRandomNeeded > 0) next = drawRandomExtraFromPiles(next, 'any', drawRandomNeeded);
        const drew = Math.max(0, handCount(next) - before);
        if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
      }

      const finisher = hasKeywordList(playKeywords, 'Finisher');
      const chain = hasKeywordList(playKeywords, 'Chain');

      // By default the player's turn ends after one play, BUT 'Chain' allows the player to continue playing.
      return finalizePlayerAction(next, { chain, finisher, sourceKind: card.kind });
    }

case 'CONFIRM_PEEK_ORDER': {
  if (state.phase !== 'player') return state;
  if (!state.pendingPeek) return state;
  const pending = state.pendingPeek;

  const kind = pending.kind;
  const piles = getPlayerPileState(state);
  const deck = [...piles.deck];

  const orderedIds = Array.isArray((action as any).orderedIds) ? (action as any).orderedIds : [];
  const ordered: Card[] = [];
  for (const id of orderedIds) {
    const c = pending.cards.find((x) => x.id === id);
    if (c) ordered.push(c);
  }

  const peekIds = new Set(pending.cards.map((c) => c.id));
  const rest = deck.filter((c) => !peekIds.has(c.id));
  let next: GameState = withPlayerPileState(
    {
      ...state,
      pendingPeek: null,
      canPlay: false,
      log: [kind === 'any' ? `Reordered top ${ordered.length} deck card(s).` : `Reordered top ${ordered.length} ${kind} card(s).`, ...state.log],
    } as GameState,
    { deck: [...ordered, ...rest], discard: piles.discard, exhaust: piles.exhaust }
  );

  const afterDraw = (pending.after as any)?.afterDraw as any;
  if (
    afterDraw &&
    ((afterDraw.sameDeck || 0) > 0 ||
      (afterDraw.sameDeckPhysical || 0) > 0 ||
      (afterDraw.sameDeckMagic || 0) > 0 ||
      (afterDraw.sameDeckAny || 0) > 0 ||
      (afterDraw.random || 0) > 0 ||
      (afterDraw.randomPhysical || 0) > 0 ||
      (afterDraw.randomMagic || 0) > 0)
  ) {
    const before = handCount(next);

    if ((afterDraw.sameDeck || 0) > 0) {
      if (kind === 'physical' || kind === 'magic') next = drawExtraFromDeck(next, kind, afterDraw.sameDeck);
      else next = drawRandomExtraFromPiles(next, 'any', afterDraw.sameDeck);
    }
    if ((afterDraw.sameDeckPhysical || 0) > 0) next = drawExtraFromDeck(next, 'physical', afterDraw.sameDeckPhysical);
    if ((afterDraw.sameDeckMagic || 0) > 0) next = drawExtraFromDeck(next, 'magic', afterDraw.sameDeckMagic);
    if ((afterDraw.sameDeckAny || 0) > 0) next = drawRandomExtraFromPiles(next, 'any', afterDraw.sameDeckAny);
    if ((afterDraw.randomPhysical || 0) > 0) next = drawRandomExtraFromPiles(next, 'physical', afterDraw.randomPhysical);
    if ((afterDraw.randomMagic || 0) > 0) next = drawRandomExtraFromPiles(next, 'magic', afterDraw.randomMagic);
    if ((afterDraw.random || 0) > 0) next = drawRandomExtraFromPiles(next, 'any', afterDraw.random);

    const drew = Math.max(0, handCount(next) - before);
    if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
  }

  const discardAfter = Math.max(0, pending.after?.discard || 0);
  if (discardAfter > 0) {
    const payable = Math.min(discardAfter, handCount(next));
    if (payable > 0) {
      next = {
        ...next,
        canPlay: false,
        pendingDiscard: { remaining: payable, chain: !!pending.after?.chain, finisher: !!pending.after?.finisher, sourceKind: pending.sourceKind },
        log: [`Choose ${payable} card(s) to discard.`, ...next.log],
      };
      return settleGameOver(next);
    }
    next = { ...next, log: ['No cards to discard. Discard requirement canceled.', ...next.log] };
  }

  const chain = !!pending.after?.chain;
  const finisher = !!pending.after?.finisher;
  return finalizePlayerAction(next, { chain, finisher, sourceKind: pending.sourceKind });
}


case 'CONFIRM_TOP_PICK': {
  if (state.phase !== 'player') return state;
  if (!state.pendingTopPick) return state;
  const pending = state.pendingTopPick;
  const kind = pending.kind;

  let next: GameState = { ...state, pendingTopPick: null, canPlay: false };

  const pickId = String((action as any).cardId ?? '').trim();
  const chosen = pending.cards.find((c) => c.id === pickId) ?? pending.cards[0] ?? null;
  const others = chosen ? pending.cards.filter((c) => c.id !== chosen.id) : pending.cards.slice();

  const piles = getPlayerPileState(next);
  const revealedIds = new Set(pending.cards.map((c) => c.id));
  next = withPlayerPileState(next, {
    deck: piles.deck.filter((c) => !revealedIds.has(c.id)),
    discard: piles.discard,
    exhaust: piles.exhaust,
  });
  if (others.length > 0) {
    next = pushCardsToDiscard(next, others);
  }

  if (chosen) {
    const before = handCount(next);
    next = addExtraToHand(next, chosen);
    const drew = Math.max(0, handCount(next) - before);
    next = {
      ...next,
      log: [
        drew > 0 ? `${kind === 'any' ? 'Third Eye' : 'Peek'}: took ${chosen.title}.` : `${kind === 'any' ? 'Third Eye' : 'Peek'}: hand full, couldn't take ${chosen.title}.`,
        ...next.log,
      ],
    };
  } else {
    next = { ...next, log: [`${kind === 'any' ? 'Third Eye' : 'Peek'}: no cards to pick.`, ...next.log] };
  }

  const discardAfter = Math.max(0, pending.after?.discard || 0);
  if (discardAfter > 0) {
    const payable = Math.min(discardAfter, handCount(next));
    if (payable > 0) {
      next = {
        ...next,
        canPlay: false,
        pendingDiscard: { remaining: payable, chain: !!pending.after?.chain, finisher: !!pending.after?.finisher, sourceKind: pending.sourceKind },
        log: [`Choose ${payable} card(s) to discard.`, ...next.log],
      };
      return settleGameOver(next);
    }
    next = { ...next, log: ['No cards to discard. Discard requirement canceled.', ...next.log] };
  }

  const chain = !!pending.after?.chain;
  const finisher = !!pending.after?.finisher;
  return finalizePlayerAction(next, { chain, finisher, sourceKind: pending.sourceKind });
}


case 'CONFIRM_CHOICE': {
  if (!state.pendingChoice) return state;
  const pending = state.pendingChoice;
  const isPlayerChoice = state.phase === 'player' && pending.sourceOwner !== 'enemy';
  const isEnemyChoice = state.phase === 'enemy' && pending.sourceOwner === 'enemy';
  if (!isPlayerChoice && !isEnemyChoice) return state;

  const idx = Math.max(0, Math.min(pending.options.length - 1, Math.floor(Number(action.optionIndex) || 0)));
  const opt = pending.options[idx];
  if (!opt) return state;

  let next: GameState = {
    ...state,
    pendingChoice: null,
    canPlay: false,
    log: [`Chose: ${opt.label}.`, ...state.log],
  };

  if (isEnemyChoice) {
    const choiceAction = String((opt as any)?.custodianAction ?? '').trim().toLowerCase();
    if (custodianTitleKey(pending.sourceTitle) === 'inventory tax') {
      if (choiceAction === 'discard_card') {
        const payable = Math.min(1, handCount(next));
        if (payable > 0) {
          next = {
            ...next,
            canPlay: false,
            pendingDiscard: { remaining: payable, chain: false, finisher: false, resumeEnemyTurn: true },
            log: ['Choose 1 card to discard.', ...next.log],
          };
          return settleGameOver(next);
        }
        next = applyEffect(next, { type: 'loseSP', amount: 25 } as any, 'player');
        return continueAfterEnemyPlayerPrompt({
          ...next,
          log: ['Inventory Tax: no card can be discarded, so the player burns 25 SP.', ...next.log],
        } as GameState);
      }

      next = applyEffect(next, { type: 'loseSP', amount: 25 } as any, 'player');
      return continueAfterEnemyPlayerPrompt(next);
    }

    return continueAfterEnemyPlayerPrompt(next);
  }

  if (effectsCountAsAttack(opt.effects)) {
    next = markPlayerAttackForTurn(next);
  }

  for (const eff of opt.effects) {
    next = applyEffect(next, eff, 'player');
  }

// Some choice options can schedule delayed effects (e.g. Second Wind draws next turn).
const optDelayed = Array.isArray((opt as any).delayed) ? ((opt as any).delayed as any[]) : [];
if (optDelayed.length > 0) {
  const sourceCard = ({ id: (pending as any).sourceId ?? pending.sourceTitle, title: pending.sourceTitle, kind: (pending as any).sourceKind ?? 'physical' } as any) as Card;
  for (const g of optDelayed) {
    const turns = Math.max(0, Math.floor(Number((g as any).turns) || 0));
    const effs = Array.isArray((g as any).effects) ? ((g as any).effects as Effect[]) : [];
    const safe = effs.filter((e: any) => e.type !== 'discard');
    if (turns > 0 && safe.length > 0) {
      next = scheduleDelayed(next, sourceCard, safe, turns);
    }
  }
}

  const discardAfter = Math.max(0, pending.after?.discard || 0);
  if (discardAfter > 0) {
    const payable = Math.min(discardAfter, handCount(next));
    if (payable > 0) {
      next = {
        ...next,
        canPlay: false,
        pendingDiscard: { remaining: payable, chain: !!pending.after?.chain, finisher: !!pending.after?.finisher, sourceKind: normalizePlayerSourceKind(pending.sourceKind) },
        log: [`Choose ${payable} card(s) to discard.`, ...next.log],
      };
      return settleGameOver(next);
    }

    // Nothing to discard: continue as if the cost was paid.
    next = { ...next, log: ['No cards to discard. Discard requirement canceled.', ...next.log] };
  }

  const chain = !!pending.after?.chain;
  const finisher = !!pending.after?.finisher;
  return finalizePlayerAction(next, { chain, finisher, sourceKind: pending.sourceKind });
}


case 'CONFIRM_X': {
  if (state.phase !== 'player') return state;
  if (!state.pendingX) return state;
  const pending = state.pendingX;

  const value = Math.max(0, Math.min(Math.floor(Number(action.value) || 0), Math.max(0, pending.max || 0)));

  let next: GameState = { ...state, pendingX: null, canPlay: false };

  // Apply the trade as two atomic effects so it follows existing clamping/log style.
  if (pending.from === 'hp') next = applyEffect(next, { type: 'loseHP', amount: value } as any, 'player');
  else next = applyEffect(next, { type: 'loseSP', amount: value } as any, 'player');

  if (pending.to === 'hp') next = applyEffect(next, { type: 'healHP', amount: value } as any, 'player');
  else next = applyEffect(next, { type: 'healSP', amount: value } as any, 'player');

  next = { ...next, log: [`X chosen: ${value}.`, ...next.log] };

  const discardAfter = Math.max(0, pending.after?.discard || 0);
  if (discardAfter > 0) {
    const payable = Math.min(discardAfter, handCount(next));
    if (payable > 0) {
      next = {
        ...next,
        canPlay: false,
        pendingDiscard: { remaining: payable, chain: !!pending.after?.chain, finisher: !!pending.after?.finisher, sourceKind: pending.sourceKind },
        log: [`Choose ${payable} card(s) to discard.`, ...next.log],
      };
      return settleGameOver(next);
    }

    // Nothing to discard: continue as if the cost was paid.
    next = { ...next, log: ['No cards to discard. Discard requirement canceled.', ...next.log] };
  }

  const chain = !!pending.after?.chain;
  const finisher = !!pending.after?.finisher;
  return finalizePlayerAction(next, { chain, finisher, sourceKind: pending.sourceKind });
}



case 'DISCARD_FROM_HAND': {
      if (state.phase !== 'player' && !(state.phase === 'enemy' && (state.pendingDiscard as any)?.resumeEnemyTurn)) return state;
      if (!state.pendingDiscard || state.pendingDiscard.remaining <= 0) return state;

      const skipDiscardPrereq = !!(action as any).skipDiscardPrereq;

      const found = findInHand(state, action.cardId);
      if (!found) return state;
      const { card, where } = found;

      // Remove from hand
      let next: GameState = {
        ...state,
        hand: removeFromHand(state, where).hand,
        log: [`Discarded ${card.title}.`, ...state.log],
      };

      // Move discarded card to its discard pile (unless Exile)
      next = discardPlayed(next, card, 'discard');

      // Relic triggers: gain SP/HP on discard
      {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        const gainSp = Math.max(0, Number(mods?.onDiscardGainSp ?? 0) || 0);
        if (gainSp > 0) {
          const newSp = clampPlayerSp(next, (next.player.sp || 0) + gainSp);
          next = {
            ...next,
            player: { ...next.player, sp: newSp },
            log: [`Relic: gain +${gainSp} SP from discarding.`, ...next.log],
          };
          next = pushRelicTriggerAmount(next, 'onDiscardGainSp', gainSp);
        }
        const gainHp = Math.max(0, Number(mods?.onDiscardGainHp ?? 0) || 0);
        if (gainHp > 0) {
          const healResult = applyPlayerHealingWithInvertedHeart(next, gainHp);
          const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
          next = {
            ...healResult.state,
            log: [
              ...(overflowLog ? [overflowLog] : []),
              `Relic: gain +${gainHp} HP from discarding.`,
              ...next.log,
            ],
          };
          next = pushRelicTriggerAmount(next, 'onDiscardGainHp', gainHp);
        }
      }

      next = triggerOnDiscard(next, card);
      const discardPlayStacks = Math.max(0, Number((next as any).discardPlayThisTurn ?? 0) || 0);
      if (discardPlayStacks > 0) {
        next = resolveDiscardedCardAsPlay(next, card, discardPlayStacks);
      }
      // Decrement remaining discards
      const remaining = Math.max(0, state.pendingDiscard.remaining - 1);

      // Still more discards to pay
      if (remaining > 0) {
        next = {
          ...next,
          canPlay: false,
          pendingDiscard: { ...state.pendingDiscard, remaining },
          log: [`Choose ${remaining} more card(s) to discard.`, ...next.log],
        };
        return settleGameOver(next);
      }

      // Done discarding: if we were only paying a pre-play discard prerequisite,
      // hand control back to the UI so it can run the play animation and commit the
      // card at the correct impact beat.
      if (state.pendingPrereqPlay) {
        next = { ...next, pendingDiscard: null, canPlay: false };
        return settleGameOver(next);
      }

      // Done discarding: resume the turn if the played card had 'Chain' (unless it had 'Finisher').
      const chain = !!state.pendingDiscard.chain;
      const finisher = !!state.pendingDiscard.finisher;

      const afterDraw = (state.pendingDiscard as any).afterDraw as any;
      next = { ...next, pendingDiscard: null };

      // If the card's written order was Discard → Draw, resolve the deferred draws now.
      if (
        afterDraw &&
        ((afterDraw.sameDeck || 0) > 0 ||
          (afterDraw.sameDeckPhysical || 0) > 0 ||
          (afterDraw.sameDeckMagic || 0) > 0 ||
          (afterDraw.sameDeckAny || 0) > 0 ||
          (afterDraw.random || 0) > 0 ||
          (afterDraw.randomPhysical || 0) > 0 ||
          (afterDraw.randomMagic || 0) > 0)
      ) {
        const before = handCount(next);

        if ((afterDraw.sameDeck || 0) > 0) {
        const k = (state.pendingDiscard as any)?.sourceKind;
        if (k === 'physical' || k === 'magic') next = drawExtraFromDeck(next, k, afterDraw.sameDeck);
        else next = drawRandomExtraFromPiles(next, 'any', afterDraw.sameDeck);
      }
        if ((afterDraw.sameDeckPhysical || 0) > 0) next = drawExtraFromDeck(next, 'physical', afterDraw.sameDeckPhysical);
        if ((afterDraw.sameDeckMagic || 0) > 0) next = drawExtraFromDeck(next, 'magic', afterDraw.sameDeckMagic);
        if ((afterDraw.sameDeckAny || 0) > 0) next = drawRandomExtraFromPiles(next, 'any', afterDraw.sameDeckAny);
        if ((afterDraw.randomPhysical || 0) > 0) next = drawRandomExtraFromPiles(next, 'physical', afterDraw.randomPhysical);
        if ((afterDraw.randomMagic || 0) > 0) next = drawRandomExtraFromPiles(next, 'magic', afterDraw.randomMagic);
        if ((afterDraw.random || 0) > 0) next = drawRandomExtraFromPiles(next, 'any', afterDraw.random);

        const drew = Math.max(0, handCount(next) - before);
        if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
      }

      if ((state.pendingDiscard as any)?.resumeEnemyTurn) {
        return continueAfterEnemyPlayerPrompt(next);
      }

      return finalizePlayerAction(next, { chain, finisher, sourceKind: state.pendingDiscard?.sourceKind });
    }

    case 'CONFIRM_TUTOR_PICK': {
      if (state.phase !== 'player') return state;
      if (!state.pendingTutor) return state;

      const kind = state.pendingTutor.kind;
      const zone = state.pendingTutor.zone === 'discard' ? 'discard' : 'deck';
      let next: GameState = zone === 'deck' ? ensureUnifiedDeckReady({ ...state }) : { ...state };

      const discardAfter = Math.max(0, state.pendingTutor.after?.discard || 0);
      const chainAfter = !!state.pendingTutor.after?.chain;
      const finisherAfter = !!state.pendingTutor.after?.finisher;

      const finishAfterTutor = (s: GameState): GameState => {
        let out: GameState = { ...s, canPlay: false, pendingTutor: null };
        if (discardAfter > 0) {
          const payable = Math.min(discardAfter, handCount(out));
          if (payable > 0) {
            out = {
              ...out,
              canPlay: false,
              pendingDiscard: { remaining: payable, chain: chainAfter, finisher: finisherAfter, sourceKind: state.pendingTutor?.sourceKind },
              log: [`Choose ${payable} card(s) to discard.`, ...out.log],
            };
            return settleGameOver(out);
          }
          out = { ...out, log: ['No cards to discard. Discard requirement canceled.', ...out.log] };
        }
        return finalizePlayerAction(out, { chain: chainAfter, finisher: finisherAfter, sourceKind: state.pendingTutor?.sourceKind });
      };

      if (handCount(next) >= 5) {
        next = { ...next, log: [zone === 'discard' ? 'Hand is full. Discard recovery skipped.' : 'Hand is full. Tutor skipped.', ...next.log] };
        return finishAfterTutor(next);
      }

      const pickId = String((action as any).cardId || '');
      if (!pickId) {
        next = { ...next, log: ['No cards to tutor.', ...next.log] };
        return finishAfterTutor(next);
      }

      const piles = getPlayerPileState(next);
      const basePile = zone === 'discard' ? piles.discard : piles.deck;
      const candidates = (kind === 'physical' || kind === 'magic')
        ? basePile.filter((c) => c.kind === kind)
        : basePile;
      const pickIndex = candidates.findIndex((c) => c.id === pickId);
      if (pickIndex < 0) {
        return next;
      }
      const picked = candidates[pickIndex];
      const sourcePile = [...basePile];
      const sourceIndex = sourcePile.findIndex((c) => c.id === pickId);
      if (sourceIndex < 0 || !picked) {
        return next;
      }
      sourcePile.splice(sourceIndex, 1);
      next = withPlayerPileState(next, zone === 'discard'
        ? { deck: piles.deck, discard: sourcePile, exhaust: piles.exhaust }
        : { deck: sourcePile, discard: piles.discard, exhaust: piles.exhaust }
      );

      next = addExtraToHand(next, picked);
      next = { ...next, log: [zone === 'discard' ? `Recovered ${picked.title} from discard.` : `Tutored ${picked.title}.`, ...next.log] };
      return finishAfterTutor(next);
    }

    case 'RESHUFFLE_SELECTED': {
      if (state.phase !== 'player') return state;
      if ((state.player?.stunned ?? 0) > 0) return state;
      if (!state.canPlay) return state;
      if (state.pendingDiscard || state.pendingPeek || (state as any).pendingTopPick || state.pendingChoice || state.pendingTutor || state.pendingX) return state;

      const rawIds = (action as any).cardIds;
      const raw: unknown[] = Array.isArray(rawIds) ? rawIds : [];
      const filtered: string[] = [];
      for (const v of raw) {
        if (typeof v === 'string') {
          const s = v.trim();
          if (s.length > 0) filtered.push(s);
        }
      }
      const ids: string[] = Array.from(new Set(filtered));
      if (ids.length === 0) return state;

      let next: GameState = state;
      const piles = getPlayerPileState(state);
      const deck = [...piles.deck, ...piles.discard];
      let moved = 0;

      for (const id of ids) {
        const found = findInHand(next, id);
        if (!found) continue;
        const { card, where } = found;
        next = { ...next, hand: removeFromHand(next, where).hand };
        deck.push(card);
        moved += 1;
      }

      if (moved <= 0) return state;

      const hpGainTarget = moved * RESHUFFLE_HEAL_PER_CARD;
      const spGainTarget = moved * RESHUFFLE_HEAL_PER_CARD;
      const prevHp = next.player.hp;
      const prevSp = next.player.sp;
      const healResult = applyPlayerHealingWithInvertedHeart(next, hpGainTarget);
      const newSp = clampPlayerSp(healResult.state, prevSp + spGainTarget);
      const overflowLog = formatInvertedHeartHealOverflowLog(healResult);

      next = {
        ...healResult.state,
        player: { ...healResult.state.player, sp: newSp },
        log: [
          ...(overflowLog ? [overflowLog] : []),
          `Reshuffle bonus: +${healResult.gainedHp} HP, +${newSp - prevSp} SP (${moved} card${moved === 1 ? '' : 's'}).`,
          ...next.log,
        ],
      };

      next = withPlayerPileState(next, {
        deck: shuffle(deck),
        discard: [],
        exhaust: piles.exhaust,
      });
      next = {
        ...next,
        canPlay: false,
        log: [`Reshuffled ${moved} selected card(s) + discard into the deck.`, ...next.log],
      };

      const before = handCount(next);
      const mods = (next as any).relicMods as RelicCombatMods | undefined;
      const recastPenalty =
        typeof mods?.recastDrawPenalty === 'number'
          ? Math.max(0, Math.floor(Number(mods.recastDrawPenalty) || 0))
          : 1;
      const preventedPenalty = Math.max(0, 1 - recastPenalty);
      const recastDrawCount = Math.max(1, moved - recastPenalty);

      next = drawRandomExtraFromPiles(next, 'any', recastDrawCount);

      if (preventedPenalty > 0) {
        next = pushRelicTrigger(
          next,
          'recastDrawPenalty',
          formatRelicFeedbackText('recastDrawPenalty', preventedPenalty)
        );
      }

      const drew = Math.max(0, handCount(next) - before);
      if (drew > 0) {
        next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
      }

      next = { ...next, log: ['Turn ends.', ...next.log] };
      next = reducer(next, { type: 'END_PLAYER_TURN' });
      return settleGameOver(next);
    }

case 'SKIP_PLAYER_TURN': {
  if (state.phase !== 'player') return state;

  const wasStunned = (state.player?.stunned ?? 0) > 0;
  const nextStunned = wasStunned
    ? Math.max(0, Math.floor(Number(state.player?.stunned ?? 0) || 0) - 1)
    : Math.max(0, Math.floor(Number(state.player?.stunned ?? 0) || 0));

  const mods = (state as any).relicMods as RelicCombatMods | undefined;
  const focusSpBonus = Math.max(0, Math.floor(Number(mods?.focusBonusSP ?? 0) || 0));
  const focusHpBonus = Math.max(0, Math.floor(Number(mods?.focusBonusHP ?? 0) || 0));

  const prevSp = Number(state.player.sp || 0);
  const prevHp = Number(state.player.hp || 0);

  const newSp = clampPlayerSp(state, prevSp + 10 + focusSpBonus);
  const healResult = applyPlayerHealingWithInvertedHeart(state, focusHpBonus);
  const overflowLog = formatInvertedHeartHealOverflowLog(healResult);

  const gainedSp = Math.max(0, newSp - prevSp);
  const gainedHp = healResult.gainedHp;

  let next: GameState = {
    ...healResult.state,
    autoFocusPending: false,
    playerFocusedThisTurn: true,
    playerMarkedByBlackhand:
      isPlayerMarkedByBlackhand(state) && (state.playerPlaysThisTurn || 0) === 0
        ? false
        : (state as any).playerMarkedByBlackhand ?? false,
    playerMarkedTurn:
      isPlayerMarkedByBlackhand(state) && (state.playerPlaysThisTurn || 0) === 0
        ? null
        : (state as any).playerMarkedTurn ?? null,
    player: { ...healResult.state.player, sp: newSp, stunned: nextStunned },
    log: [
      ...(overflowLog ? [overflowLog] : []),
      ...(isPlayerMarkedByBlackhand(state) && (state.playerPlaysThisTurn || 0) === 0 ? ['Mark removed: Focus was your only turn action.'] : []),
      gainedHp > 0
        ? `Focused: +${gainedSp} SP, +${gainedHp} HP.`
        : gainedSp > 0
          ? `Focused: +${gainedSp} SP.`
          : 'Focused (HP/SP already full).',
      ...(wasStunned
        ? nextStunned > 0
          ? [`Stun remains for ${nextStunned} turn(s).`]
          : ['Stun wears off.']
        : []),
      ...state.log,
    ],
  };

  if (gainedSp > 10) {
    next = pushRelicTriggerAmount(next, 'focusBonusSP', gainedSp - 10);
  }
  if (gainedHp > 0) {
    next = pushRelicTriggerAmount(next, 'focusBonusHP', gainedHp);
  }

  return reducer(next, { type: 'END_PLAYER_TURN' });
}

case 'RECAST': {
  if (state.phase !== 'player') return state;
  if ((state.player?.stunned ?? 0) > 0) return state;
  if (!state.canPlay) return state;
  if (state.pendingDiscard || state.pendingPeek || (state as any).pendingTopPick || state.pendingChoice || state.pendingTutor || state.pendingX) return state;

  const mods = (state as any).relicMods as RelicCombatMods | undefined;
  const recastPenalty =
    typeof mods?.recastDrawPenalty === 'number'
      ? Math.max(0, Math.floor(Number(mods.recastDrawPenalty) || 0))
      : 1;
  const preventedPenalty = Math.max(0, 1 - recastPenalty);
  const prevHand = handCount(state);
  const drawCount = Math.max(1, prevHand - recastPenalty);

  const handCards = allHandCards(state);
  const piles = getPlayerPileState(state);
  const deck = [...piles.deck, ...piles.discard, ...handCards];

  let next: GameState = withPlayerPileState(
    {
      ...state,
      hand: { main: { physical: null, magic: null }, extra: [] },
      canPlay: false,
      log: ['Recast: shuffled hand + discard into the deck.', ...state.log],
    } as GameState,
    { deck: shuffle(deck), discard: [], exhaust: piles.exhaust }
  );

  const before = handCount(next);
  next = drawRandomExtraFromPiles(next, 'any', drawCount);
  const drew = Math.max(0, handCount(next) - before);
  if (drew > 0) {
    next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
  }
  if (preventedPenalty > 0) {
    next = pushRelicTrigger(
      next,
      'recastDrawPenalty',
      formatRelicFeedbackText('recastDrawPenalty', preventedPenalty)
    );
  }

  next = { ...next, log: ['Turn ends.', ...next.log] };
  next = reducer(next, { type: 'END_PLAYER_TURN' });
  return settleGameOver(next);
}

    case 'END_PLAYER_TURN': {
      if (state.phase !== 'player') return state;
      const curWeaken = Math.max(0, Number((state as any).playerWeakenedTurns ?? 0) || 0);
      const nextWeaken = curWeaken > 0 ? Math.max(0, curWeaken - 1) : 0;
      const curPunitive = Math.max(0, Number((state as any).punitiveMeasureTurns ?? 0) || 0);
      const nextPunitive = curPunitive > 0 ? Math.max(0, curPunitive - 1) : 0;
      const unauthorizedTick = tickUnauthorizedPassageLocksAfterPlayerTurn(state);
      const curForfeiture = Math.max(0, Number((state as any).custodianForfeitureTurns ?? 0) || 0);
      const nextForfeiture = curForfeiture > 0 ? Math.max(0, curForfeiture - 1) : 0;
      let nextState: GameState = state;
      // Apply end-of-turn relic effects before transitioning to enemy.
      const mods = (state as any).relicMods as RelicCombatMods | undefined;
      const hc = handCount(state);
      // Hollow Chime: draw if hand is empty
      const emptyDraw = Math.max(0, Number(mods?.endTurnEmptyDraw ?? 0) || 0);
      if (emptyDraw > 0 && hc === 0) {
        const before = handCount(nextState);
        nextState = drawRandomExtraFromPiles(nextState, 'any', emptyDraw);
        const drew = Math.max(0, handCount(nextState) - before);
        if (drew > 0) {
          nextState = { ...nextState, log: [`Relic: hand empty at turn end, drew ${drew} card(s).`, ...nextState.log] };
          nextState = pushRelicTriggerAmount(nextState, 'endTurnEmptyDraw', drew, { distribute: 'sequential' });
        }
      }
      // Fifth-Finger Ring: if hand has exactly 5 cards, gain armor
      const fiveArmor = Math.max(0, Number(mods?.endTurnFiveCardsArmor ?? 0) || 0);
      if (fiveArmor > 0 && hc === 5) {
        const armorResult = applyPlayerArmorGainWithInvertedHeart(nextState, fiveArmor);
        const overflowLog = formatInvertedHeartArmorOverflowLog(armorResult);
        nextState = {
          ...armorResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            `Relic: 5 cards in hand at turn end, gain +${fiveArmor} Armor.`,
            ...nextState.log,
          ],
        };
        nextState = pushRelicTriggerAmount(nextState, 'endTurnFiveCardsArmor', fiveArmor);
      }
      // Palm Brooch: gain armor per card in hand
      const perArmor = Math.max(0, Number(mods?.endTurnArmorPerCard ?? 0) || 0);
      if (perArmor > 0 && hc > 0) {
        const total = hc * perArmor;
        const armorResult = applyPlayerArmorGainWithInvertedHeart(nextState, total);
        const overflowLog = formatInvertedHeartArmorOverflowLog(armorResult);
        nextState = {
          ...armorResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            `Relic: end of turn, gain +${total} Armor (${perArmor} x ${hc}).`,
            ...nextState.log,
          ],
        };
        nextState = pushRelicTriggerAmount(nextState, 'endTurnArmorPerCard', total, { multiplier: hc });
      }
      // Reset per-turn drawn count for Dealing Die for next turn
      // (Handled in startPlayerTurn)
      const decree = (nextState as any).monsignorDecree;
      if (decree && !decree.broken && decree.id === 'max-three-cards-in-hand' && handCount(nextState) > 3) {
        nextState = breakMonsignorDecree(nextState, 'ended the turn with more than 3 cards in hand');
      }
      if ((nextState as any).darkApostleCursedFateActive && !((nextState as any).playerAttackedThisTurn)) {
        nextState = triggerDarkApostleCursedFate(nextState);
        nextState = settleGameOver(nextState);
        if (nextState.phase === 'reward' || nextState.phase === 'gameover' || isCombatEndPending(nextState)) {
          return nextState;
        }
      }
      const playerTurnDamageToEnemy = Math.max(0, Math.floor(Number((nextState as any).playerTurnDamageToEnemy ?? 0) || 0));
      const next = {
        ...nextState,
        ...enemyActionsAtTurnStart(nextState),
        lastPlayerTurnDamageToEnemy: playerTurnDamageToEnemy,
        playerTurnDamageToEnemy: 0,
        lastPlayerTurnPlays: Math.max(0, Math.floor(Number((nextState as any).playerPlaysThisTurn ?? 0) || 0)),
        lastPlayerTurnBlackCardPlays: Math.max(0, Math.floor(Number((nextState as any).playerBlackCardPlaysThisTurn ?? 0) || 0)),
        lastPlayerTurnMagicChainOverloadSp: Math.max(0, Math.floor(Number((nextState as any).playerMagicChainOverloadSpThisTurn ?? 0) || 0)),
        lastPlayerTurnFocusedOnly: !!((nextState as any).playerFocusedThisTurn),
        lastPlayerTurnAttacked: !!((nextState as any).playerAttackedThisTurn),
        monsignorDecree: null,
        phase: 'enemy',
        nextAttackBonus: 0,
        utilityCostReductionThisTurn: 0,
        relicNextAttackBonus: 0,
        magicChainStreak: 0,
        canPlay: false,
        autoFocusPending: false,
        enemyAwaitingAck: false,
        enemyStartPending: true,
        blackhandShadowstepTurn:
          Number((nextState as any).blackhandShadowstepTurn ?? -1) === Number((nextState as any).turn ?? -999)
            ? null
            : (nextState as any).blackhandShadowstepTurn ?? null,
        blackhandPressurePointTurn:
          Number((nextState as any).blackhandPressurePointTurn ?? -1) === Number((nextState as any).turn ?? -999)
            ? null
            : (nextState as any).blackhandPressurePointTurn ?? null,
        // "This turn" effects end when the player ends their turn.
        healPerDamageDealtThisTurn: 0,
        playerWeakenedTurns: nextWeaken,
        punitiveMeasureTurns: nextPunitive,
        unauthorizedPassageLocks: unauthorizedTick.locks,
        custodianForfeitureTurns: nextForfeiture,
        discardPlayThisTurn: 0,
        log: [
          ...(curWeaken > 0 && nextWeaken === 0 ? ['Debuff ends: your cards are no longer weakened.'] : []),
          ...(curPunitive > 0 && nextPunitive === 0 ? ['Punitive Measure ends.'] : []),
          ...(unauthorizedTick.expiredCount > 0 ? [`Unauthorized Passage ends on ${unauthorizedTick.expiredCount} card(s).`] : []),
          ...(curForfeiture > 0 && nextForfeiture === 0 ? ['Forfeiture ends.'] : []),
          'Enemy turn.',
          ...nextState.log,
        ],
      } as GameState;
      return next;
    }

    case 'RESOLVE_ENEMY_TURN': {
  if (state.phase !== 'enemy') return state;

  // If we're already waiting for an acknowledgement, do nothing.
  if (state.enemyAwaitingAck) return state;

  let currentState = tickDelayedAtEnemyTurnStart(state);
  currentState = settleGameOver(currentState);
  if (currentState.phase === 'reward' || currentState.phase === 'gameover' || isCombatEndPending(currentState)) {
    return currentState;
  }

  // If enemy stunned, skip action
  if (currentState.enemy.stunned > 0) {
    const cleared = {
      ...currentState,
      enemy: { ...currentState.enemy, stunned: Math.max(0, currentState.enemy.stunned - 1) },
      log: ['Enemy is stunned and skips its turn.', ...currentState.log],
    };
    const ticked = tickEnemyDamageDebuffAfterEnemyTurn(cleared);
    return startPlayerTurn(ticked);
  }

  // 1) If we have forced follow-ups queued, use them first (across turns).
  const q: Card[] = currentState.enemyForcedQueue ?? [];
  let forced = false;
  let enemyCard: Card | null = null;
  let enemyForcedQueue: Card[] = q;

  if (q.length > 0) {
    forced = true;
    enemyCard = q[0] ?? null;
    enemyForcedQueue = q.slice(1);
  } else {
    // 2) Otherwise pick the previewed card when available, falling back to a random pick.
    const revealIntent = !!((currentState as any).relicMods as RelicCombatMods | undefined)?.revealEnemyIntentPreview;
    if (revealIntent && (currentState as any).enemyIntentPreview) {
      enemyCard = (currentState as any).enemyIntentPreview as Card;
    }
    if (!enemyCard && shouldForceParalyzingVenomOnEnemyTurn(currentState)) {
      enemyCard = findEnemyCard(currentState.enemyDeck, isParalyzingVenomCard);
    }
    if (!enemyCard) {
      enemyCard = pickEnemyCard(currentState, filterRandomEnemyDeck(currentState, currentState.enemyDeck));
    }
  }

  if (!enemyCard) {
    const noCards = { ...currentState, log: ['Enemy has no cards.', ...currentState.log] };
    return startPlayerTurn(tickEnemyDamageDebuffAfterEnemyTurn(noCards));
  }

  // 3) If the first Charge was random, schedule next two ENEMY TURNS:
  //    next enemy turn: Charge again, following enemy turn: that enemy's finisher.
  //    Examples:
  //    - Cultist: Sprint → Sprint → Power Strike
  //    - Dreadbrand: Sprint → Sprint → Iron Rend
  //    - Hexbinder: Meditation → Meditation → Ritual
  //    We only do this when the first Charge was random to avoid infinite loops.
  if (!forced && isChargeCard(enemyCard) && !hasEnemyAiRouting(currentState.enemyId)) {
    const finisher = findEnemyFinisherCard(currentState.enemyId, currentState.enemyDeck);
    const combo: Card[] = [enemyCard]; // next enemy turn: Charge again
    if (finisher) combo.push(finisher); // following enemy turn: enemy-specific finisher
    enemyForcedQueue = combo;
  }

  const next: GameState = {
    ...currentState,
    enemyForcedQueue,
    enemyIntent: enemyCard,
    enemyIntentPreview: undefined,
    enemyAwaitingAck: true,
    enemyStartPending: false,
    log: [`Enemy plays ${enemyCard.title}.`, ...currentState.log],
  } as GameState;

  // Wait for UI to acknowledge before applying effects.
  return next;
}




    case 'ACK_ENEMY_CARD': {
  if (isCombatEndPending(state)) {
    return finalizeCombatEnd(state);
  }
  if (state.phase !== 'enemy') return state;
  if ((state as any).enemyStartPending && !state.enemyAwaitingAck && !state.enemyIntent) {
    return reducer({ ...state, enemyStartPending: false } as GameState, { type: 'RESOLVE_ENEMY_TURN' });
  }
  if (!state.enemyAwaitingAck || !state.enemyIntent) return state;

  const enemyCard = state.enemyIntent;
  const enemyActionsBeforePlay = Math.max(0, Math.floor(Number((state as any).enemyActionsRemainingThisTurn ?? 1) || 1));

  let next = {
    ...state,
    enemyAwaitingAck: false,
    enemyStartPending: false,
    enemyActionsRemainingThisTurn: enemyActionsBeforePlay,
    discardPlayThisTurn: 0,
    monsignorSpSpentThisTurn: 0,
    monsignorPlayedCardTypesThisTurn: [],
  } as GameState;

  if (state.enemyId === 'nested_horror' && isParalyzingVenomCard(enemyCard)) {
    next = activateParalyzingVenom(next, enemyCard);
  } else if (isMonsignorEnemy(state.enemyId)) {
    next = applyMonsignorEnemyCard(next, enemyCard);
  } else if (isDarkApostleEnemy(state.enemyId)) {
    next = applyDarkApostleEnemyCard(next, enemyCard);
  } else if (isBlackhandEnemy(state.enemyId)) {
    next = applyBlackhandEnemyCard(next, enemyCard);
  } else if (isPrimeVesselEnemy(state.enemyId)) {
    next = applyPrimeVesselEnemyCard(next, enemyCard);
  } else if (isChymistEnemy(state.enemyId)) {
    next = applyChymistEnemyCard(next, enemyCard);
  } else if (isLampKeeperEnemy(state.enemyId)) {
    next = applyLampKeeperEnemyCard(next, enemyCard);
  } else if (isThresholdVerifierEnemy(state.enemyId)) {
    next = applyThresholdVerifierEnemyCard(next, enemyCard);
  } else if (isCustodianEnemy(state.enemyId)) {
    next = applyCustodianEnemyCard(next, enemyCard);
  } else if (isMercenaryEnemy(state.enemyId)) {
    next = applyMercenaryEnemyCard(next, enemyCard);
  } else {
    next = applyCard(next, enemyCard, 'enemy');
  }
  next = applyEnemyCardDelayGroups(next, enemyCard);
  next = recordEnemyAiAction(next, enemyCard);
  next = { ...next, enemyIntent: undefined };

  if (next.pendingChoice && (next.pendingChoice as any).sourceOwner === 'enemy') {
    return {
      ...next,
      pendingEnemyResolution: { consumesAction: true },
      phase: 'enemy',
      enemyAwaitingAck: false,
      enemyStartPending: false,
    } as GameState;
  }

  // If someone died, stop here.
  next = settleGameOver(next);
  if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) return next;

  const consumesEnemyAction = !(isBlackhandEnemy(state.enemyId) && isBlackhandMomentumCard(enemyCard));
  const actionsRemainingRaw = Math.max(0, Math.floor(Number((next as any).enemyActionsRemainingThisTurn ?? enemyActionsBeforePlay) || enemyActionsBeforePlay));
  const actionsRemaining = consumesEnemyAction
    ? Math.max(0, actionsRemainingRaw - 1)
    : actionsRemainingRaw;

  if (actionsRemaining > 0) {
    const enemyContinues = {
      ...next,
      enemyActionsRemainingThisTurn: actionsRemaining,
      phase: 'enemy',
      enemyAwaitingAck: false,
      enemyStartPending: false,
      log: ['The Blackhand acts again.', ...next.log],
    } as GameState;
    return reducer(enemyContinues, { type: 'RESOLVE_ENEMY_TURN' });
  }

  next = { ...next, enemyActionsRemainingThisTurn: 0 } as GameState;
  next = tickEnemyDamageDebuffAfterEnemyTurn(next);
  return startPlayerTurn(next);
}

    case 'SET_REWARD_CHOICES': {
      // UI computes the reward roll from the full library; engine just stores it.
      return { ...state, rewardChoices: [...action.choices] };
    }

    case 'RESTART':
      return { ...initialState(), phase: 'loading' };

    case 'BACK_TO_SETUP':
      {
        const enemyId = 'cultist';
        const enemyDef = getEnemyDef(enemyId);
        return {
        ...state,
        phase: 'setup',
        turn: 0,
        playerPlaysThisTurn: 0,
    playerBlackCardPlaysThisTurn: 0,
        nextAttackBonus: 0,
        relicNextAttackBonus: 0,
        nextAttackIgnoreArmor: 0,
        nextCardCost0: 0,
        utilityCostReductionThisTurn: 0,
        nextMagicCardCostDoubleSP: 0,
        playerWeakenedTurns: 0,
        punitiveMeasureTurns: 0,
        unauthorizedPassageLocks: [],
        playerDamageMultThisTurn: 1,
        playerDamageMultNextTurn: 1,
        playerIncomingDamageMultNextTurn: 1,
        playerMarkedByBlackhand: false,
        playerMarkedTurn: null,
        blackhandShadowstepTurn: null,
        blackhandPressurePointTurn: null,
        enemyActionsRemainingThisTurn: 1,
        enemyBonusActionsNextTurn: 0,
        // Keep dungeon-provided relic mods between combats (the dungeon will overwrite when needed).
        relicMods: (state as any).relicMods,
        player: { name: 'Player', hp: 100, sp: 100, armor: 0, stunned: 0, reflectPct: 0, negateNextDamage: 0 },
        enemyId,
        enemyMaxHpBase: Math.max(1, Math.floor(Number(enemyDef?.hp ?? 1) || 1)),
        enemy: enemyFighterFromDef(enemyDef),
        playerTurnDamageToEnemy: 0,
        lastPlayerTurnDamageToEnemy: 0,
      lastPlayerTurnBlackCardPlays: 0,
        deck: [],
        discard: [],
        exhaust: [],
        physicalDeck: [],
        magicDeck: [],
        physicalDiscard: [],
        magicDiscard: [],
        physicalEscalation: 0,
        magicEscalation: 0,
        magicChainStreak: 0,
        hand: { main: { physical: null, magic: null }, extra: [] },
        enemyIntent: undefined,
        enemyAwaitingAck: false,
        playerAttackedThisTurn: false,
        darkApostleCursedFateActive: false,
        activeCompound: null,
        chymistOpeningCompoundPlayed: false,
        chymistForceNextCompound: false,
        enemyAiCurrentTurnActions: [],
        enemyAiLastTurnActions: [],
        enemyAiCombatHistory: [],
        mercenaryPrecision: false,
        mercenaryAmbush: false,
        lampKeeperRoomLight: undefined,
        lampKeeperDisposition: null,
        lampKeeperRaisedLightThisRound: false,
        lampKeeperLoweredLightThisRound: false,
        custodianForfeitureTurns: 0,
        custodianExiledAttackCount: 0,
        custodianExiledDefenseCount: 0,
        custodianExiledUtilityCount: 0,
        custodianTrackedExiledCards: [],
        enemyOngoingVenom: createInitialEnemyOngoingVenomState(),
        playerTookDamage: false,
        playerTookEnemyDamageThisCombat: false,
        enemyKilledExactlyAtZero: false,
        drawnThisTurn: 0,
        suppressDrawTrackingThisTurn: false,
        reflectOverflowBurstStacks: 0,
        discardPlayThisTurn: 0,
        canPlay: false,
        pendingDiscard: null,
        pendingPeek: null,
        pendingTopPick: null,
        pendingChoice: null,
        pendingEnemyResolution: null,
        pendingTutor: null,
        pendingX: null,
        rewardChoices: [],
        delayed: [],
        pendingPrereqPlay: null,
        log: ['Back to deck builder.', ...state.log],
      };
      }

    default:
      return state;
  }
}

function scheduleDelayed(state: GameState, card: Card, effects: Effect[], turns: number, owner: 'player' | 'enemy' = 'player'): GameState {
  const t = Math.max(0, Number(turns) || 0);
  if (t <= 0 || effects.length === 0) return state;
  const bundle: DelayedBundle = {
    id: `${card.id}-delay-${state.turn}-${Math.random().toString(16).slice(2)}`,
    sourceTitle: card.title,
    sourceKind: card.kind,
    turnsLeft: t,
    effects: effects,
    owner,
  };
  const plural = t === 1 ? '' : 's';
  return {
    ...state,
    delayed: [...state.delayed, bundle],
    log: [`${card.title} delayed: triggers in ${t} turn${plural}.`, ...state.log],
  };
}

function scheduleDelayedCost(state: GameState, card: Card, payHp: number, paySp: number, turns: number): GameState {
  const t = Math.max(0, Number(turns) || 0);
  const hp = Math.max(0, Math.floor(Number(payHp) || 0));
  const sp = Math.max(0, Math.floor(Number(paySp) || 0));
  if (t <= 0 || (hp <= 0 && sp <= 0)) return state;

  const effects: Effect[] = [];
  if (hp > 0) effects.push({ type: 'loseHP', amount: hp } as any);
  if (sp > 0) effects.push({ type: 'loseSP', amount: sp } as any);

  const bundle: DelayedBundle = {
    id: `${card.id}-costdelay-${state.turn}-${Math.random().toString(16).slice(2)}`,
    sourceTitle: card.title,
    sourceKind: card.kind,
    turnsLeft: t,
    effects,
    meta: { type: 'cost', payHp: hp || undefined, paySp: sp || undefined },
  };

  const plural = t === 1 ? '' : 's';
  const parts = [hp > 0 ? `${hp} HP` : '', sp > 0 ? `${sp} SP` : ''].filter(Boolean).join(' + ');
  return {
    ...state,
    delayed: [...state.delayed, bundle],
    log: [`${card.title} cost delayed: pay ${parts} in ${t} turn${plural}.`, ...state.log],
  };
}

function triggerDelayedEffectsNow(state: GameState, card: Card, effects: Effect[], owner: 'player' | 'enemy' = 'player'): GameState {
  if (!effects || effects.length === 0) return state;
  const bundle: DelayedBundle = {
    id: `${card.id}-delay0-${state.turn}-${Math.random().toString(16).slice(2)}`,
    sourceTitle: card.title,
    sourceKind: card.kind,
    turnsLeft: 0,
    effects: effects,
    owner,
  };
  return triggerDelayedBundle(state, bundle);
}


function tickEnemyDamageDebuffAfterEnemyTurn(state: GameState): GameState {
  const turns = Math.max(0, Number((state as any).enemyDamageMultTurns ?? 0) || 0);
  if (turns <= 0) return state;
  const nextTurns = Math.max(0, turns - 1);
  if (nextTurns <= 0) {
    return {
      ...state,
      enemyDamageMultTurns: 0,
      enemyDamageMult: 1,
      log: ['Debuff ends: enemy damage returns to normal.', ...state.log],
    };
  }
  return { ...state, enemyDamageMultTurns: nextTurns };
}

function startPlayerTurn(state: GameState): GameState {
  // Transition to the player's turn, tick delayed effects, then refill hand
  // to ensure at least 1 Physical and 1 Magic card when possible.
  const carryDamageDealtMult = Math.max(1, Number((state as any).playerDamageMultNextTurn ?? 1) || 1);
  const stateWithEnemyAiHistory = rotateEnemyAiTurnHistory(state);
  let next = {
    ...stateWithEnemyAiHistory,
    phase: 'player',
    turn: state.turn + 1,
    playerPlaysThisTurn: 0,
    playerBlackCardPlaysThisTurn: 0,
    // Per-turn buffs that should not persist across turns.
    healPerDamageDealtThisTurn: 0,
    playerAttackedThisTurn: false,
    utilityCostReductionThisTurn: 0,
    nextAttackBonus: Math.max(0, Math.floor(Number((((state as any).relicMods as RelicCombatMods | undefined)?.turnStartAttackBonus ?? 0)) || 0)),
    relicNextAttackBonus: Math.max(0, Math.floor(Number((((state as any).relicMods as RelicCombatMods | undefined)?.turnStartAttackBonus ?? 0)) || 0)),

    // Apply any "next turn" damage-dealt multipliers at the start of the new player turn.
    playerDamageMultThisTurn: carryDamageDealtMult,
    // Consume the "next turn" multiplier once it's applied.
    playerDamageMultNextTurn: 1,
    // "Next turn" penalties are consumed when we move back into the player's turn.
    playerIncomingDamageMultNextTurn: 1,
    magicChainStreak: 0,
    canPlay: true,
    enemyIntent: undefined,
    enemyIntentPreview: undefined,
    enemyAwaitingAck: false,
    enemyStartPending: false,
    discardPlayThisTurn: 0,
    monsignorSpSpentThisTurn: 0,
    monsignorPlayedCardTypesThisTurn: [],
    playerMagicChainOverloadSpThisTurn: 0,
    playerFocusedThisTurn: false,
    lampKeeperRaisedLightThisRound: false,
    lampKeeperLoweredLightThisRound: false,
  } as GameState;
  (next as any).lastNonEchoPlayThisTurn = null;



  // Reset per-turn draw counter for relics like Dealing Die.
  next = { ...next, drawnThisTurn: 0, suppressDrawTrackingThisTurn: false };

  if ((next as any).enemyOngoingVenom?.active) {
    next = resolveEnemyOngoingVenomTick(next, NESTED_HORROR_PARALYZING_VENOM.title);
    next = settleGameOver(next);
    if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) return next;
  }
  next = applyChymistCompoundTurnStart(next);
  next = settleGameOver(next);
  if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) return next;
  if ((next.nextAttackBonus || 0) > 0) {
  next = pushRelicTriggerAmount(next, 'turnStartAttackBonus', next.nextAttackBonus || 0);
}

  // Relic: flat Armor gain at the start of each player turn.
  const tsa = Math.max(0, Math.floor(Number((((next as any).relicMods as RelicCombatMods | undefined)?.turnStartArmor ?? 0)) || 0));
  if (tsa > 0) {
    const armorResult = applyPlayerArmorGainWithInvertedHeart(next, tsa);
    const overflowLog = formatInvertedHeartArmorOverflowLog(armorResult);
    next = {
      ...armorResult.state,
      log: [
        ...(overflowLog ? [overflowLog] : []),
        `Relic: gain +${tsa} Armor at turn start.`,
        ...next.log,
      ],
    };
    next = pushRelicTriggerAmount(next, 'turnStartArmor', tsa);
  }

  next = tickDelayedAtPlayerTurnStart(next);
  next = settleGameOver(next);
  if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) return next;

  // Player draws 1 random card at the start of each turn (max hand size 5).
  {
    const before = handCount(next);
    next = drawRandomExtraFromPiles(next, 'any', 1);
    const drew = Math.max(0, handCount(next) - before);
    if (drew > 0) {
      next = { ...next, log: [`Drew ${drew} card(s).`, ...next.log] };
    }
  }

  if ((next.player?.stunned ?? 0) > 0) {
    next = {
      ...next,
      log: ['Player is stunned: cards are disabled and only Focus is available.', ...next.log],
    };
  }

  if (((next as any).relicMods as RelicCombatMods | undefined)?.revealEnemyIntentPreview) {
    next = { ...next, enemyIntentPreview: predictEnemyIntentCard(next) };
  }

  return settleGameOver(next);
}


function tickDelayedAtTurnStartForOwner(state: GameState, owner: 'player' | 'enemy'): GameState {
  if (!state.delayed || state.delayed.length === 0) return state;
  let next: GameState = state;
  const remaining: DelayedBundle[] = [];

  for (const b of state.delayed) {
    const bundleOwner = (b.owner ?? 'player') === 'enemy' ? 'enemy' : 'player';
    if (bundleOwner !== owner) {
      remaining.push(b);
      continue;
    }

    const tl = (Number(b.turnsLeft) || 0) - 1;
    if (tl > 0) {
      remaining.push({ ...b, turnsLeft: tl });
      continue;
    }
    next = triggerDelayedBundle(next, b);
  }

  return { ...next, delayed: remaining };
}

function tickDelayedAtPlayerTurnStart(state: GameState): GameState {
  return tickDelayedAtTurnStartForOwner(state, 'player');
}

function tickDelayedAtEnemyTurnStart(state: GameState): GameState {
  return tickDelayedAtTurnStartForOwner(state, 'enemy');
}

function triggerDelayedBundle(state: GameState, bundle: DelayedBundle): GameState {
  const isCost = (bundle as any)?.meta?.type === 'cost';
  const owner = (bundle.owner ?? 'player') === 'enemy' ? 'enemy' : 'player';
  let next: GameState = {
    ...state,
    log: [isCost ? `Delayed cost triggers: ${bundle.sourceTitle}.` : `Delayed effects trigger: ${bundle.sourceTitle}.`, ...state.log],
  };

  for (const eff of bundle.effects) {
    // Draw is handled in the engine because it needs deck context and should obey hand cap.
    if (eff.type === 'draw' || eff.type === 'drawRandom' || eff.type === 'drawRandomPhysical' || eff.type === 'drawRandomMagic') {
      if (owner === 'enemy') {
        next = { ...next, log: ['Enemy delayed draw is ignored.', ...next.log] };
        continue;
      }

      const before = handCount(next);
      if (eff.type === 'draw') {
        if (bundle.sourceKind === 'physical' || bundle.sourceKind === 'magic') {
          next = drawExtraFromDeck(next, bundle.sourceKind, eff.amount);
        }
      } else if (eff.type === 'drawRandomPhysical') {
        next = drawRandomExtraFromPiles(next, 'physical', eff.amount);
      } else if (eff.type === 'drawRandomMagic') {
        next = drawRandomExtraFromPiles(next, 'magic', eff.amount);
      } else {
        next = drawRandomExtraFromPiles(next, 'any', eff.amount);
      }
      const drew = Math.max(0, handCount(next) - before);
      if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s) (delayed).`, ...next.log] };
      continue;
    }

    // Discard is treated as a cost and is never delayed.
    if (eff.type === 'discard') continue;

    next = applyEffect(next, eff, owner);
  }

  // If this delayed bundle represents a deferred COST payment, apply the Zero Key rule
  // (gain SP when SP becomes exactly 0 after paying card costs).
  if (isCost && owner === 'player') {
    next = maybeTriggerZeroKey(state, next);
  }

  return next;
}

// --- Hasten the Inevitable helpers ---
// Halve numeric magnitudes for effects. Used when force-resolving delayed bundles at reduced strength.
function halfNum(n: any): number {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  return Math.floor(v / 2);
}

function halfEffect(eff: Effect): Effect {
  if (!eff || typeof eff !== 'object') return eff;

  // Conditional effects
  if ((eff as any).type === 'ifOpponentHasStun') {
    return {
      ...(eff as any),
      then: Array.isArray((eff as any).then) ? (eff as any).then.map(halfEffect) : [],
      else: Array.isArray((eff as any).else) ? (eff as any).else.map(halfEffect) : [],
    } as any;
  }

  // Choice effects (rare/improbable in delayed bundles, but keep safe)
  if ((eff as any).type === 'chooseOne') {
    const opts = Array.isArray((eff as any).options) ? (eff as any).options : [];
    return {
      type: 'chooseOne',
      options: opts.map((o: any) => ({
        ...o,
        effects: Array.isArray(o?.effects) ? o.effects.map(halfEffect) : [],
      })),
    } as any;
  }

  // Atomic effects
  switch ((eff as any).type) {
    case 'damage':
    case 'block':
    case 'healHP':
    case 'healSP':
    case 'loseHP':
    case 'loseSP':
      return { ...(eff as any), amount: halfNum((eff as any).amount) } as any;

    case 'setHP':
    case 'setSP':
    case 'setArmor':
    case 'setOpponentArmor':
      return { ...(eff as any), value: halfNum((eff as any).value) } as any;

    case 'stun':
    case 'weakenPlayerCards':
      return { ...(eff as any), turns: halfNum((eff as any).turns) } as any;

    case 'reflect':
      return { ...(eff as any), percent: halfNum((eff as any).percent) } as any;

    case 'nextAttackBonus':
      return { ...(eff as any), amount: halfNum((eff as any).amount) } as any;

    case 'draw':
    case 'drawRandom':
    case 'drawRandomPhysical':
    case 'drawRandomMagic':
      return { ...(eff as any), amount: halfNum((eff as any).amount) } as any;

    case 'drawFromDeck':
      return { ...(eff as any), amount: halfNum((eff as any).amount) } as any;

    case 'peekReorderPhysical':
    case 'peekReorderMagic':
      return { ...(eff as any), count: halfNum((eff as any).count) } as any;

    case 'discard':
      return { ...(eff as any), amount: halfNum((eff as any).amount) } as any;

    case 'blockPerPlaysThisTurn':
    case 'healHPPerPlaysThisTurn':
    case 'healSPPerPlaysThisTurn':
      return { ...(eff as any), amountPer: halfNum((eff as any).amountPer) } as any;

    case 'gainMaxHpThisCombat':
      return { ...(eff as any), amount: halfNum((eff as any).amount) } as any;

    case 'healOnDamageDealtThisTurn':
      return { ...(eff as any), amountPerDamage: halfNum((eff as any).amountPerDamage) } as any;

    default:
      return eff;
  }
}

function resolveAllDelayedBundlesHalved(state: GameState): GameState {
  const bundles = Array.isArray(state.delayed) ? state.delayed : [];
  if (bundles.length === 0) {
    return { ...state, log: ['No Delay stacks to resolve.', ...state.log] };
  }

  // Clear the queue first so nothing double-triggers.
  let next: GameState = { ...state, delayed: [], log: ['Resolve all Delay stacks (halved).', ...state.log] };

  for (const b of bundles) {
    const isCost = (b as any)?.meta?.type === 'cost';
    const scaledEffects = Array.isArray(b.effects) ? b.effects.map(halfEffect) : [];
    const scaledMeta = isCost
      ? {
          type: 'cost',
          payHp: (b as any)?.meta?.payHp != null ? halfNum((b as any).meta.payHp) || undefined : undefined,
          paySp: (b as any)?.meta?.paySp != null ? halfNum((b as any).meta.paySp) || undefined : undefined,
        }
      : undefined;

    const scaledBundle: DelayedBundle = {
      ...b,
      turnsLeft: 0,
      effects: scaledEffects,
      meta: scaledMeta as any,
    };

    next = {
      ...next,
      log: [`Hastened: ${b.sourceTitle} (half strength).`, ...next.log],
    };
    next = triggerDelayedBundle(next, scaledBundle);
    next = settleGameOver(next);
    if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) return next;
  }

  return next;
}

function applyCard(state: GameState, card: Card, by: 'player' | 'enemy'): GameState {
  let next = state;
  for (const eff of card.effects) {
    next = applyEffect(next, eff, by);
  }
  return next;
}


function applyEffect(state: GameState, eff: Effect, by: 'player' | 'enemy'): GameState {
  const src = by === 'player' ? state.player : state.enemy;
  const tgt = by === 'player' ? state.enemy : state.player;

  switch (eff.type) {
    case 'damageIgnoreArmor': {
      // Player-only: deal damage directly to enemy HP, bypassing Armor.
      if (by !== 'player') {
        // Fallback: treat as normal damage for non-player sources.
        return applyEffect(state, { type: 'damage', amount: (eff as any).amount } as any, by);
      }
      const mult = Math.max(1, Number((state as any).playerDamageMultThisTurn ?? 1) || 1);
      const rawBase = Math.max(0, Math.floor((eff as any).amount ?? 0));
      const base = applyPlayerWeaken(state, by, rawBase);
      const dealt = base * mult;

      return applyIncomingPlayerDamage(state, dealt, {
        sourceTitle: src.name,
        ignoreArmor: true,
      });
    }

    case 'damage': {
      // Player -> Enemy: standard armor-first damage
      if (by === 'player') {
        const mult = Math.max(1, Number((state as any).playerDamageMultThisTurn ?? 1) || 1);
        const rawBase = Math.max(0, Math.floor((eff as any).amount ?? 0));
        const base = applyPlayerWeaken(state, by, rawBase);
        const dealt = base * mult;
        const suffix = mult !== 1 ? ` (x${mult})` : '';
        return applyIncomingPlayerDamage(state, dealt, {
          sourceTitle: `${src.name}${suffix}`,
        });
      }

      // Enemy -> Player: support "Reflect X% of the next damage you would take".
      const inMult = Math.max(1, Number((state as any).playerIncomingDamageMultNextTurn ?? 1) || 1);
      let incoming = Math.max(0, Math.floor((eff as any).amount ?? 0)) * inMult;
      const dmgTurns = Math.max(0, Number((state as any).enemyDamageMultTurns ?? 0) || 0);
      const dmgMult = dmgTurns > 0 ? Math.max(0, Number((state as any).enemyDamageMult ?? 1) || 1) : 1;
      if (dmgMult !== 1) {
        incoming = Math.max(0, Math.round(incoming * dmgMult));
      }

      if (getPrimeVesselCombatFlags(state).primeCrucibleActive) {
        incoming = Math.max(0, Math.round(incoming * 1.3));
      }
      if (getActiveChymistCompound(state) === 'catalyst') {
        incoming = Math.max(0, Math.round(incoming * 1.3));
      }

      return applyIncomingEnemyDamage(state, incoming, { sourceTitle: src.name });
    }

    case 'damageEqualToArmor': {
      const amt = Math.max(0, Math.floor(src.armor || 0));
      return applyEffect(state, { type: 'damage', amount: amt } as any, by);
    }

    case 'damageEqualToReflect': {
      const amt = Math.max(0, Math.floor(src.reflectPct || 0));
      return applyEffect(state, { type: 'damage', amount: amt } as any, by);
    }

    case 'convertArmorToSp': {
      if (by !== 'player') return state;
      const armorAmt = Math.max(0, Math.floor(Number(src.armor) || 0));
      const nextSp = clampPlayerSp(state, (state.player.sp || 0) + armorAmt);
      const gained = Math.max(0, nextSp - (state.player.sp || 0));
      return {
        ...state,
        player: { ...state.player, armor: 0, sp: nextSp },
        log: [`Confidence: convert ${armorAmt} Armor into ${gained} SP.`, ...state.log],
      };
    }

    case 'convertReflectToHp': {
      if (by !== 'player') return state;
      const reflectAmt = Math.max(0, Math.floor(Number(src.reflectPct) || 0));
      const baseState = {
        ...state,
        player: { ...state.player, reflectPct: 0 },
      } as GameState;
      const healResult = applyPlayerHealingWithInvertedHeart(baseState, reflectAmt);
      const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
      return {
        ...healResult.state,
        log: [
          ...(overflowLog ? [overflowLog] : []),
          `Confidence: convert ${reflectAmt}% Reflect into ${healResult.gainedHp} HP.`,
          ...state.log,
        ],
      };
    }

    case 'setHP': {
      const val = Math.max(0, Math.floor((eff as any).value ?? 0));
      const nextSelf = { ...src, hp: (by === 'player' ? clampPlayerHp(state, val) : clampEnemyHp(state, val)) };

      const msg = `${src.name} sets HP to ${val}.`;
      return by === 'player'
        ? { ...state, player: nextSelf, log: [msg, ...state.log] }
        : { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

    case 'setSP': {
      const val = Math.max(0, Math.floor((eff as any).value ?? 0));
      const nextSelf = { ...src, sp: (by === 'player' ? clampPlayerSp(state, val) : clamp100(val)) };

      const msg = `${src.name} sets SP to ${val}.`;
      if (by === 'player') {
        return maybeTriggerZeroKey(state, { ...state, player: nextSelf, log: [msg, ...state.log] });
      }
      return { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

    case 'doubleArmor': {
      const doubled = Math.max(0, Math.floor((src.armor || 0) * 2));
      const nextVal = by === 'player' ? clampPlayerArmor(state, doubled) : doubled;
      const nextSelf = { ...src, armor: nextVal };
      const msg = `${src.name} doubles Armor.`;
      let nextState = by === 'player'
        ? ({ ...state, player: nextSelf, log: [msg, ...state.log] } as GameState)
        : ({ ...state, enemy: nextSelf, log: [msg, ...state.log] } as GameState);
      const decree = (nextState as any).monsignorDecree;
      if (by === 'player' && nextVal > (src.armor || 0) && decree && !decree.broken && decree.id === 'no-armor-gain' && nextState.phase === 'player') {
        nextState = breakMonsignorDecree(nextState, 'gained Armor');
      }
      return nextState;
    }

    case 'setArmor': {
      const raw = Math.max(0, Math.floor((eff as any).value ?? 0));
const val = by === 'player' ? clampPlayerArmor(state, raw) : raw;
const nextSelf = { ...src, armor: val };
const msg = `${src.name} sets Armor to ${val}.`;
      return by === 'player'
        ? { ...state, player: nextSelf, log: [msg, ...state.log] }
        : { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

    case 'setOpponentArmor': {
      const raw = Math.max(0, Math.floor((eff as any).value ?? 0));
      // If the opponent is the player, clamp to player armor limits.
      const val = by === 'enemy' ? clampPlayerArmor(state, raw) : raw;
      const nextTgt = { ...tgt, armor: val };
      const msg = `${src.name} sets ${tgt.name}'s Armor to ${val}.`;
      return by === 'player'
        ? { ...state, enemy: nextTgt, log: [msg, ...state.log] }
        : { ...state, player: nextTgt, log: [msg, ...state.log] };
    }

    case 'nextCardCost0': {
      // Player-only mechanic.
      if (by !== 'player') return state;
      const nextCardCost0 = Math.max(0, Number((state as any).nextCardCost0 ?? 0) || 0) + 1;
      return pushStatusPulse({ ...state, nextCardCost0, log: ['Next card costs 0.', ...state.log] }, 'free');
    }

    case 'nextMagicCardCostDoubleSP': {
      // Enemy-only debuff in the current card set.
      if (by !== 'enemy') return state;
      const nextMagicCardCostDoubleSP = Math.max(0, Number((state as any).nextMagicCardCostDoubleSP ?? 0) || 0) + 1;
      return pushStatusPulse({
        ...state,
        nextMagicCardCostDoubleSP,
        log: ['Debuff: your next Magic card costs double SP.', ...state.log],
      }, 'magicDouble');
    }

    case 'weakenPlayerCards': {
      // Enemy-only debuff in the current card set.
      if (by !== 'enemy') return state;
      const turns = Math.max(1, Math.floor(Number((eff as any).turns ?? 0) || 0));
      const cur = Math.max(0, Number((state as any).playerWeakenedTurns ?? 0) || 0);
      const playerWeakenedTurns = Math.max(cur, turns);
      return pushStatusPulse({
        ...state,
        playerWeakenedTurns,
        log: [`Debuff: your cards are weakened for ${turns} turn(s).`, ...state.log],
      }, 'weaken');
    }

case 'weakenEnemyDamage': {
  // Player-applied debuff: reduce the enemy's outgoing damage for N enemy turns.
  if (by !== 'player') return state;
  const turns = Math.max(1, Math.floor(Number((eff as any).turns ?? 0) || 0));
  const mult = Math.max(0, Math.min(1, Number((eff as any).mult ?? 1) || 1));

  const curTurns = Math.max(0, Number((state as any).enemyDamageMultTurns ?? 0) || 0);
  const curMult = curTurns > 0 ? Math.max(0, Number((state as any).enemyDamageMult ?? 1) || 1) : 1;

  const nextTurns = Math.max(curTurns, turns);
  const nextMult = curTurns > 0 ? (curMult * mult) : mult;

  return pushStatusPulse({
    ...state,
    enemyDamageMultTurns: nextTurns,
    enemyDamageMult: nextMult,
    log: [`Enemy damage reduced (x${nextMult}) for ${nextTurns} turn(s).`, ...state.log],
  }, 'enemyWeaken');
}

    case 'doubleDamageDealtThisTurn': {
      // Player-only mechanic.
      if (by !== 'player') return state;
      const cur = Math.max(1, Number((state as any).playerDamageMultThisTurn ?? 1) || 1);
      const nextMult = cur * 2;
      return pushStatusPulse({ ...state, playerDamageMultThisTurn: nextMult, log: [`Damage dealt this turn x${nextMult}.`, ...state.log] }, 'dmgUp');
    }

    case 'doubleDamageDealtNextTurn': {
      // Player-only mechanic.
      if (by !== 'player') return state;
      const cur = Math.max(1, Number((state as any).playerDamageMultNextTurn ?? 1) || 1);
      const nextMult = cur * 2;
      return { ...state, playerDamageMultNextTurn: nextMult, log: [`Damage dealt next turn x${nextMult}.`, ...state.log] };
    }

    case 'doubleDamageTakenNextTurn': {
      // Player-only mechanic.
      if (by !== 'player') return state;
      const cur = Math.max(1, Number((state as any).playerIncomingDamageMultNextTurn ?? 1) || 1);
      const nextMult = cur * 2;
      return pushStatusPulse({ ...state, playerIncomingDamageMultNextTurn: nextMult, log: [`Damage taken next enemy turn x${nextMult}.`, ...state.log] }, 'dmgDown');
    }

    case 'doubleHP': {
      const nextSelf = { ...src, hp: (by === 'player' ? clampPlayerHp(state, (src.hp || 0) * 2) : clampEnemyHp(state, (src.hp || 0) * 2)) };

      const msg = `${src.name} doubles HP.`;
      return by === 'player'
        ? { ...state, player: nextSelf, log: [msg, ...state.log] }
        : { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

    case 'doubleSP': {
      const nextSelf = { ...src, sp: (by === 'player' ? clampPlayerSp(state, (src.sp || 0) * 2) : clamp100((src.sp || 0) * 2)) };

      const msg = `${src.name} doubles SP.`;
      return by === 'player'
        ? { ...state, player: nextSelf, log: [msg, ...state.log] }
        : { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

    case 'frenzy': {
      // Player-only: deal damage equal to current HP, then lose half of HP and SP.
      if (by !== 'player') return state;
      const dmg = Math.max(0, Math.floor(state.player.hp || 0));
      let next = applyEffect(state, { type: 'damage', amount: dmg } as any, by);
      const hpNow = Math.max(0, Math.floor(next.player.hp || 0));
      const spNow = Math.max(0, Math.floor(next.player.sp || 0));
      const hpAfter = Math.max(0, Math.ceil(hpNow / 2));
      const spAfter = Math.max(0, Math.ceil(spNow / 2));
      next = { ...next, player: { ...next.player, hp: hpAfter, sp: spAfter }, log: [`Frenzy backlash: HP→${hpAfter}, SP→${spAfter}.`, ...next.log] };
      return next;
    }

    case 'die': {
      // Player-only in current card set. Triggered via delayed bundles.
      if (by !== 'player') return state;
      const nextPlayer = { ...state.player, hp: 0 };
      return { ...state, player: nextPlayer, log: ['You die.', ...state.log] };
    }

    case 'block': {
      let amt = applyPlayerWeaken(state, by, (eff as any).amount);
      let relicBonusArmor = 0;
      // Double armor gain from cards if the player has the relevant relic.
      if (by === 'player') {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        if (mods?.doubleArmorGainFromCards) {
          relicBonusArmor = amt;
          amt *= 2;
        }
      }
      let nextState: GameState;
      if (by === 'player') {
        const armorResult = applyPlayerArmorGainWithInvertedHeart(state, amt);
        const overflowLog = formatInvertedHeartArmorOverflowLog(armorResult);
        nextState = {
          ...armorResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            `${src.name} gains ${amt} armor.`,
            ...state.log,
          ],
        } as GameState;
      } else {
        const nextArmor = src.armor + amt;
        const nextSelf = { ...src, armor: nextArmor };
        nextState = { ...state, enemy: nextSelf, log: [`${src.name} gains ${amt} armor.`, ...state.log] } as GameState;
      }
      if (by === 'player' && relicBonusArmor > 0) {
        nextState = pushRelicTriggerAmount(nextState, 'doubleArmorGainFromCards', relicBonusArmor);
      }
      const decree = (nextState as any).monsignorDecree;
      if (by === 'player' && amt > 0 && decree && !decree.broken && decree.id === 'no-armor-gain' && nextState.phase === 'player') {
        nextState = breakMonsignorDecree(nextState, 'gained Armor');
      }
      return nextState;
    }

    case 'blockPerPlaysThisTurn': {
      // Player-only mechanic for now.
      const plays = Math.max(0, Number(state.playerPlaysThisTurn ?? 0) || 0);
      const per = Math.max(0, Number(eff.amountPer) || 0);
      const totalRaw = plays * per;
      let total = applyPlayerWeaken(state, by, totalRaw);
      let relicBonusArmor = 0;
      // Double armor gain from cards if the player has the relevant relic.
      if (by === 'player') {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        if (mods?.doubleArmorGainFromCards) {
          relicBonusArmor = total;
          total *= 2;
        }
      }
      const msg = `${src.name} gains ${total} armor (${per} x ${plays} plays).`;
      let nextState: GameState;
      if (by === 'player') {
        const armorResult = applyPlayerArmorGainWithInvertedHeart(state, total);
        const overflowLog = formatInvertedHeartArmorOverflowLog(armorResult);
        nextState = {
          ...armorResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            msg,
            ...state.log,
          ],
        } as GameState;
      } else {
        const nextArmor = src.armor + total;
        const nextSelf = { ...src, armor: nextArmor };
        nextState = { ...state, enemy: nextSelf, log: [msg, ...state.log] } as GameState;
      }
      if (by === 'player' && relicBonusArmor > 0) {
        nextState = pushRelicTriggerAmount(nextState, 'doubleArmorGainFromCards', relicBonusArmor);
      }
      const decree = (nextState as any).monsignorDecree;
      if (by === 'player' && total > 0 && decree && !decree.broken && decree.id === 'no-armor-gain' && nextState.phase === 'player') {
        nextState = breakMonsignorDecree(nextState, 'gained Armor');
      }
      return nextState;
    }

    case 'healHPPerPlaysThisTurn': {
      // Player-only mechanic for now.
      const plays = Math.max(0, Number(state.playerPlaysThisTurn ?? 0) || 0);
      const per = Math.max(0, Number(eff.amountPer) || 0);
      const totalRaw = plays * per;
      const total = applyPlayerWeaken(state, by, totalRaw);
      if (by === 'player') {
        const healResult = applyPlayerHealingWithInvertedHeart(state, total);
        const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
        const msg = `${src.name} gains ${total} HP (${per} x ${plays} plays).`;
        return {
          ...healResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            msg,
            ...state.log,
          ],
        };
      }

      const nextSelf = { ...src, hp: clampEnemyHp(state, (src.hp || 0) + total) };
      const msg = `${src.name} gains ${total} HP (${per} x ${plays} plays).`;
      return { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

    
    case 'healSPPerPlaysThisTurn': {
      // Player-only mechanic for now.
      const plays = Math.max(0, Number(state.playerPlaysThisTurn ?? 0) || 0);
      const per = Math.max(0, Number(eff.amountPer) || 0);
      const totalRaw = plays * per;
      const total = applyPlayerWeaken(state, by, totalRaw);
      const nextSelf = { ...src, sp: (by === 'player' ? clampPlayerSp(state, (src.sp || 0) + total) : clamp100((src.sp || 0) + total)) };

      const msg = `${src.name} gains ${total} SP (${per} x ${plays} plays).`;
      return by === 'player'
        ? { ...state, player: nextSelf, log: [msg, ...state.log] }
        : { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

case 'healHP': {
      const amt = applyPlayerWeaken(state, by, (eff as any).amount);
      if (by === 'player') {
        const healResult = applyPlayerHealingWithInvertedHeart(state, amt);
        const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
        return {
          ...healResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            `${src.name} heals ${amt} HP.`,
            ...state.log,
          ],
        };
      }

      const nextSelf = { ...src, hp: clampEnemyHp(state, (src.hp || 0) + amt) };
      return { ...state, enemy: nextSelf, log: [`${src.name} heals ${amt} HP.`, ...state.log] };
    }

    case 'healSP': {
      const amt = applyPlayerWeaken(state, by, (eff as any).amount);
      const nextSelf = { ...src, sp: (by === 'player' ? clampPlayerSp(state, (src.sp || 0) + amt) : clamp100((src.sp || 0) + amt)) };

      return by === 'player'
        ? { ...state, player: nextSelf, log: [`${src.name} gains ${eff.amount} SP.`, ...state.log] }
        : { ...state, enemy: nextSelf, log: [`${src.name} gains ${eff.amount} SP.`, ...state.log] };
    }

    case 'gainMaxHpThisCombat': {
      // Player-only: increase Max HP cap for the rest of this combat.
      if (by !== 'player') return state;
      const add = Math.max(0, Math.floor(Number(applyPlayerWeaken(state, by, (eff as any).amount)) || 0));
      if (add <= 0) return state;

      const prevBonus = Math.max(0, Math.floor(Number((state as any).tempMaxHpBonus ?? 0) || 0));
      const nextBonus = prevBonus + add;
      const baseCap = Math.floor(Number((state as any).relicMods?.capHp ?? 100) || 100);
      const newCap = Math.max(0, baseCap + nextBonus);

      const nextHp = Math.max(0, Math.min(newCap, (state.player.hp || 0) + add));

      return {
        ...state,
        tempMaxHpBonus: nextBonus,
        player: { ...state.player, hp: nextHp },
        log: [`Max HP +${add} (this combat).`, ...state.log],
      };
    }

    case 'healOnDamageDealtThisTurn': {
      // Player-only: set a per-turn "lifesteal" rate.
      if (by !== 'player') return state;
      const per = Math.max(0, Math.floor(Number((eff as any).amountPerDamage ?? 0) || 0));
      if (per <= 0) return state;
      const cur = Math.max(0, Math.floor(Number((state as any).healPerDamageDealtThisTurn ?? 0) || 0));
      const nextPer = cur + per;
      return {
        ...state,
        healPerDamageDealtThisTurn: nextPer,
        log: [`This turn: heal ${nextPer} HP per damage dealt.`, ...state.log],
      };
    }

    case 'reduceUtilityCardCostsThisTurn': {
      if (by !== 'player') return state;
      const add = Math.max(0, Math.floor(Number((eff as any).amount ?? 0) || 0));
      if (add <= 0) return state;
      const cur = Math.max(0, Math.floor(Number((state as any).utilityCostReductionThisTurn ?? 0) || 0));
      const nextAmt = cur + add;
      return {
        ...state,
        utilityCostReductionThisTurn: nextAmt,
        log: [`This turn: Utility cards cost ${nextAmt} less.`, ...state.log],
      };
    }

    case 'resolveAllDelayHalved': {
      // Player-only mechanic (Hasten the Inevitable).
      if (by !== 'player') return state;
      return resolveAllDelayedBundlesHalved(state);
    }
case 'loseHP': {
  const before = Math.max(0, Number(src.hp || 0) || 0);
  const amt = Math.max(0, Math.floor(Number((eff as any).amount) || 0));
  const after = Math.max(0, before - amt);
  const lost = Math.max(0, before - after);
  const nextSelf = { ...src, hp: after };
  return by === 'player'
    ? { ...state, player: nextSelf, log: [`${src.name} loses ${lost} HP.`, ...state.log] }
    : { ...state, enemy: nextSelf, log: [`${src.name} loses ${lost} HP.`, ...state.log] };
}

case 'loseSP': {
  const before = Math.max(0, Number(src.sp || 0) || 0);
  const amt = Math.max(0, Math.floor(Number((eff as any).amount) || 0));
  const after = Math.max(0, before - amt);
  const lost = Math.max(0, before - after);
  const nextSelf = { ...src, sp: after };
  if (by === 'player') {
    return maybeTriggerZeroKey(state, { ...state, player: nextSelf, log: [`${src.name} loses ${lost} SP.`, ...state.log] });
  }
  return { ...state, enemy: nextSelf, log: [`${src.name} loses ${lost} SP.`, ...state.log] };
}

    case 'xTrade': {
      // Resolved via pendingX in the PLAY_CARD flow.
      return state;
    }

    case 'ifOpponentHasStun': {
      const opp = by === 'player' ? state.enemy : state.player;
      const branch = (opp.stunned || 0) > 0 ? (eff as any).then : (eff as any).else;
      let next = state;
      for (const e of (branch ?? [])) {
        next = applyEffect(next, e as any, by);
      }
      return next;
    }



    case 'nextAttackIgnoreArmor': {
      if (by !== 'player') return state;
      const add = Math.max(1, Math.floor(Number((eff as any).count ?? 1) || 1));
      const cur = Math.max(0, Math.floor(Number((state as any).nextAttackIgnoreArmor ?? 0) || 0));
      const nextCount = cur + add;
      return { ...state, nextAttackIgnoreArmor: nextCount, log: [`Next Attack ignores Armor (${nextCount}).`, ...state.log] };
    }

    case 'reduceAllDelay': {
      if (by !== 'player') return state;
      const amt = Math.max(1, Math.floor(Number((eff as any).amount ?? 1) || 1));
      const bundles = Array.isArray(state.delayed) ? state.delayed : [];
      if (bundles.length === 0) return { ...state, log: [`No Delay stacks to reduce.`, ...state.log] };

      let next: GameState = { ...state, log: [`Reduce all Delay stacks by ${amt}.`, ...state.log] };
      const remaining: DelayedBundle[] = [];
      for (const b of bundles) {
        const tl = (Number(b.turnsLeft) || 0) - amt;
        if (tl > 0) {
          remaining.push({ ...b, turnsLeft: tl });
          continue;
        }
        // Trigger immediately
        next = triggerDelayedBundle(next, { ...b, turnsLeft: 0 });
        next = settleGameOver(next);
        if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) break;
      }
      return { ...next, delayed: remaining };
    }

    case 'nextAttackBonus': {
      // Only meaningful for the player.
      if (by !== 'player') return state;
      const amt = applyPlayerWeaken(state, by, (eff as any).amount);
      return pushStatusPulse({ ...state, nextAttackBonus: amt, relicNextAttackBonus: 0, log: [`Next Attack bonus: +${amt} damage.`, ...state.log] }, 'atkBonus');
    }

    case 'reflect': {
  const cap = by === 'player' ? capHp(state) : 500;
  const rawAddPct = Math.max(0, Math.floor(Number(eff.percent) || 0));
  const currentPct = Math.max(0, Math.min(cap, Math.floor(Number(src.reflectPct) || 0)));
  const totalPct = Math.max(0, Math.min(cap, currentPct + rawAddPct));
  const overflow = Math.max(0, currentPct + rawAddPct - cap);

  const nextSelf = { ...src, reflectPct: totalPct };

  if (by === 'player') {
    let nextState = pushStatusPulse({ ...state, player: nextSelf, log: [`Reflect shield ready: ${totalPct}% (next hit).`, ...state.log] }, 'reflect');
    const burstStacks = Math.max(0, Math.floor(Number((state as any).reflectOverflowBurstStacks ?? 0) || 0));
    if (overflow > 0 && burstStacks > 0) {
      const burstDamage = overflow * burstStacks;
      nextState = {
        ...nextState,
        log: [`Overflow Burst: ${overflow}% excess Reflect becomes ${burstDamage} damage.`, ...nextState.log],
      };
      nextState = applyEffect(nextState, { type: 'damage', amount: burstDamage } as any, 'player');
    }
    return nextState;
  }

  return { ...state, enemy: nextSelf, log: [`Reflect shield ready: ${totalPct}% (next hit).`, ...state.log] };
}

    case 'enableReflectOverflowBurst': {
      if (by !== 'player') return state;
      const stacks = Math.max(0, Math.floor(Number((state as any).reflectOverflowBurstStacks ?? 0) || 0)) + 1;
      return {
        ...state,
        reflectOverflowBurstStacks: stacks,
        log: [`Overflow Burst active${stacks > 1 ? ` x${stacks}` : ''}.`, ...state.log],
      };
    }

    case 'nextReflectGainSp': {
      // Player-only: the next time the player reflects damage, they gain SP equal to the reflected amount.
      if (by !== 'player') return state;
      const cur = Math.max(0, Math.floor(Number((state as any).nextReflectGainSp ?? 0) || 0));
      const nextStacks = cur + 1;
      return {
        ...state,
        nextReflectGainSp: nextStacks,
        log: [`Next reflect will grant SP equal to reflected damage.`, ...state.log],
      };
    }


    case 'nextReflectTriggerTwice': {
      // Player-only: the next time the player reflects damage, the reflect trigger happens an additional time.
      // 1 stack => reflect triggers twice.
      if (by !== 'player') return state;
      const cur = Math.max(0, Math.floor(Number((state as any).nextReflectTriggerTwice ?? 0) || 0));
      const nextStacks = cur + 1;
      return {
        ...state,
        nextReflectTriggerTwice: nextStacks,
        log: [`Next reflect will trigger an extra time.`, ...state.log],
      };
    }


    case 'enableDiscardPlayThisTurn': {
      if (by !== 'player') return state;
      const stacks = Math.max(0, Math.floor(Number((state as any).discardPlayThisTurn ?? 0) || 0)) + 1;
      return {
        ...state,
        discardPlayThisTurn: stacks,
        log: [`Fate Reversed active for this turn${stacks > 1 ? ` x${stacks}` : ''}.`, ...state.log],
      };
    }

    case 'negateNextDamage': {
      // Player-only: reduce the next incoming damage to 0 (consumed on first incoming hit).
      const nextSelf = { ...src, negateNextDamage: (src.negateNextDamage || 0) + 1 };
      const msg = 'Next damage reduced to 0.';
      return by === 'player'
        ? pushStatusPulse({ ...state, player: nextSelf, log: [msg, ...state.log] }, 'negate')
        : { ...state, enemy: nextSelf, log: [msg, ...state.log] };
    }

    case 'discard': {
      // Discard is handled as a pending player choice in the PLAY_CARD flow.
      return state;
    }

    case 'stun': {
      // Ignore stun on player if they have a relic granting stun immunity (Lead Ribbon).
      if (by !== 'player') {
        const mods = (state as any).relicMods as RelicCombatMods | undefined;
        const immune = !!mods?.immuneStun;
        if (immune) {
          let next = {
            ...state,
            log: [`Relic: immune to stun, no effect.`, ...state.log],
          } as GameState;
          next = pushRelicTrigger(next, 'immuneStun', formatRelicFeedbackText('immuneStun'));
          return next;
        }
      }
      const nextTgt = { ...tgt, stunned: Math.max(tgt.stunned, eff.turns) };
      return by === 'player'
        ? pushStatusPulse({ ...state, enemy: nextTgt, log: [`Enemy stunned for ${eff.turns} turn(s).`, ...state.log] }, 'enemyStun')
        : { ...state, player: nextTgt, log: [`Player stunned for ${eff.turns} turn(s).`, ...state.log] };
    }

    case 'drawFromDeck': {
      // Player-only for now: draw extra cards from a chosen deck (used by Premonition).
      if (by !== 'player') return state;
      const count = Math.max(0, Math.floor((eff as any).amount ?? 0));
      if (count <= 0) return state;
      const rawKind = (eff as any).kind;
      const kind = rawKind === 'magic' ? 'magic' : rawKind === 'any' ? 'any' : 'physical';
      const setCostTo0 = !!(eff as any).setCostTo0;
      let next = state;
      let drew = 0;
      for (let i = 0; i < count; i++) {
        if (handCount(next) >= 5) break;
        const beforeCount = handCount(next);
        const drawn = kind === 'any' ? takeTopCardFromUnifiedDeck(next) : drawOneFromPile(next, kind);
        const { state: s2, card } = drawn;
        next = s2;
        if (!card) break;
        const c2 = setCostTo0 ? { ...card, cost: { ...card.cost, hp: 0, sp: 0 } } : card;
        next = addExtraToHand(next, c2);
        const afterCount = handCount(next);
        const drawnCount = Math.max(0, afterCount - beforeCount);
        if (drawnCount > 0) {
          // Increment draw counter and apply SP gain for each drawn card
          next = handleDrawn(next, drawnCount);
          drew += drawnCount;
        }
      }
      if (drew > 0) {
        const suffix = setCostTo0 ? ' (0 cost)' : '';
        next = { ...next, log: [`Drew ${drew} ${kind === 'any' ? 'deck' : kind} card(s)${suffix}.`, ...next.log] };
      }
      return next;
    }

    default:
      return state;
  }
}

function dealDamage(hp: number, armor: number, dmg: number): { newHp: number; newArmor: number; dmgToHp: number } {
  const absorbed = Math.min(armor, dmg);
  const remaining = dmg - absorbed;
  const newArmor = Math.max(0, armor - absorbed);
  const newHp = Math.max(0, hp - remaining);
  return { newHp, newArmor, dmgToHp: remaining };
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function hasKeyword(card: Card, kw: string): boolean {
  const needle = kw.trim().toLowerCase();
  if (!needle) return false;
  // Defensive: some cards coming from data/backends may omit `keywords`.
  // Treat missing keywords as "no keywords" rather than crashing.
  return (card.keywords || []).some((k: any) => String(k).trim().toLowerCase() === needle);
}

function hasKeywordList(keywords: string[], kw: string): boolean {
  const needle = kw.trim().toLowerCase();
  if (!needle) return false;
  return (keywords || []).some((k) => String(k).trim().toLowerCase() === needle);
}

function resolveDiscardedCardAsPlay(state: GameState, card: Card, repeatCount?: number): GameState {
  const times = Math.max(0, Math.floor(Number(repeatCount ?? 1) || 0));
  if (times <= 0 || state.phase === 'gameover' || state.phase === 'reward') return state;

  let next = state;

  for (let i = 0; i < times; i++) {
    let playEffects: Effect[] = Array.isArray(card.effects) ? [...card.effects] : [];
    let playKeywords: string[] = Array.isArray(card.keywords) ? [...card.keywords] : [];
    let delayGroups: any[] = Array.isArray((card as any).delayed) ? [...((card as any).delayed as any[])] : [];

    const allowException = ['against the odds', 'dark pact'].includes(norm(card.title));
    if (allowException) {
      const custom = applyPlayExceptions({
        cardTitle: card.title,
        stateAfterCosts: next,
        playEffects,
        playKeywords,
        delayGroups,
      });
      if (custom) {
        next = custom.stateAfterCosts;
        playEffects = custom.playEffects;
        playKeywords = custom.playKeywords;
        delayGroups = custom.delayGroups;
      }
    }

    const chooseOne = playEffects.find((e: any) => e.type === 'chooseOne') as any;
    const xTradeNeeded = playEffects.find((e: any) => e.type === 'xTrade') as any;
    const tutorEffect = playEffects.find((e: any) => e.type === 'tutor') as any;
    const peekPhysicalCount = playEffects.filter((e: any) => e.type === 'peekReorderPhysical').reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);
    const peekMagicCount = playEffects.filter((e: any) => e.type === 'peekReorderMagic').reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);
    const peekDeckCount = playEffects.filter((e: any) => e.type === 'peekReorderDeck').reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);
    const topPickPhysicalCount = playEffects.filter((e: any) => e.type === 'peekPickOneDiscardRestPhysical').reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);
    const topPickMagicCount = playEffects.filter((e: any) => e.type === 'peekPickOneDiscardRestMagic').reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);
    const topPickAnyCount = playEffects.filter((e: any) => e.type === 'peekPickOneDiscardRestAny').reduce((a: number, e: any) => a + (Number(e.count) || 0), 0);
    const discardNeeded = playEffects.filter((e: any) => e.type === 'discard').reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
    const drawSameDeckNeeded = playEffects.filter((e: any) => e.type === 'draw').reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
    const drawRandomNeeded = playEffects.filter((e: any) => e.type === 'drawRandom').reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
    const drawRandomPhysicalNeeded = playEffects.filter((e: any) => e.type === 'drawRandomPhysical').reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);
    const drawRandomMagicNeeded = playEffects.filter((e: any) => e.type === 'drawRandomMagic').reduce((a: number, e: any) => a + (Number(e.amount) || 0), 0);

    next = {
      ...next,
      log: [`Fate Reversed: play ${card.title}${times > 1 ? ` (${i + 1}/${times})` : ''}.`, ...next.log],
    };

    for (const eff of playEffects) {
      if (
        eff.type === 'draw' ||
        eff.type === 'drawRandom' ||
        eff.type === 'drawRandomPhysical' ||
        eff.type === 'drawRandomMagic' ||
        eff.type === 'discard' ||
        eff.type === 'chooseOne' ||
        eff.type === 'xTrade' ||
        eff.type === 'tutor' ||
        eff.type === 'peekReorderPhysical' ||
        eff.type === 'peekReorderMagic' ||
        eff.type === 'peekReorderDeck' ||
        eff.type === 'peekPickOneDiscardRestPhysical' ||
        eff.type === 'peekPickOneDiscardRestMagic' ||
        eff.type === 'peekPickOneDiscardRestAny'
      ) {
        continue;
      }
      next = applyEffect(next, eff, 'player');
      next = settleGameOver(next);
      if (next.phase === 'reward' || next.phase === 'gameover' || isCombatEndPending(next)) return next;
    }

    const before = handCount(next);
    if (drawSameDeckNeeded > 0 && (card.kind === 'physical' || card.kind === 'magic')) {
      next = drawExtraFromDeck(next, card.kind, drawSameDeckNeeded);
    }
    if (drawRandomPhysicalNeeded > 0) next = drawRandomExtraFromPiles(next, 'physical', drawRandomPhysicalNeeded);
    if (drawRandomMagicNeeded > 0) next = drawRandomExtraFromPiles(next, 'magic', drawRandomMagicNeeded);
    if (drawRandomNeeded > 0) next = drawRandomExtraFromPiles(next, 'any', drawRandomNeeded);
    const drew = Math.max(0, handCount(next) - before);
    if (drew > 0) next = { ...next, log: [`Fate Reversed: drew ${drew} card(s).`, ...next.log] };

    const hasUnsupported = !!chooseOne || !!xTradeNeeded || !!tutorEffect || peekPhysicalCount > 0 || peekMagicCount > 0 || peekDeckCount > 0 || topPickPhysicalCount > 0 || topPickMagicCount > 0 || topPickAnyCount > 0 || discardNeeded > 0;
    if (hasUnsupported) {
      next = { ...next, log: [`Fate Reversed: ${card.title} has a choice/search/discard effect that is skipped when auto-played from discard.`, ...next.log] };
    }

    if (Array.isArray(delayGroups) && delayGroups.length > 0) {
      const sourceCard = ({ ...card, id: String(card.id), title: String(card.title), kind: card.kind } as any);
      for (const g of delayGroups) {
        const baseTurns = Math.max(0, Number((g as any).turns) || 0);
        let t = baseTurns;
        const red = Math.max(0, Number((next as any).reduceDelayForLargeDeck ?? 0) || 0);
        if (red > 0) t = Math.max(0, t - red);
        const effs = Array.isArray((g as any).effects) ? ((g as any).effects as Effect[]) : [];
        const safe = effs.filter((e: any) => e.type !== 'discard');
        if (safe.length === 0) continue;
        if (t > 0) next = scheduleDelayed(next, sourceCard, safe, t);
        else if (baseTurns > 0 && red > 0) next = triggerDelayedEffectsNow(next, sourceCard, safe);
      }
    }
  }

  return next;
}

/**
 * Determine whether a card should be treated as an Exile card.
 *
 * Some cards may not explicitly include the "Exile" keyword in their
 * normalized keyword list, especially if an Addendum alters the effect
 * text or if the backend omits the keyword field. To ensure that relics
 * like First Exile Seal apply consistently, this helper inspects both
 * the explicit keyword list and the card's full description for common
 * phrases that imply exiling (e.g. "exile", "exiles", "exhaust", or
 * "remove this card from the game").
 *
 * @param card The card to inspect.
 * @returns True if the card exiles itself when played.
 */
function isExileCard(card: Card): boolean {
  // Check explicit keyword list first
  if (hasKeyword(card, 'Exile')) return true;
  // Fall back to description heuristics. The description field contains the
  // base card text (not modified by play-time keywords), so it can be used
  // to detect exile behaviour even when the Addendum alters the keyword list.
  const desc = String((card as any).description ?? '').toLowerCase();
  if (!desc) return false;
  // Match variations of exile/exhaust/remove from game
  if (/\bexile\b/.test(desc)) return true;
  if (/\bexiles\b/.test(desc)) return true;
  if (/\bexhaust\b/.test(desc)) return true;
  if (/remove\s+this\s+card\s+from\s+the\s+game/.test(desc)) return true;
  return false;
}

function applyPlayExceptions(args: {
  cardTitle: string;
  stateAfterCosts: GameState;
  playEffects: Effect[];
  playKeywords: string[];
  delayGroups: any[];
}): null | { stateAfterCosts: GameState; playEffects: Effect[]; playKeywords: string[]; delayGroups: any[] } {
  const title = args.cardTitle.trim().toLowerCase();

  if (getActiveChymistCompound(args.stateAfterCosts) === 'sedative' && Number(args.stateAfterCosts.playerPlaysThisTurn ?? 0) === 1) {
    const immediate = Array.isArray(args.playEffects) ? args.playEffects : [];
    const keepImmediate = immediate.filter((e: any) => e?.type === 'discard');
    const delayable = immediate.filter((e: any) => e?.type !== 'discard');
    if (delayable.length > 0) {
      return {
        stateAfterCosts: {
          ...args.stateAfterCosts,
          log: ['Sedative Compound: your first card this turn is delayed.', ...args.stateAfterCosts.log],
        },
        playEffects: keepImmediate,
        playKeywords: args.playKeywords,
        delayGroups: [{ turns: 1, effects: delayable }, ...(Array.isArray(args.delayGroups) ? args.delayGroups : [])],
      };
    }
  }

  // Dark Pact:
  // "Refill all your SP. Draw cards until you have 5 in your hand. You die next turn."
  // Implemented as an exception to keep scope tight.
  // - Refill SP = set SP to the current cap.
  // - Draw-until = draw from unified piles until hand size reaches 5.
  // - "You die next turn" is already parsed into a delayed bundle; preserve delayGroups.
  if (title === 'dark pact') {
    const maxSp = capSp(args.stateAfterCosts);
    const spBefore = args.stateAfterCosts.player.sp;
    const spAfter = clampPlayerSp(args.stateAfterCosts, maxSp);

    let nextState = args.stateAfterCosts;
    if (spAfter !== spBefore) {
      nextState = {
        ...nextState,
        player: { ...nextState.player, sp: spAfter },
        log: [`Dark Pact: SP refilled to ${spAfter}.`, ...nextState.log],
      };
    }

    // Hand cap is 5, so drawing to 5 is always safe.
    const need = Math.max(0, 5 - handCount(nextState));

    // Preserve any other inferred effects, but avoid double-draw by replacing any existing generic draw.
    const preserved = (args.playEffects || []).filter((e: any) => e?.type !== 'draw');
    const playEffects = need > 0 ? ([...preserved, { type: 'draw', amount: need }] as any) : preserved;

    return {
      stateAfterCosts: nextState,
      playEffects,
      playKeywords: args.playKeywords,
      delayGroups: args.delayGroups,
    };
  }

  // Against the Odds:
  // "If you have 20 Points of HP or less, deal 200 damage. If you don't, draw a card."
  if (title === 'against the odds') {
    const hpNow = args.stateAfterCosts.player.hp;
    if (hpNow <= 20) {
      return {
        stateAfterCosts: { ...args.stateAfterCosts, log: ['Against the Odds: HP ≤ 20 — deal 200 damage.', ...args.stateAfterCosts.log] },
        playEffects: [{ type: 'damage', amount: 200 }] as any,
        // Preserve whatever keywords the card already has.
        playKeywords: args.playKeywords,
        delayGroups: args.delayGroups,
      };
    }
    return {
      stateAfterCosts: { ...args.stateAfterCosts, log: ['Against the Odds: HP > 20 — draw 1.', ...args.stateAfterCosts.log] },
      playEffects: [{ type: 'draw', amount: 1 }] as any,
      // Preserve whatever keywords the card already has.
      playKeywords: args.playKeywords,
      delayGroups: args.delayGroups,
    };
  }



  // Spell Echo:
  // "Copy the effects of the last card you played this turn. Discard a card."
  // Implemented as an exception because it needs access to turn history.
  if (title === 'spell echo') {
    const prev = (args.stateAfterCosts as any).lastNonEchoPlayThisTurn as
      | undefined
      | {
          id: string;
          title: string;
          kind: any;
          playEffects: Effect[];
          playKeywords: string[];
          delayGroups: { turns: number; effects: Effect[] }[];
        };

    const prevEffects = Array.isArray(prev?.playEffects) ? prev.playEffects : [];
    const prevDelay = Array.isArray(prev?.delayGroups) ? prev.delayGroups : [];

    if (!prev || (prevEffects.length === 0 && prevDelay.length === 0)) {
      return {
        stateAfterCosts: { ...args.stateAfterCosts, log: ['Spell Echo: no previous card played this turn.', ...args.stateAfterCosts.log] },
        playEffects: args.playEffects,
        playKeywords: args.playKeywords,
        delayGroups: args.delayGroups,
      };
    }

    const echoedPrevEffects: Effect[] = prevEffects.map((eff: any) => {
      if (eff?.type === 'draw') {
        return { ...eff, sourceKind: prev.kind === 'magic' ? 'magic' : prev.kind === 'physical' ? 'physical' : 'any' } as any;
      }
      return eff;
    });

    const mergedEffects: Effect[] = [...echoedPrevEffects, ...args.playEffects];

    // Copy only turn-flow keywords (Chain / Finisher).
    const prevKws = Array.isArray(prev.playKeywords) ? prev.playKeywords : [];
    const needChain = prevKws.some((k) => String(k).trim().toLowerCase() === 'chain');
    const needFinisher = prevKws.some((k) => String(k).trim().toLowerCase() === 'finisher');
    let mergedKeywords = args.playKeywords;
    if (needChain && !hasKeywordList(mergedKeywords, 'Chain')) mergedKeywords = [...mergedKeywords, 'Chain'];
    if (needFinisher && !hasKeywordList(mergedKeywords, 'Finisher')) mergedKeywords = [...mergedKeywords, 'Finisher'];

    // Carry over delayed bundles, but preserve the original card's title/kind for delayed draws.
    const echoedDelayGroups = prevDelay.map((g: any) => ({
      ...g,
      sourceTitle: prev.title,
      sourceKind: prev.kind,
      sourceId: prev.id,
    }));
    const mergedDelayGroups = [...echoedDelayGroups, ...(Array.isArray(args.delayGroups) ? args.delayGroups : [])];

    return {
      stateAfterCosts: { ...args.stateAfterCosts, log: [`Spell Echo: copying ${prev.title}.`, ...args.stateAfterCosts.log] },
      playEffects: mergedEffects,
      playKeywords: mergedKeywords,
      delayGroups: mergedDelayGroups,
    };
  }

  // Omenfall:
  // "Discard all cards in your hand. Deal 20 damage for each card discarded this way."
  // Implemented as an exception because it requires a forced full-hand discard (no player choice)
  // and the damage scales with how many cards were actually removed from hand.
  if (title === 'omenfall') {
    let nextState: GameState = args.stateAfterCosts;
    const handCards = allHandCards(nextState);
    let discarded = 0;

    for (const c of handCards) {
      const found = findInHand(nextState, c.id);
      if (!found) continue;
      const { card, where } = found;

      // Remove from hand
      nextState = {
        ...nextState,
        hand: removeFromHand(nextState, where).hand,
        log: [`Discarded ${card.title}.`, ...nextState.log],
      };

      // Move discarded card to its discard pile (unless Exile)
      nextState = discardPlayed(nextState, card, 'discard');

      // Relic triggers: gain SP/HP on discard
      {
        const mods = (nextState as any).relicMods as RelicCombatMods | undefined;
        const gainSp = Math.max(0, Number(mods?.onDiscardGainSp ?? 0) || 0);
        if (gainSp > 0) {
          const newSp = clampPlayerSp(nextState, (nextState.player.sp || 0) + gainSp);
          nextState = {
            ...nextState,
            player: { ...nextState.player, sp: newSp },
            log: [`Relic: gain +${gainSp} SP from discarding.`, ...nextState.log],
          };
          nextState = pushRelicTriggerAmount(nextState, 'onDiscardGainSp', gainSp);
        }
        const gainHp = Math.max(0, Number(mods?.onDiscardGainHp ?? 0) || 0);
        if (gainHp > 0) {
          const healResult = applyPlayerHealingWithInvertedHeart(nextState, gainHp);
          const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
          nextState = {
            ...healResult.state,
            log: [
              ...(overflowLog ? [overflowLog] : []),
              `Relic: gain +${gainHp} HP from discarding.`,
              ...nextState.log,
            ],
          };
          nextState = pushRelicTriggerAmount(nextState, 'onDiscardGainHp', gainHp);
        }
      }

      nextState = triggerOnDiscard(nextState, card);
      const discardPlayStacks = Math.max(0, Number((nextState as any).discardPlayThisTurn ?? 0) || 0);
      if (discardPlayStacks > 0) {
        nextState = resolveDiscardedCardAsPlay(nextState, card, discardPlayStacks);
      }
      discarded += 1;
    }

    const dmg = Math.max(0, discarded * 20);
    const playEffects: Effect[] = dmg > 0 ? ([{ type: 'damage', amount: dmg }] as any) : ([] as any);

    return {
      stateAfterCosts:
        discarded > 0
          ? { ...nextState, log: [`Omenfall: discarded ${discarded} card(s) — deal ${dmg} damage.`, ...nextState.log] }
          : { ...nextState, log: [`Omenfall: no cards to discard.`, ...nextState.log] },
      playEffects,
      playKeywords: args.playKeywords,
      delayGroups: args.delayGroups,
    };
  }

  return null;
}


function triggerOnDiscard(state: GameState, card: Card): GameState {
  const trig = (card as any).onDiscard as undefined | { effects: Effect[]; delayed?: { turns: number; effects: Effect[] }[] };
  if (!trig) return state;
  // If the card exiles itself, it is not considered discarded into a discard pile.
  // Exile cards are removed from the game immediately and do not trigger discard effects.
  if (isExileCard(card)) return state;

  const effects = Array.isArray(trig.effects) ? trig.effects : [];
  const delayed = Array.isArray(trig.delayed) ? trig.delayed : [];
  if (effects.length === 0 && delayed.length === 0) return state;

  let next: GameState = { ...state, log: [`Discard trigger: ${card.title}.`, ...state.log] };

  // Apply immediate effects (Draw is handled here because it needs deck context and should obey hand cap).
  for (const eff of effects) {
    if (
      eff.type === 'draw' ||
      eff.type === 'drawRandom' ||
      eff.type === 'drawRandomPhysical' ||
      eff.type === 'drawRandomMagic'
    ) {
      const before = handCount(next);
      const amt = Math.max(0, Math.floor(Number((eff as any).amount) || 0));
      if (amt > 0) {
        if (eff.type === 'drawRandomPhysical') next = drawRandomExtraFromPiles(next, 'physical', amt);
        else if (eff.type === 'drawRandomMagic') next = drawRandomExtraFromPiles(next, 'magic', amt);
        else next = drawRandomExtraFromPiles(next, 'any', amt);
      }
      const drew = Math.max(0, handCount(next) - before);
      if (drew > 0) next = { ...next, log: [`Drew ${drew} card(s) (discard).`, ...next.log] };
      continue;
    }

    // Discard is treated as a cost/choice and is not valid inside discard-triggers.
    if (eff.type === 'discard') continue;

    next = applyEffect(next, eff, 'player');
  }

  // Schedule any delayed effect bundles produced by "Delay (X)" sequences inside the discard trigger.
  if (delayed.length > 0) {
    for (const g of delayed) {
      const baseTurns = Math.max(0, Number((g as any).turns) || 0);
      let t = baseTurns;

      // Apply relic-based delay reduction (Fine Sand Hourglass) if deck is large.
      const red = Math.max(0, Number((state as any).reduceDelayForLargeDeck ?? 0) || 0);
      const delayReducedByRelic = red > 0 ? Math.min(red, baseTurns) : 0;
      if (red > 0) {
        t = Math.max(0, t - red);
      }
      if (delayReducedByRelic > 0) {
        next = pushRelicTriggerAmount(next, 'reduceDelayForLargeDeck', delayReducedByRelic);
      }

      const effs = Array.isArray((g as any).effects) ? ((g as any).effects as Effect[]) : [];
      const safe = effs.filter((e: any) => e.type !== 'discard');
      if (safe.length === 0) continue;

      if (t > 0) {
        next = scheduleDelayed(next, card, safe, t);
      } else if (baseTurns > 0 && red > 0) {
        // If a relic reduces Delay to 0, the delayed bundle triggers immediately.
        next = triggerDelayedEffectsNow(next, card, safe);
      }
    }
  }


  return next;
}

function discardPlayed(state: GameState, card: Card, reason: 'played' | 'discard'): GameState {
  const forfeitureActive =
    reason === 'discard' &&
    isCustodianEnemy(state.enemyId) &&
    Math.max(0, Number((state as any).custodianForfeitureTurns ?? 0) || 0) > 0 &&
    !!card &&
    card.kind !== 'enemy';

  if (forfeitureActive) {
    const next = pushCardsToExhaust(state, [card]);
    return {
      ...next,
      log: [`Forfeiture exiles ${card.title}.`, ...next.log],
    } as GameState;
  }

  // Handle the First Exile Seal: the first Exile card played each combat does not Exile.
  if (isExileCard(card)) {
    const mods = (state as any).relicMods as RelicCombatMods | undefined;
    const allowSkip = reason === 'played' && !!mods?.firstExileDontExile;
    const already = !!(state as any).firstExileCardPlayed;
    if (allowSkip && !already) {
      const next = pushCardsToDiscard({ ...(state as any), firstExileCardPlayed: true } as GameState, [card]);
      return pushRelicTrigger(next, 'firstExileDontExile', formatRelicFeedbackText('firstExileDontExile'));
    }
    return pushCardsToExhaust(state, [card]);
  }
  return pushCardsToDiscard(state, [card]);
}



// Draw random cards from the specified piles.
// mode:
// - 'physical' / 'magic': draw randomly from that deck
// - 'any': choose randomly between physical and magic for each draw (when available)

// At the start of combat (and at the start of each player turn), ensure the player
// has at least 1 Physical and 1 Magic card in hand when possible.


function ensureDeckReady(state: GameState, kind: 'physical' | 'magic'): GameState {
  // In the unified-deck model there is a single deck and a single discard pile.
  // Specific-kind operations still filter by card kind, but reshuffling is shared.
  return ensureUnifiedDeckReady(state);
}

function bumpDiscardIntoDeck(discard: Card[], _kind: 'physical' | 'magic'): Card[] {
  // If cost escalation is disabled, reshuffle only (no stat/cost changes)
  if (!COMBAT_RULES.increaseCostOnReshuffle) {
    return shuffle(discard.map((c) => ({ ...c, cost: { ...(c.cost ?? { hp: 0, sp: 0 }) } })));
  }

  // Unified deck reshuffle: each card escalates according to its own kind.
  const bumped = discard.map((c) => {
    const hp = c.cost?.hp ?? 0;
    const sp = c.cost?.sp ?? 0;
    return c.kind === 'physical'
      ? { ...c, cost: { ...c.cost, hp: hp + 1, sp } }
      : c.kind === 'magic'
        ? { ...c, cost: { ...c.cost, hp, sp: sp + 1 } }
        : { ...c, cost: { ...c.cost, hp, sp } };
  });
  return shuffle(bumped);
}

function drawOneFromPile(state: GameState, kind: 'physical' | 'magic'): { state: GameState; card: Card | null } {
  if (state.phase === 'gameover') return { state, card: null };
  return takeKindCardFromUnifiedDeck(state, kind);
}

function drawToMain(state: GameState, kind: 'physical' | 'magic'): GameState {
  if (state.phase === 'gameover') return state;
  if (state.hand.main[kind]) return state;
  const cap = 5;
  if (handCount(state) >= cap) return state;

  const { state: s2, card } = drawOneFromPile(state, kind);
  if (!card) return s2;
  const beforeCount = handCount(s2);
  const next = setMainSlot(s2, kind, card);
  const afterCount = handCount(next);
  const drawn = Math.max(0, afterCount - beforeCount);
  if (drawn > 0) {
    return handleDrawn(next, drawn);
  }
  return next;
}

function drawExtraFromDeck(state: GameState, kind: 'physical' | 'magic', count: number): GameState {
  if (state.phase === 'gameover') return state;
  let next = state;
  for (let i = 0; i < count; i++) {
    if (handCount(next) >= 5) break;
    const beforeCount = handCount(next);
    const { state: s2, card } = drawOneFromPile(next, kind);
    next = s2;
    if (!card) break;
    next = addExtraToHand(next, card);
    const afterCount = handCount(next);
    const drawn = Math.max(0, afterCount - beforeCount);
    if (drawn > 0) {
      next = handleDrawn(next, drawn);
    }
  }
  return next;
}

function ensureUnifiedAnyDrawReady(state: GameState): GameState {
  return ensureUnifiedDeckReady(state);
}

function revealTopCardsFromCombinedDeck(state: GameState, count: number): { state: GameState; cards: Card[] } {
  return peekTopCardsFromUnifiedDeck(state, count);
}

function drawRandomExtraFromPiles(state: GameState, mode: 'physical' | 'magic' | 'any', count: number): GameState {
  if (state.phase === 'gameover') return state;
  let next: GameState = state;
  const mods = (state as any).relicMods as RelicCombatMods | undefined;
  for (let i = 0; i < count; i++) {
    if (handCount(next) >= 5) {
      const gainHp = Math.max(0, Number(mods?.fullHandGainHpOnDraw ?? 0) || 0);
      if (gainHp > 0) {
        const healResult = applyPlayerHealingWithInvertedHeart(next, gainHp);
        const overflowLog = formatInvertedHeartHealOverflowLog(healResult);
        next = {
          ...healResult.state,
          log: [
            ...(overflowLog ? [overflowLog] : []),
            `Relic: hand is full, gain +${gainHp} HP instead of drawing.`,
            ...next.log,
          ],
        };
      }
      continue;
    }

    const beforeCount = handCount(next);
    if (mode === 'any') {
      const drawn = takeTopCardFromUnifiedDeck(next);
      next = drawn.state;
      if (!drawn.card) break;
      next = addExtraToHand(next, drawn.card);
    } else {
      next = drawRandomOne(next, mode);
    }
    const afterCount = handCount(next);
    const drawnCount = Math.max(0, afterCount - beforeCount);
    if (drawnCount > 0) {
      next = handleDrawn(next, drawnCount);
    }
  }
  return next;
}

function drawRandomOne(state: GameState, kind: 'physical' | 'magic'): GameState {
  if (state.phase === 'gameover') return state;
  const drawn = takeRandomCardFromUnifiedDeck(state, kind);
  if (!drawn.card) return drawn.state;
  return addExtraToHand(drawn.state, drawn.card);
}

function refillChoiceInHand(state: GameState): GameState {
  if (state.phase === 'gameover') return state;
  let next = state;
  if (!next.hand.main.physical) next = drawToMain(next, 'physical');
  if (!next.hand.main.magic) next = drawToMain(next, 'magic');
  return next;
}

function hasAnyPlayableCard(state: GameState): boolean {
  if (state.phase !== 'player') return false;
  return allHandCards(state).some((c) => state.player.hp >= c.cost.hp && state.player.sp >= c.cost.sp);
}

function pickEnemyCard(state: GameState, enemyDeck: Card[]): Card | null {
  const aiRouted = hasEnemyAiRouting(state.enemyId);
  const pool = enemyDeck.filter((c) => (aiRouted || !isEnemyFinisherCard(c, state.enemyId)) && isMonsignorCardPlayable(state, c));
  if (pool.length === 0) return null;

  if (shouldForceChymistCompoundNow(state)) {
    const compoundPool = pool.filter(isChymistCompoundCard);
    if (compoundPool.length > 0) {
      const compoundIdx = Math.floor(Math.random() * compoundPool.length);
      return compoundPool[compoundIdx] ?? null;
    }
  }

  const aiDecision = chooseEnemyCardViaAi(state, enemyDeck, pool, false);
  if (aiDecision?.card) return aiDecision.card;

  if (isMercenaryEnemy(state.enemyId)) {
    const priority = pickMercenaryPriorityCard(pool, state);
    if (priority) return priority;
  }

  const idx = Math.floor(Math.random() * pool.length);
  return pool[idx] ?? null;
}

function pickEnemyCardDeterministic(state: GameState, enemyDeck: Card[]): Card | null {
  const aiRouted = hasEnemyAiRouting(state.enemyId);
  const pool = enemyDeck.filter((c) => (aiRouted || !isEnemyFinisherCard(c, state.enemyId)) && isMonsignorCardPlayable(state, c));
  if (pool.length === 0) return null;

  if (shouldForceChymistCompoundNow(state)) {
    const compoundPool = pool.filter(isChymistCompoundCard);
    if (compoundPool.length > 0) {
      let compoundSeed = Math.imul((state.turn || 0) + 1, 1103515245) >>> 0;
      compoundSeed = (compoundSeed ^ Math.imul((state.player?.hp || 0) + 1, 2654435761)) >>> 0;
      compoundSeed = (compoundSeed ^ Math.imul((state.enemy?.hp || 0) + 1, 2246822519)) >>> 0;
      for (const card of compoundPool) {
        const id = String(card?.id ?? card?.title ?? '');
        for (let i = 0; i < id.length; i++) {
          compoundSeed = (compoundSeed ^ id.charCodeAt(i)) >>> 0;
          compoundSeed = Math.imul(compoundSeed, 16777619) >>> 0;
        }
      }
      const compoundIdx = Math.abs(compoundSeed) % compoundPool.length;
      return compoundPool[compoundIdx] ?? null;
    }
  }

  const aiDecision = chooseEnemyCardViaAi(state, enemyDeck, pool, true);
  if (aiDecision?.card) return aiDecision.card;

  if (isMercenaryEnemy(state.enemyId)) {
    const priority = pickMercenaryPriorityCardDeterministic(pool, state);
    if (priority) return priority;
  }

  let seed = Math.imul((state.turn || 0) + 1, 1103515245) >>> 0;
  seed = (seed ^ Math.imul((state.player?.hp || 0) + 1, 2654435761)) >>> 0;
  seed = (seed ^ Math.imul((state.enemy?.hp || 0) + 1, 2246822519)) >>> 0;
  seed = (seed ^ Math.imul((state.enemyForcedQueue?.length || 0) + 1, 3266489917)) >>> 0;
  for (const card of pool) {
    const id = String(card?.id ?? card?.title ?? '');
    for (let i = 0; i < id.length; i++) {
      seed = (seed ^ id.charCodeAt(i)) >>> 0;
      seed = Math.imul(seed, 16777619) >>> 0;
    }
  }

  const idx = Math.abs(seed) % pool.length;
  return pool[idx] ?? null;
}

function predictEnemyIntentCard(state: GameState): Card | null {
  const q: Card[] = state.enemyForcedQueue ?? [];
  if (q.length > 0) return q[0] ?? null;
  if (shouldForceParalyzingVenomOnEnemyTurn(state)) {
    return findEnemyCard(state.enemyDeck, isParalyzingVenomCard);
  }
  return pickEnemyCardDeterministic(state, filterRandomEnemyDeck(state, state.enemyDeck));
}


function findEnemyCardByTitle(enemyDeck: Card[], title: string): Card | null {
  return enemyDeck.find((c) => c.title === title) ?? null;
}


function getCombatEndPendingResult(state: GameState): 'reward' | 'gameover' | null {
  const pending = (state as any).combatEndPendingResult;
  return pending === 'reward' || pending === 'gameover' ? pending : null;
}

function isCombatEndPending(state: GameState): boolean {
  return getCombatEndPendingResult(state) !== null;
}

function finalizeCombatEnd(state: GameState): GameState {
  const pendingResult = getCombatEndPendingResult(state);
  if (!pendingResult) return state;

  if (pendingResult === 'gameover') {
    const next: any = {
      ...state,
      phase: 'gameover',
      enemyForcedQueue: [],
      enemyIntent: undefined,
      enemyAwaitingAck: false,
      enemyStartPending: false,
      canPlay: false,
      log: ['You died.', ...state.log],
    };
    delete next.combatEndPendingResult;
    return next as GameState;
  }

  // Combat-end cleanup for temporary Max HP bonuses (e.g. Endure).
  // These bonuses should NOT persist back to the dungeon layer.
  const baseCapHp = Math.floor(Number((state as any).relicMods?.capHp ?? 100) || 100);
  const cleanedPlayer = {
    ...state.player,
    hp: clampTo(state.player.hp, baseCapHp),
    armor: clampTo(state.player.armor, baseCapHp),
    reflectPct: Math.max(0, Math.min(baseCapHp, Math.floor(Number(state.player.reflectPct ?? 0) || 0))),
  };

  let next: any = {
    ...state,
    phase: 'reward',
    tempMaxHpBonus: 0,
    player: cleanedPlayer,
    endCombatHp: cleanedPlayer.hp,
    endCombatSp: cleanedPlayer.sp,
    // Clear any enemy modal state
    enemyIntent: undefined,
    enemyAwaitingAck: false,
    enemyStartPending: false,
    enemyForcedQueue: [],
    canPlay: false,
    pendingDiscard: null,
    pendingPeek: null,
    pendingChoice: null,
    pendingTutor: null,
    delayed: [],
    log: ['Enemy defeated! Choose your reward.', ...state.log],
  };
  delete next.combatEndPendingResult;

  // Relic: Red Ledger Seal – if enough Addendum cards were played and every Addendum cost was paid,
  // gain SP at the end of combat.
  {
    const mods = (state as any).relicMods as RelicCombatMods | undefined;
    const threshold = Math.max(0, Number(mods?.gainSpOnPayAddendumThreshold ?? 0) || 0);
    const amount = Math.max(0, Number(mods?.gainSpAmount ?? 0) || 0);
    if (threshold > 0 && amount > 0) {
      const playedCount = Math.max(0, Number((state as any).addendumPlayedCountThisCombat ?? 0) || 0);
      const allPaid: boolean = (state as any).paidAllAddendumCostsThisCombat !== false;
      if (playedCount >= threshold && allPaid) {
        next = {
          ...next,
          player: { ...next.player, sp: clampPlayerSp(next as GameState, (next.player.sp || 0) + amount) },
          log: [`Relic: gain +${amount} SP at end of combat (Red Ledger Seal).`, ...next.log],
        };
      }
    }
  }

  // Relic: Untarnished Candle – if the player took no enemy damage during combat, apply a HP penalty and log the Teeth bonus.
  {
    const mods = (state as any).relicMods as RelicCombatMods | undefined;
    // Trigger only if the relic defines a HP penalty and the player hasn't taken any enemy damage this combat.
    if (mods?.untarnishedCandleLoseHp && !((state as any).playerTookEnemyDamageThisCombat || (state as any).playerTookDamage)) {
      const loss = Math.max(0, Number(mods.untarnishedCandleLoseHp) || 0);
      if (loss > 0) {
        const newHp = clampPlayerHp(next as GameState, (next.player.hp || 0) - loss);
        // Lookup the extra Teeth to grant from the relic mods (default to 0 if undefined).
        const teethGain = Math.max(0, Number((mods as any).untarnishedCandleTeeth) || 0);
        next = {
          ...next,
          player: { ...next.player, hp: newHp },
          // Note: only HP changes are applied in the engine. The Teeth gain is handled in the dungeon layer.
          log: [
            `Relic: you took no damage; lose ${loss} HP${teethGain ? ` and gain ${teethGain} Teeth` : ''} (Untarnished Candle).`,
            ...next.log,
          ],
        };
      }
    }
  }
  return next as GameState;
}

function settleGameOver(state: GameState): GameState {
  if (state.phase === 'gameover' || state.phase === 'reward' || isCombatEndPending(state)) {
    return state;
  }
  if (state.player.hp <= 0) {
    return {
      ...(state as any),
      combatEndPendingResult: 'gameover',
      enemyForcedQueue: [],
      enemyIntent: undefined,
      enemyAwaitingAck: false,
      enemyStartPending: false,
      canPlay: false,
      autoFocusPending: false,
      pendingDiscard: null,
      pendingPeek: null,
      pendingChoice: null,
      pendingTutor: null,
      pendingTopPick: null,
      pendingX: null,
    } as GameState;
  }
  if (state.enemy.hp <= 0) {
    const { synapticRetortActive } = getPrimeVesselCombatFlags(state);
    const reviveUsed = !!((state as any).primeVesselSynapticRetortUsed);
    if (synapticRetortActive && !reviveUsed) {
      const baseHp = Math.max(1, Number((state as any).primeVesselInitialHp ?? (state.enemy as any).maxHp ?? 1) || 1);
      const reviveHp = Math.max(1, Math.round(baseHp * 0.3));
      return {
        ...(state as any),
        combatEndPendingResult: undefined,
        enemy: { ...state.enemy, hp: reviveHp, armor: 0 },
        primeVesselSynapticRetortUsed: true,
        log: [`Synaptic Retort: ${state.enemy.name} reanimates with ${reviveHp} HP.`, ...state.log],
      } as GameState;
    }

    return {
      ...(state as any),
      combatEndPendingResult: 'reward',
      enemyIntent: undefined,
      enemyAwaitingAck: false,
      enemyStartPending: false,
      enemyForcedQueue: [],
      canPlay: false,
      autoFocusPending: false,
      pendingDiscard: null,
      pendingPeek: null,
      pendingChoice: null,
      pendingTutor: null,
      pendingTopPick: null,
      pendingX: null,
    } as GameState;
  }
  return state;
}
